// src/pages/Tabs/CollectionDetails.jsx
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { jsPDF } from 'jspdf';
import { AddProjectButton, AddStreetIcon, AddPndIcon } from '../SidebarAddControls';
import {
  ChevronLeft, 
  ChevronDown,
  ChevronRight,
  MapPin,
  Building2,
  CircleDot,
  X,
  Navigation,
  Layers,
  Search,
  SlidersHorizontal,
  CalendarRange,
  Check,
  Calendar,
  TrendingUp,
  Loader2,
  CreditCard,
  Wallet,
  Globe,
  BarChart2,
  Clock,
  Clock1,
  AlertTriangle,
  Ban,
  GripVertical,
  CircleParking,
  FileBarChart2,
  Settings2,
  Download,
} from 'lucide-react';
import { GOOGLE_MAP_KEY } from '@/constants/constant';
import { useTheme } from '@/components/theme-provider';
import { apiCall } from '@/api/apiCall';
import LoadingComponent from '@/components/common/LoadingComponent';
import ErrorState from '@/components/common/ErrorStateComponent';
import ReactSelect from '@/components/common/ReactSelect';
import AreaChart from '@/components/BoardPack/components/charts/AreaChart';
import HourlyRevenueChart from '../HourlyRevenueChart';
import MapShimmer from '@/components/common/MapShimmer';
import { usePermissions } from '@/context/PermissionContext';
import { PaymentTableShimmer, PndListShimmer, StatsGridShimmer } from '@/components/child/SectionShimmer';

// ============================================
// CONSTANTS - ONSTREET CONTRACTS for Permission Filtering
// ============================================
const ONSTREET_CONTRACTS = {
  name: '901',
  fullName: 'Saudi Arabia',
  children: [
    { name: '0030021', fullName: 'AL-BAHA' },
    { name: '0030006', fullName: 'AL-HASSA' },
    { name: '0030018', fullName: 'BALJURASHI' },
    { name: '1501005', fullName: 'DIGITAL CITY' },
    { name: '0030001', fullName: 'DAMMAM' },
    { name: '0020001', fullName: 'JEDDAH' },
    { name: '1930002', fullName: 'JEDDAH JV' },
    { name: '0030013', fullName: 'JUBAIL' },
    { name: '0030017', fullName: 'KFCA' },
    { name: '0030014', fullName: 'KFMH' },
    { name: '0030009', fullName: 'KHAMISH MUSHAITH' },
    { name: '0030001', fullName: 'KHOBAR' },
    { name: '0040003', fullName: 'MADINA' },
    { name: '1400101', fullName: 'MAKKAH' },
    { name: '0020025', fullName: 'MINISTRY OF CULTURE' },
    { name: '0020013', fullName: 'TAIF' },
    { name: '2010001', fullName: 'RIYADH PARKING' },
  ],
};

// ============================================
// UNIQUE KEY for each contract (code + name)
// ============================================
const getContractKey = (contract) => `${contract.name}|${contract.fullName}`;

// ============================================
// API Permission to Contract Mapping - COMPLETE
// ============================================
const API_PERMISSION_TO_CONTRACT = {
  'BHA-AL-BAHA': { name: '0030021', fullName: 'AL-BAHA' },
  'BLJ-BALJURASHI': { name: '0030018', fullName: 'BALJURASHI' },
  'DAM-Dammam': { name: '0030001', fullName: 'DAMMAM' },
  'DC-DIGITAL CITY': { name: '1501005', fullName: 'DIGITAL CITY' },
  'HAS-AL-HASA': { name: '0030006', fullName: 'AL-HASSA' },
  'JED-JEDDAH': { name: '0020001', fullName: 'JEDDAH' },
  'JUB-JUBAIL': { name: '0030013', fullName: 'JUBAIL' },
  'JV-JEDDAH JV': { name: '1930002', fullName: 'JEDDAH JV' },
  'KFC-KFCA': { name: '0030017', fullName: 'KFCA' },
  'KFMH-KFMH': { name: '0030014', fullName: 'KFMH' },
  'KHA-KHAMIS MUSHAIT': { name: '0030009', fullName: 'KHAMISH MUSHAITH' },
  'KHO-KHOBER': { name: '0030001', fullName: 'KHOBAR' },
  'MAK-MAKKAH': { name: '1400101', fullName: 'MAKKAH' },
  'MED-MADINAH': { name: '0040003', fullName: 'MADINA' },
  'MOC-MINISTRY OF CULTURE': { name: '0020025', fullName: 'MINISTRY OF CULTURE' },
  'RYDO-RIYADH PARKING': { name: '2010001', fullName: 'RIYADH PARKING' },
  'Saudi Arabia': { name: '901', fullName: 'Saudi Arabia' },
  'TAF-TAIF': { name: '0020013', fullName: 'TAIF' },
};

// ============================================
// NORMALIZE NAME - Complete Fix
// ============================================
const normalizeName = (s) => {
  if (!s) return '';
  
  let cleaned = String(s).toUpperCase().trim();
  
  if (cleaned.includes('-')) {
    cleaned = cleaned.split('-').slice(1).join('-');
  }
  
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  const specialMappings = {
    'AL-HASA': 'AL-HASSA',
    'KHAMIS MUSHAIT': 'KHAMISH MUSHAITH',
    'KHOBER': 'KHOBAR',
    'MADINAH': 'MADINA',
  };
  
  return specialMappings[cleaned] || cleaned;
};

// ============================================
// Normalize API Permissions to Contracts
// ============================================
const normalizeToContract = (permission) => {
  if (!permission) return null;
  const trimmed = String(permission).trim();
  
  if (API_PERMISSION_TO_CONTRACT[trimmed]) {
    return API_PERMISSION_TO_CONTRACT[trimmed];
  }
  
  const foundByCode = ONSTREET_CONTRACTS.children.find(c => c.name === trimmed);
  if (foundByCode) return foundByCode;
  
  const normalizedName = normalizeName(trimmed);
  const foundByName = ONSTREET_CONTRACTS.children.find(
    c => normalizeName(c.fullName) === normalizedName
  );
  if (foundByName) return foundByName;
  
  return null;
};

const normalizePermissions = (userContracts) => {
  if (!userContracts) return { onstreet: [], offstreet: [] };
  
  const normalizeList = (list) => {
    if (!list || !Array.isArray(list)) return [];
    return list
      .map(item => normalizeToContract(item))
      .filter(Boolean);
  };
  
  return {
    onstreet: normalizeList(userContracts.onstreet),
    offstreet: normalizeList(userContracts.offstreet),
  };
};

// ─── Device helpers ───────────────────────────────────────────────────────────
const getDeviceColor = (deviceType) => {
  if (!deviceType) return '#9CA3AF';
  if (deviceType.includes('BNA')) return '#8B5CF6';
  if (deviceType.includes('TX')) return '#F59E0B';
  return '#10B981';
};

const getDeviceTypeName = (deviceType) => {
  if (!deviceType) return 'Unknown';
  if (deviceType.includes('BNA')) return 'BNA';
  if (deviceType.includes('TX')) return 'TX';
  return deviceType;
};

// ─── Street color palette ─────────────────────────────────────────────────────
const STREET_COLORS = [
  '#F59E0B', '#10B981', '#EC4899', '#14B8A6',
  '#F97316', '#06B6D4', '#84CC16', '#A78BFA',
  '#EF4444', '#3B82F6',
];

// ─── Google Maps Styles ───────────────────────────────────────────────────────
const getDarkMapStyles = (hideLabels = false) => [
  { elementType: 'geometry', stylers: [{ color: '#1a1d28' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1d28' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8b92a8' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d0d3de' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6b7280' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#232938' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c3142' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f1419' }] },
  ...(hideLabels ? [{ elementType: 'labels', stylers: [{ visibility: 'off' }] }] : []),
];

const getLightMapStyles = (hideLabels = false) => [
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e7eb' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#d1d5db' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dbeafe' }] },
  ...(hideLabels ? [{ elementType: 'labels', stylers: [{ visibility: 'off' }] }] : []),
];

// ─── Month helpers ────────────────────────────────────────────────────────────
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const getRawMonth = (month) => {
  if (!month) return MONTH_NAMES[new Date().getMonth()];
  if (month.includes('-')) return month.split('-')[1];
  return month;
};

const buildMonthParam = (month) => {
  if (!month) return null;
  const GROUP_NAMES = ['Full_Year', 'FY'];
  if (GROUP_NAMES.includes(month)) return month;
  if (month.includes('-')) return month;
  return month;
};

// ─── Stale-collection helpers ──────────────────────────────────────────────────
const parseFlexibleDate = (value) => {
  if (!value) return null;
  const str = String(value).trim();
  if (!str) return null;

  const dmy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const dt = new Date(str);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const daysSinceCollection = (value) => {
  const dt = parseFlexibleDate(value);
  if (!dt) return null;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const dtStart = new Date(dt);
  dtStart.setHours(0, 0, 0, 0);
  const diffMs = startOfToday.getTime() - dtStart.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

const isStaleCollectionPnd = (pnd, thresholdDays) => {
  const days = daysSinceCollection(pnd?.collection_date);
  if (days === null || days <= thresholdDays) return false;
  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, '')) || 0;
  const hasMoney = toNum(pnd?.coins_sales) > 0 || toNum(pnd?.notes_sales) > 0;
  return hasMoney;
};

const isNoTransactionPnd = (pnd, thresholdDays) => {
  const days = daysSinceCollection(pnd?.last_trans_date);
  if (days === null || days <= thresholdDays) return false;
  return true;
};

// ─── PND status filter helpers ─────────────────────────────────────────────────
const PND_STATUS_LABELS = {
  all: 'All',
  active: 'Active',
  inactive: 'Inactive',
  no_transaction: 'No Transaction',
  below_threshold: 'Below Threshold',
};

const EMPTY_FILTERS = [];

const formatCount = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? Number(value.replace(/,/g, '')) : Number(value);
  return Number.isNaN(num) ? String(value) : num.toLocaleString();
};

const parseCount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'string' ? Number(value.replace(/,/g, '')) : Number(value);
  return Number.isNaN(num) ? null : num;
};

const buildFiltersParam = (statusFilters) => {
  if (!statusFilters || statusFilters.length === 0) return '';
  return statusFilters.map(f => `&filters[]=${f}`).join('');
};

// ─── Parking Bays helpers ───────────────────────────────────────────────────
const ONSTREET_CONTRACTS_FOR_BAYS = [
  { code: '0030021', name: 'AL-BAHA' },
  { code: '0030006', name: 'AL-HASSA', aliases: ['AL HASA', 'AL-HASA', 'ALAHSA', 'AL AHSA', 'AL-AHSA', 'AHSA', 'HASA'] },
  { code: '0030018', name: 'BALJURASHI' },
  { code: '1501005', name: 'DIGITAL CITY' },
  { code: '0030001', name: 'DAMMAM' },
  { code: '0020001', name: 'JEDDAH' },
  { code: '1930002', name: 'JEDDAH JV' },
  { code: '0030013', name: 'JUBAIL' },
  { code: '0030017', name: 'KFCA' },
  { code: '0030014', name: 'KFMH' },
  { code: '0030009', name: 'KHAMISH MUSHAITH', aliases: ['KHAMIS MUSHAYT', 'KHAMIS MUSHAIT', 'KHAMIS MUSHAITH', 'KHAMEES MUSHAYT', 'KHAMIS'] },
  { code: '0030001', name: 'KHOBAR' },
  { code: '0040003', name: 'MADINA' },
  { code: '1400101', name: 'MAKKAH' },
  { code: '0020025', name: 'MINISTRY OF CULTURE' },
  { code: '0020013', name: 'TAIF' },
  { code: '2010001', name: 'RIYADH PARKING' },
];

const getContractCodeForCity = (city) => {
  if (!city) return null;
  const candidates = [city.city_name, city.city_send_name, city.city_cd].filter(Boolean).map(normalizeName);
  const namesFor = (c) => [c.name, ...(c.aliases || [])].map(normalizeName);
  const match = ONSTREET_CONTRACTS_FOR_BAYS.find(c => namesFor(c).some(n => candidates.includes(n)))
    || ONSTREET_CONTRACTS_FOR_BAYS.find(c => namesFor(c).some(n => candidates.some(cand => cand.includes(n) || n.includes(cand))));
  return match?.code || null;
};

const PARENT_CONTRACT_CODE_FOR_BAYS = '901';

const buildBaysMonthParam = (month) => {
  const GROUP_NAMES = ['Full_Year', 'FY'];
  if (!month || GROUP_NAMES.includes(month)) {
    return MONTH_NAMES[new Date().getMonth()];
  }
  return buildMonthParam(month) || getRawMonth(month);
};

const useCountryParkingBaysCount = (month, year) => {
  const [countryParkingBaysCount, setCountryParkingBaysCount] = useState(null);
  const [countryParkingBaysLoading, setCountryParkingBaysLoading] = useState(false);

  useEffect(() => {
    if (!month || !year) { setCountryParkingBaysCount(null); setCountryParkingBaysLoading(false); return; }
    let cancelled = false;
    setCountryParkingBaysLoading(true);
    const monthParam = buildBaysMonthParam(month);
    const url = `/get-enforcement-data-os?contract=${PARENT_CONTRACT_CODE_FOR_BAYS}&year=${year}&month=${monthParam}`;
    apiCall('get', url, null, null, false, false)
      .then(response => {
        if (cancelled || !response?.success) return;
        const bays = response.data?.cns_cards?.parking_bays ?? response.data?.enforcement_cards?.parking_bays ?? null;
        setCountryParkingBaysCount(bays);
      })
      .catch(() => { if (!cancelled) setCountryParkingBaysCount(null); })
      .finally(() => { if (!cancelled) setCountryParkingBaysLoading(false); });

    return () => { cancelled = true; };
  }, [month, year]);

  return { countryParkingBaysCount, countryParkingBaysLoading };
};

const useParkingBaysCount = (city, month, year) => {
  const [parkingBaysCount, setParkingBaysCount] = useState(null);
  const [parkingBaysLoading, setParkingBaysLoading] = useState(false);

  useEffect(() => {
    const contractCode = getContractCodeForCity(city);
    if (!contractCode) { setParkingBaysCount(null); return; }

    let cancelled = false;
    setParkingBaysLoading(true);
    const monthParam = buildBaysMonthParam(month);
    const url = `/get-enforcement-data-os?contract=${contractCode}&year=${year}&month=${monthParam}`;
    apiCall('get', url, null, null, false, false)
      .then(response => {
        if (cancelled || !response?.success) return;
        const bays = response.data?.cns_cards?.parking_bays ?? response.data?.enforcement_cards?.parking_bays ?? null;
        setParkingBaysCount(bays);
      })
      .catch(() => { if (!cancelled) setParkingBaysCount(null); })
      .finally(() => { if (!cancelled) setParkingBaysLoading(false); });

    return () => { cancelled = true; };
  }, [city?.city_name, city?.city_send_name, city?.city_cd, month, year]);

  return { parkingBaysCount, parkingBaysLoading };
};

// ============================================
// AccordionSection — reusable toggle section
// ============================================
const AccordionSection = ({ icon, title, badge, children, defaultOpen = false, id, openId, onToggle }) => {
  const isControlled = openId !== undefined && onToggle !== undefined;
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  
  // ✅ SIMPLE EQUALITY CHECK — sirf ek hi section open hoga
  const open = isControlled ? (openId === id) : localOpen;

  const toggle = () => {
    if (isControlled) onToggle(id);
    else setLocalOpen(v => !v);
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors text-left"
      >
        <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 text-muted-foreground">
          {icon}
        </div>
        <span className="flex-1 text-xs font-semibold text-foreground">{title}</span>
        {badge && (
          Array.isArray(badge)
            ? badge.map((b, i) => (
                <span key={i} className="text-[10px] text-muted-foreground bg-secondary border border-border px-2 py-0.5 rounded-full mr-1">
                  {b}
                </span>
              ))
            : (
                <span className="text-[10px] text-muted-foreground bg-secondary border border-border px-2 py-0.5 rounded-full mr-1">
                  {badge}
                </span>
              )
        )}
        <ChevronDown
          className={`w-3.5 h-3.5 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// PaymentBreakdownCard
// ============================================
const PaymentBreakdownCard = ({ breakdown }) => {
  if (!breakdown) return null;

  const [openGroups, setOpenGroups] = useState({});

  const toggleGroup = (key) => {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const groups = [
    {
      key: 'app_web',
      label: 'App / Web',
      items: [
        { key: 'card',     label: 'Card'     },
        { key: 'ewallet',  label: 'eWallet'  },
        { key: 'web_card', label: 'Web Card' },
      ],
    },
    {
      key: 'pnd',
      label: 'PND',
      items: [
        { key: 'card',    label: 'Card'    },
        { key: 'notes',   label: 'Notes'   },
        { key: 'coins',   label: 'Coins'   },
        { key: 'ewallet', label: 'eWallet' },
      ],
    },
  ];

  const pct = (v) => parseFloat(String(v ?? '0').replace('%', '')) || 0;
  const num = (v) => parseFloat(String(v ?? '0').replace(/,/g, '')) || 0;
  const fmtNum = (v) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const appWebTotal = breakdown?.app_web?.total;
  const pndTotal     = breakdown?.pnd?.total;

  const totalData = breakdown?.total ?? (
    (appWebTotal || pndTotal)
      ? (() => {
          const totalCollection = num(appWebTotal?.collection) + num(pndTotal?.collection);
          const totalTickets    = num(appWebTotal?.ticket_count) + num(pndTotal?.ticket_count);
          const weightedAls = totalTickets
            ? (num(appWebTotal?.als) * num(appWebTotal?.ticket_count) + num(pndTotal?.als) * num(pndTotal?.ticket_count)) / totalTickets
            : 0;
          return {
            collection: fmtNum(totalCollection),
            ticket_count: Math.round(totalTickets).toLocaleString('en-US'),
            als: weightedAls.toFixed(2),
            atv: totalTickets ? (totalCollection / totalTickets).toFixed(2) : '0.00',
            revenue_percentage: '100.00%',
          };
        })()
      : null
  );

  const sortedGroups = [...groups].sort((a, b) =>
    pct(breakdown?.[b.key]?.total?.revenue_percentage) - pct(breakdown?.[a.key]?.total?.revenue_percentage)
  );

  const DataRow = ({ label, data }) => {
    return (
      <div className="grid grid-cols-[1.8fr_1fr_0.8fr_0.6fr_0.6fr_0.8fr] gap-1 items-center px-3.5 py-2 border-b border-border/50 hover:bg-zinc-100 dark:hover:bg-zinc-800/60 transition-colors">
        <p className="text-[10px] font-medium text-foreground truncate">{label}</p>
        <p className="text-[10px] tabular-nums font-semibold text-foreground">{data?.collection ?? '0'}</p>
        <p className="text-[10px] tabular-nums text-foreground">{data?.ticket_count ?? '0'}</p>
        <p className="text-[10px] tabular-nums text-foreground">{data?.als ?? '0'}</p>
        <p className="text-[10px] tabular-nums text-foreground">{data?.atv ?? '0'}</p>
        <p className="text-[9px] tabular-nums font-semibold text-muted-foreground">
          {data?.revenue_percentage ?? '0.00%'}
        </p>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden w-full">
      {sortedGroups.map((group) => {
        const groupData  = breakdown?.[group.key];
        const groupTotal = groupData?.total;
        const isOpen     = !!openGroups[group.key];
        const sortedItems = [...group.items].sort((a, b) =>
          pct(groupData?.[b.key]?.revenue_percentage) - pct(groupData?.[a.key]?.revenue_percentage)
        );

        return (
          <div key={group.key} className="border-b border-border last:border-b-0">
            <button
              onClick={() => toggleGroup(group.key)}
              className="w-full grid grid-cols-[1.8fr_1fr_0.8fr_0.6fr_0.6fr_0.8fr] gap-1 items-center px-3.5 py-2.5 
              bg-[#ddebf7] 
              hover:bg-[#d2e3f3]
              transition-colors text-left"
            >
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {group.label}
              </span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">{groupTotal?.collection ?? '0'}</span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">{groupTotal?.ticket_count ?? '0'}</span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">{groupTotal?.als ?? '0'}</span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">{groupTotal?.atv ?? '0'}</span>
              <div className="flex items-center gap-1">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-secondary border border-border text-foreground tabular-nums">
                  {groupTotal?.revenue_percentage ?? '0%'}
                </span>
                <ChevronDown className={`w-3 h-3 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  {sortedItems.map(({ key, label }) => (
                    <DataRow key={key} label={label} data={groupData?.[key]} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      {totalData && (
        <div className="grid grid-cols-[1.8fr_1fr_0.8fr_0.6fr_0.6fr_0.8fr] gap-1 items-center px-3.5 py-2.5 bg-zinc-200 dark:bg-zinc-700">
          <p className="text-[10px] font-black text-foreground uppercase tracking-wide">Grand Total</p>
          <p className="text-[11px] font-black tabular-nums text-foreground">{totalData.collection ?? 0}</p>
          <p className="text-[11px] font-black tabular-nums text-foreground">{totalData.ticket_count ?? 0}</p>
          <p className="text-[11px] font-black tabular-nums text-foreground">{totalData.als ?? 0}</p>
          <p className="text-[11px] font-black tabular-nums text-foreground">{totalData.atv ?? 0}</p>
          <p className="text-[10px] font-black tabular-nums text-foreground">{totalData.revenue_percentage ?? '100%'}</p>
        </div>
      )}
    </div>
  );
};

// ============================================
// CityRevenueSection
// ============================================
const CityRevenueSection = ({ city, filterMonth, filterYear, filterThreshold, onBreakdownLoad, filterStatus = EMPTY_FILTERS }) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear  = filterYear  || String(now.getFullYear());
  const threshold    = filterThreshold || '';

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const hasFetchedRef = useRef(false);

  const cityId = city?.city_send_name || city?.city_cd;

  const fetchRevenue = useCallback(async () => {
    if (!cityId) return;
    setLoading(true);
    setError(null);
    setRevenueData(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      const url = `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}${buildFiltersParam(filterStatus)}`;
      const response = await apiCall('get', url, null, null, false, false);

      if (response?.success) {
        const payload = response.data?.city_breakdown ?? response.city_breakdown ?? response.data ?? response;
        setRevenueData(payload);

        const belowSet = new Set();
        const noTxSet  = new Set();
        const revMap   = new Map();
        (payload.streets || []).forEach(st => {
          (st.pnds || []).forEach(p => {
            if (p.below_threshold === true) belowSet.add(String(p.pnd));
            if (p.no_transaction  === true) noTxSet.add(String(p.pnd));
            revMap.set(String(p.pnd), {
              collection: p.collection,
              ticket_count: p.ticket_count,
              als: p.als,
              atv: p.atv,
              contribution: p.contribution,
              device_status_date: p.device_status_date,
              device_active_date: p.device_active_date,
            });
          });
        });
        onBreakdownLoad?.({ belowThreshold: belowSet, noTransaction: noTxSet, revenueMap: revMap });
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to fetch revenue');
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [cityId, displayMonth, displayYear, threshold, filterStatus, onBreakdownLoad]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true; 
    fetchRevenue(); 
  }, [fetchRevenue]);

  return (
    <div>
      {loading && <PaymentTableShimmer groups={2} rowsPerGroup={3} />}

      {!loading && error && (
        <div className="flex flex-col items-center gap-2 py-5">
          <p className="text-xs text-muted-foreground text-center">{error}</p>
          <button onClick={fetchRevenue} className="text-[10px] font-semibold text-primary hover:underline">Retry</button>
        </div>
      )}
      {!cityId && !loading && (
        <p className="text-xs text-muted-foreground text-center py-5">No city ID available for revenue lookup.</p>
      )}
      {!loading && !error && revenueData && revenueData.payment_breakdown && (
        <PaymentBreakdownCard breakdown={revenueData.payment_breakdown} />
      )}
    </div>
  );
};

// ============================================
// StreetRevenueSection
// ============================================
const StreetRevenueSection = ({ street, filterMonth, filterYear, filterThreshold, onSelectPnd, onBreakdownLoad, filterStatus = EMPTY_FILTERS }) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear  = filterYear  || String(now.getFullYear());
  const threshold    = filterThreshold || '';

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
 
  const hasFetchedRef = useRef(false);

  const streetId = street?.street_send_name || street?.street_cd;

  const fetchRevenue = useCallback(async () => {
    if (!streetId) return;
    setLoading(true);
    setError(null);
    setRevenueData(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      const url = `/get-pnd-collection?street=true&device_id=${streetId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}${buildFiltersParam(filterStatus)}`;
      const response = await apiCall('get', url, null, null, false, false);

      if (response?.success) {
        setRevenueData(response);
        const belowSet = new Set();
        const noTxSet  = new Set();
        const revMap   = new Map();
        (response.street_breakdown?.pnds || []).forEach(p => {
          if (p.below_threshold === true) belowSet.add(String(p.pnd));
          if (p.no_transaction  === true) noTxSet.add(String(p.pnd));
          revMap.set(String(p.pnd), {
            collection: p.collection,
            ticket_count: p.ticket_count,
            als: p.als,
            atv: p.atv,
            contribution: p.contribution,
            device_status_date: p.device_status_date,
            device_active_date: p.device_active_date,
          });
        });
        onBreakdownLoad?.({ belowThreshold: belowSet, noTransaction: noTxSet, revenueMap: revMap });
      } else {
        setError(response?.message || 'No revenue data found');
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to fetch revenue');
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [streetId, displayMonth, displayYear, threshold, filterStatus, onBreakdownLoad]);

  useEffect(() => { 
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchRevenue(); 
  }, [fetchRevenue]);

  return (
    <div>
      {loading && <StatsGridShimmer count={4} />}

      {!loading && error && (
        <div className="flex flex-col items-center gap-2 py-5">
          <p className="text-xs text-muted-foreground text-center">{error}</p>
          <button onClick={fetchRevenue} className="text-[10px] font-semibold text-primary hover:underline">Retry</button>
        </div>
      )}
      {!streetId && !loading && (
        <p className="text-xs text-muted-foreground text-center py-5">No street ID available for revenue lookup.</p>
      )}
      {!loading && !error && revenueData && (
        <>
          {revenueData.payment_breakdown && (
            <div className="mb-3">
              <PaymentBreakdownCard breakdown={revenueData.payment_breakdown} />
            </div>
          )}
          {Array.isArray(revenueData.street_breakdown?.pnds) && revenueData.street_breakdown.pnds.length > 0 && (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-3.5 py-2 bg-secondary/60 border-b border-border">
                {['PND', 'Revenue', 'Tickets', 'ALS', 'ATV', 'Contribution'].map(h => (
                  <p key={h} className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">{h}</p>
                ))}
              </div>

              <div className="divide-y divide-border max-h-64 overflow-y-scroll cb-scroll">
                {revenueData.street_breakdown.pnds.map((item, idx) => (
                  <div
                    key={`pnd_row_${item.pnd ?? idx}`}
                    onClick={() => onSelectPnd?.(item)}
                    className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr] gap-2 items-center px-3.5 py-2.5 hover:bg-secondary/40 transition-colors cursor-pointer"
                  >
                    <p className="text-[11px] font-mono font-semibold text-foreground">#{item.pnd}</p>
                    <p className="text-[11px] font-semibold">{item.collection ?? 0}</p>
                    <p className="text-[11px] text-foreground">{item.ticket_count ?? 0}</p>
                    <p className="text-[11px] text-foreground">{item.als ?? 0}</p>
                    <p className="text-[11px] text-foreground">{item.atv ?? 0}</p>
                    <p className="text-[10px] font-semibold tabular-nums text-muted-foreground">
                      {item.contribution ?? '—'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================
// PND RevenueSection
// ============================================
const RevenueSection = ({ pnd, filterMonth, filterYear, filterThreshold }) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear  = filterYear  || String(now.getFullYear());
  const threshold    = filterThreshold || '';

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);

  const hasFetchedRef = useRef(false);
  const deviceId = pnd?.pnd_number || pnd?.device_id;

  const fetchRevenue = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setError(null);
    setRevenueData(null);
    try {
      const monthParam = buildMonthParam(displayMonth)
        ? displayMonth
        : displayMonth.includes('-')
          ? displayMonth
          : `01-${displayMonth}`;

      const url = `/get-pnd-collection?device_id=${deviceId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}`;
      const response = await apiCall('get', url, null, null, false, false);

      if (response?.success) {
        const payload = response.data ?? response;
        if (payload.collection !== null || payload.ticket_count !== null) {
          setRevenueData(payload);
        } else {
          setError('No revenue data found for this period');
        }
      } else {
        setError(response?.message || 'No revenue data found');
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to fetch revenue');
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [deviceId, displayMonth, displayYear, threshold]);

  useEffect(() => { 
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchRevenue(); 
  }, [fetchRevenue]);

  return (
    <div>
      {loading && (
        <div className="space-y-3">
          <PaymentTableShimmer groups={2} rowsPerGroup={2} />
          <PndListShimmer rows={5} />
        </div>
      )}
      {!loading && error && (
        <div className="flex flex-col items-center gap-2 py-5">
          <p className="text-xs text-muted-foreground text-center">{error}</p>
          <button onClick={fetchRevenue} className="text-[10px] font-semibold text-primary hover:underline">Retry</button>
        </div>
      )}
      {!deviceId && !loading && (
        <p className="text-xs text-muted-foreground text-center py-5">No device ID available for revenue lookup.</p>
      )}
      {!loading && !error && revenueData && (
        <div className="grid grid-cols-2 gap-2.5">
          <div className="rounded-lg bg-secondary p-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Revenue</p>
            <p className="text-[22px] font-medium leading-none">
              {revenueData.collection ?? revenueData.total_collection ?? revenueData.total ?? 0}
            </p>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Ticket Count</p>
            <p className="text-[22px] font-medium text-foreground leading-none">{revenueData.ticket_count ?? 0}</p>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ALS</p>
            <p className="text-[22px] font-medium text-foreground leading-none">{revenueData.als ?? 0}</p>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ATV</p>
            <p className="text-[22px] font-medium text-foreground leading-none">{revenueData.atv ?? 0}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// CityStreetsRevenueSummary
// ============================================
const CityStreetsRevenueSummary = ({ city, filterMonth, filterYear, filterThreshold, onSelectStreet, filterStatus = EMPTY_FILTERS }) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear  = filterYear  || String(now.getFullYear());
  const threshold    = filterThreshold || '';

  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [streetSearch, setStreetSearch] = useState('');

  const hasFetchedRef = useRef(false);

  const cityId = city?.city_send_name || city?.city_cd;

  const fetchBreakdown = useCallback(async () => {
    if (!cityId) return;
    setLoading(true);
    setError(null);
    setBreakdown(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      const url = `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}${buildFiltersParam(filterStatus)}`;
      const response = await apiCall('get', url, null, null, false, false);
      if (response?.success) {
        const payload = response.data?.city_breakdown ?? response.city_breakdown ?? response.data ?? response;
        setBreakdown(payload);
      } else {
        setError(response?.message || 'No data');
      }
    } catch {
      setError('Failed to fetch');
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [cityId, displayMonth, displayYear, threshold, filterStatus]);

  useEffect(() => { 
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchBreakdown(); 
  }, [fetchBreakdown]);

  if (loading) {
    return <PndListShimmer rows={6} withHeader={false} />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-5">
        <p className="text-xs text-muted-foreground text-center">{error}</p>
        <button onClick={fetchBreakdown} className="text-[10px] font-semibold text-primary hover:underline">Retry</button>
      </div>
    );
  }

  const streets = breakdown?.streets || [];
  if (!streets.length) {
    return <p className="text-xs text-muted-foreground text-center py-5">No streets data available.</p>;
  }

  const totalStreets = streets.length;

  const q = streetSearch.trim().toLowerCase();
  const filteredStreets = q
    ? streets.filter(s => s.street?.toLowerCase().includes(q))
    : streets;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          placeholder="Search streets..."
          value={streetSearch}
          onChange={e => setStreetSearch(e.target.value)}
          className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
        />
        {streetSearch && (
          <button
            onClick={() => setStreetSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {q && (
        <p className="text-[10px] text-muted-foreground px-0.5">
          {filteredStreets.length === 0
            ? `No streets matched "${streetSearch}"`
            : `${filteredStreets.length} of ${totalStreets} streets`}
        </p>
      )}

      <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
        {filteredStreets.length === 0 && q ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
            <Search className="w-5 h-5 opacity-30" />
            <p className="text-xs">No results for "{streetSearch}"</p>
          </div>
        ) : (
          filteredStreets.map((street, idx) => (
            <StreetRevenueSummaryRow
              key={`${street.street}_${idx}`}
              street={street}
              cityStreets={city?.streets || []}
              onSelectStreet={onSelectStreet}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ============================================
// StreetRevenueSummaryRow
// ============================================
const StreetRevenueSummaryRow = ({ street, cityStreets, onSelectStreet }) => {
  const [open, setOpen] = useState(false);

  const isNoTransaction = street.no_transaction === true;
  const contributionPct = parseFloat(street.contribution) || 0;

  const collection   = street.collection   ?? '0';
  const ticketCount  = street.ticket_count ?? '0';
  const contribution = street.contribution ?? '0.00%';
  const als = (street.als !== null && street.als !== undefined && street.als !== '')
    ? String(street.als) : '—';
  const atv = (street.atv !== null && street.atv !== undefined && street.atv !== '')
    ? String(street.atv) : '—';

  const noTranCount   = street.no_tran_pnd_count        ?? 0;
  const belowThrCount = street.below_threshold_pnd_count ?? 0;
  const pnds          = street.pnds || [];

  const fullStreet = cityStreets.find(s =>
    s.street_name      === street.street ||
    s.street_send_name === street.street ||
    s.street_cd        === street.street
  );

  const handleStreetClick = () => {
    if (fullStreet) {
      onSelectStreet?.(fullStreet);
    }
    setOpen(true);
  };

  return (
    <div className={`rounded-xl border overflow-hidden transition-colors ${
      isNoTransaction
        ? 'border-red-500/30 bg-red-500/5'
        : 'border-border bg-card'
    }`}>
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen(v => !v);
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <div className={`w-2 h-2 rounded-full ${
            isNoTransaction ? 'bg-red-500' : 'bg-emerald-500'
          }`} />
        </button>

        <button
          onClick={handleStreetClick}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
          title="View street details"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {street.street}
          </span>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {collection}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contribution}
            </span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen(v => !v);
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`} />
        </button>
      </div>

      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              isNoTransaction ? 'bg-red-500' : 'bg-primary'
            }`}
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              <div className="space-y-2">
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Revenue</p>
                  <p className="text-lg font-bold tabular-nums leading-none text-primary">{collection}</p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Tickets</p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">{ticketCount}</p>
                  </div>
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Contribution</p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">{contribution}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">ALS</p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">{als}</p>
                  </div>
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">ATV</p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">{atv}</p>
                  </div>
                </div>
              </div>

              {(noTranCount > 0 || belowThrCount > 0) && (
                <div className="flex gap-2 flex-wrap">
                  {noTranCount > 0 && (
                    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/25">
                      <Ban className="w-3 h-3 text-red-500 flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-red-500">
                        {noTranCount} No Transaction
                      </span>
                    </div>
                  )}
                  {belowThrCount > 0 && (
                    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/10 border border-black/25">
                      <AlertTriangle className="w-3 h-3 text-black flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-black">
                        {belowThrCount} Below Threshold
                      </span>
                    </div>
                  )}
                </div>
              )}

              {pnds.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_0.7fr_0.8fr] gap-1.5 px-3 py-2 bg-secondary/60 border-b border-border">
                    {['PND', 'Revenue', 'Tickets', 'ALS', 'ATV', 'Contribution'].map(h => (
                      <p key={h} className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
                        {h}
                      </p>
                    ))}
                  </div>

                  <div className="max-h-52 overflow-y-auto cb-scroll divide-y divide-border">
                    {pnds.map((pnd, pi) => {
                      const pndPct      = parseFloat(pnd.contribution) || 0;
                      const pndNoTran   = pnd.no_transaction  === true;
                      const pndBelowThr = pnd.below_threshold === true;
                      const pndAls = (pnd.als !== null && pnd.als !== undefined && pnd.als !== '')
                        ? String(pnd.als) : '—';
                      const pndAtv = (pnd.atv !== null && pnd.atv !== undefined && pnd.atv !== '')
                        ? String(pnd.atv) : '—';

                      return (
                        <div
                          key={`pnd_${pnd.pnd}_${pi}`}
                          className={`px-3 py-2.5 transition-colors ${
                            pndNoTran   ? 'bg-red-500/5' :
                            pndBelowThr ? 'bg-black/5'    :
                            'hover:bg-secondary/40'
                          }`}
                        >
                          <div className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_0.7fr_0.8fr] gap-1.5 items-center">
                            <div className="flex items-center gap-1 min-w-0">
                              {pndNoTran   && <Ban           className="w-2.5 h-2.5 text-red-500 flex-shrink-0" />}
                              {pndBelowThr && <AlertTriangle className="w-2.5 h-2.5 text-black    flex-shrink-0" />}
                              <p className="text-[10px] font-mono font-semibold text-foreground truncate">
                                #{pnd.pnd}
                              </p>
                            </div>

                            <p className={`text-[10px] font-semibold tabular-nums truncate ${
                              pndNoTran ? 'text-red-500' : 'text-foreground'
                            }`}>
                              {pnd.collection ?? '0'}
                            </p>

                            <p className="text-[10px] text-muted-foreground tabular-nums">
                              {pnd.ticket_count ?? '0'}
                            </p>

                            <p className="text-[10px] text-foreground tabular-nums">
                              {pndAls}
                            </p>

                            <p className="text-[10px] text-foreground tabular-nums">
                              {pndAtv}
                            </p>

                            <div className="space-y-1">
                              <p className={`text-[9px] font-bold tabular-nums ${
                                pndPct >= 30 ? 'text-primary' : 'text-muted-foreground'
                              }`}>
                                {pnd.contribution ?? '0%'}
                              </p>
                              <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    pndNoTran   ? 'bg-red-500' :
                                    pndBelowThr ? 'bg-black'    :
                                    'bg-primary'
                                  }`}
                                  style={{ width: `${Math.min(pndPct, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {fullStreet ? (
                <button
                  onClick={() => onSelectStreet?.(fullStreet)}
                  className="w-full text-[10px] font-semibold text-primary hover:underline text-center py-1 flex items-center justify-center gap-1"
                >
                  View Street Detail <ChevronRight className="w-3 h-3" />
                </button>
              ) : (
                <p className="text-[9px] text-muted-foreground text-center py-1">
                  Street detail not available
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// DateFilterPanel
// ============================================
const DateFilterPanel = ({
  onApply, onReset, onClose, topPos,
  activeMonth, activeYear, activeThreshold,
  initialGroup = '', initialMonth = '', initialDay = null,
  monthGroups = [], yearOptions = [], optLoading = false, optError = null,
}) => {
  const _currentYear = String(new Date().getFullYear());
  const [selYear, setSelYear]          = useState(activeYear ? { value: activeYear, label: activeYear } : { value: _currentYear, label: _currentYear });
  const [selGroup, setSelGroup]        = useState(initialGroup);
  const [selMonth, setSelMonth]        = useState(initialMonth);
  const [selDay, setSelDay]            = useState(initialDay);
  const [threshold, setThreshold]      = useState(activeThreshold || '');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  useEffect(() => {
    setSelYear(activeYear ? { value: activeYear, label: activeYear } : { value: _currentYear, label: _currentYear });
    setSelGroup(initialGroup);
    setSelMonth(initialMonth);
    setSelDay(initialDay);
    setThreshold(activeThreshold || '');
  }, [activeYear, initialGroup, initialMonth, initialDay?.value, activeThreshold]);

  const panelRef    = useRef(null);
  const [panelPos, setPanelPos]       = useState({ top: topPos, left: 328 });
  const [panelHeight, setPanelHeight] = useState(null);
  const isDragging  = useRef(false);
  const isResizing  = useRef(false);
  const dragStart   = useRef({ x: 0, y: 0, top: 0, left: 0 });
  const resizeStart = useRef({ y: 0, height: 0 });

  const handleDragMouseDown = (e) => {
    if (e.target.closest('button')) return;
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX, y: e.clientY,
      top: panelPos.top, left: panelPos.left,
    };
    e.preventDefault();
  };

  const handleResizeMouseDown = (e) => {
    isResizing.current = true;
    resizeStart.current = {
      y: e.clientY,
      height: panelRef.current?.offsetHeight || 400,
    };
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (isDragging.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPanelPos({
          top:  Math.max(8, dragStart.current.top  + dy),
          left: Math.max(8, dragStart.current.left + dx),
        });
      }
      if (isResizing.current) {
        const dy  = e.clientY - resizeStart.current.y;
        const newH = Math.min(
          window.innerHeight - 80,
          Math.max(200, resizeStart.current.height + dy)
        );
        setPanelHeight(newH);
      }
    };
    const onUp = () => { isDragging.current = false; isResizing.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',  onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',  onUp);
    };
  }, []);

  useEffect(() => {
    setPanelPos({ top: topPos, left: 328 });
  }, [topPos]);

  useEffect(() => {
    if (!monthGroups || monthGroups.length === 0) return;
    if (initialGroup && initialMonth) {
      setExpandedGroups(prev => new Set([...prev, initialGroup]));
      setExpandedMonths(prev => new Set([...prev, initialMonth]));
      return;
    }
    const currentDate      = new Date();
    const currentMonthName = MONTH_NAMES[currentDate.getMonth()];
    const currentYearValue = String(currentDate.getFullYear());
    const fullYearGroup    = monthGroups.find(g => g.name === 'Full_Year');
    if (fullYearGroup?.children) {
      const currentMonthObj = fullYearGroup.children.find(m => m.name === currentMonthName);
      if (currentMonthObj) {
        setSelGroup('Full_Year');
        setSelMonth(currentMonthName);
        setSelDay({ value: currentMonthName, label: currentMonthName });
        setSelYear({ value: currentYearValue, label: currentYearValue });
        setExpandedGroups(new Set(['Full_Year']));
        setExpandedMonths(new Set([currentMonthName]));
      }
    }
  }, [monthGroups, initialGroup, initialMonth]);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => { const n = new Set(prev); n.has(groupName) ? n.delete(groupName) : n.add(groupName); return n; });
  };
  const toggleMonth = (monthName) => {
    setExpandedMonths(prev => { const n = new Set(prev); n.has(monthName) ? n.delete(monthName) : n.add(monthName); return n; });
  };
  const handleGroupSelect = (group) => {
    setSelGroup(group.name); setSelMonth(group.name);
    setSelDay({ value: group.name, label: group.name === 'Full_Year' ? 'Full Year' : 'FY' });
    toggleGroup(group.name);
  };
  const handleMonthSelect = (groupName, month) => {
    setSelGroup(groupName); setSelMonth(month.name);
    setSelDay({ value: month.name, label: month.name });
    if (month.children?.length > 0 && !expandedMonths.has(month.name)) toggleMonth(month.name);
  };
  const handleDaySelect = (groupName, monthName, day) => {
    setSelGroup(groupName); setSelMonth(monthName);
    setSelDay({ value: day.name, label: day.name });
  };

  const isComplete   = !!selMonth && !!selYear;
  const previewLabel = isComplete ? `${selDay.label} ${selYear.value}` : null;

  const handleApply = () => {
    if (!isComplete) return;
    onApply({
      month: selDay.value, year: selYear.value, type: selGroup,
      displayMonth: selDay.label || selMonth,
      panelMonth: selMonth,
      threshold: threshold || undefined,
    });
  };
  const handleReset = () => {
    setSelGroup(''); setSelMonth(''); setSelDay(null); setSelYear(null);
    setThreshold('');
    onReset();
  };

  return (
    <motion.div
      ref={panelRef}
      initial={{ opacity: 0, x: -6, scale: 0.97 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -6, scale: 0.97 }}
      transition={{ duration: 0.16 }}
      style={{
        position: 'fixed',
        left: `${panelPos.left}px`,
        top:  `${panelPos.top}px`,
        zIndex: 9999,
        width: '280px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.28)',
        height:    panelHeight ? `${panelHeight}px` : 'auto',
        maxHeight: panelHeight ? 'none' : '85vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      className="rounded-xl border border-border bg-card overflow-hidden"
    >
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-border bg-sidebar flex-shrink-0"
        onMouseDown={handleDragMouseDown}
        style={{ cursor: 'move', userSelect: 'none' }}
      >
        <div className="flex items-center gap-2">
          <CalendarRange className="w-4 h-4 text-primary" />
          <span className="text-xs font-bold text-foreground tracking-tight"> Filter</span>
        </div>
        <button
          onMouseDown={e => e.stopPropagation()}
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto cb-scroll p-4 space-y-4">
        {optLoading && (
          <div className="flex items-center justify-center gap-2 py-6">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-xs text-muted-foreground">Loading...</span>
          </div>
        )}
        {!optLoading && optError && (
          <p className="text-xs text-red-500 text-center py-2">{optError}</p>
        )}
        {!optLoading && !optError && (
          <>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">Threshold</p>
              <input
                type="number"
                placeholder="Enter threshold..."
                value={threshold}
                onChange={e => setThreshold(e.target.value)}
                className="w-full px-3 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
              />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">Year</p>
              <ReactSelect options={yearOptions} value={selYear} onChange={setSelYear} placeholder="Select year..." />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">Month / Period</p>
              <div className="border border-border rounded-lg overflow-hidden bg-background max-h-52 overflow-y-auto cb-scroll">
                {monthGroups.map((group) => {
                  const isGroupExpanded = expandedGroups.has(group.name);
                  const isGroupSelected = selGroup === group.name && selMonth === group.name;
                  return (
                    <div key={group.name}>
                      <button
                        onClick={() => handleGroupSelect(group)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors border-b border-border ${isGroupSelected ? 'bg-primary/10' : 'hover:bg-muted/50'}`}
                      >
                        <button onClick={(e) => { e.stopPropagation(); toggleGroup(group.name); }} className="flex-shrink-0 text-muted-foreground">
                          {isGroupExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        </button>
                        <span className="text-xs font-semibold text-foreground flex-1 text-left">
                          {group.name === 'Full_Year' ? 'Full Year' : 'Fiscal Year (FY)'}
                        </span>
                        {isGroupSelected && <Check className="w-3 h-3 text-primary flex-shrink-0" />}
                      </button>
                      {isGroupExpanded && (group.children || []).map((month) => {
                        const isMonthExpanded = expandedMonths.has(month.name);
                        const isMonthSelected = selGroup === group.name && selMonth === month.name && selDay?.value === month.name;
                        const hasDays = month.children?.length > 0;
                        return (
                          <div key={month.name}>
                            <button
                              onClick={() => handleMonthSelect(group.name, month)}
                              className={`w-full flex items-center gap-2 pl-7 pr-3 py-1.5 text-left transition-colors border-b border-border/50 ${isMonthSelected ? 'bg-primary/8 text-primary' : 'hover:bg-muted/40'}`}
                            >
                              {hasDays ? (
                                <button onClick={(e) => { e.stopPropagation(); toggleMonth(month.name); }} className="flex-shrink-0 text-muted-foreground">
                                  {isMonthExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                </button>
                              ) : <div className="w-3" />}
                              <span className={`text-xs flex-1 text-left ${isMonthSelected ? 'font-semibold text-primary' : 'text-foreground'}`}>{month.name}</span>
                              {isMonthSelected && <Check className="w-3 h-3 ml-auto text-primary flex-shrink-0" />}
                            </button>
                            {hasDays && isMonthExpanded && (
                              <div className="bg-muted/20">
                                {(month.children || []).map((day) => {
                                  const isDaySelected = selGroup === group.name && selMonth === month.name && selDay?.value === day.name;
                                  return (
                                    <button
                                      key={day.name}
                                      onClick={() => handleDaySelect(group.name, month.name, day)}
                                      className={`w-full flex items-center gap-2 pl-14 pr-3 py-1.5 text-left transition-colors border-b border-border/30 ${isDaySelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}`}
                                    >
                                      <span className={`text-[11px] font-mono ${isDaySelected ? 'font-semibold text-primary' : 'text-foreground'}`}>{day.name}</span>
                                      {isDaySelected && <Check className="w-3 h-3 ml-auto text-primary flex-shrink-0" />}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
            {previewLabel && (
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
                <Check className="w-3 h-3 text-primary flex-shrink-0" />
                <span className="text-[10px] text-primary font-semibold">{previewLabel}</span>
                {threshold && <span className="text-[10px] text-primary font-semibold ml-1">• Threshold: {threshold}</span>}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={handleReset} className="flex-1 py-1.5 text-xs font-semibold rounded-lg border border-border text-muted-foreground hover:bg-secondary transition-colors">Reset</button>
              <button
                disabled={!isComplete}
                onClick={handleApply}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${isComplete ? 'bg-primary text-white hover:bg-primary/90' : 'bg-muted text-muted-foreground cursor-not-allowed'}`}
              >
                Apply
              </button>
            </div>
          </>
        )}
      </div>

      <div
        onMouseDown={handleResizeMouseDown}
        className="flex-shrink-0 flex items-center justify-center border-t border-border hover:bg-primary/10 transition-colors"
        style={{ height: '10px', cursor: 'ns-resize' }}
      >
        <div className="w-8 h-[3px] rounded-full bg-border" />
      </div>
    </motion.div>
  );
};

// ============================================
// Sale & Collection Report — params popover + button
// ============================================
const SaleCollectionReportButton = ({ cityCd, streetCd, cityName, streetName, className = '' }) => {
  const [showParams, setShowParams] = useState(false);
  const [collDays, setCollDays]     = useState(30);
  const [transDays, setTransDays]   = useState(60);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [report, setReport]         = useState(null);
  const [showReport, setShowReport] = useState(false);

  const triggerRef  = useRef(null);
  const popoverRef  = useRef(null);

  useEffect(() => {
    if (!showParams) return;
    const onClickOutside = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setShowParams(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showParams]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qp = new URLSearchParams();
      if (cityCd)   qp.set('city_cd', cityCd);
      if (streetCd) qp.set('street_cd', streetCd);
      qp.set('coll_days',  String(collDays === '' ? 0 : collDays));
      qp.set('trans_days', String(transDays === '' ? 0 : transDays));

      const response = await apiCall('get', `/get-sale-collection-report?${qp.toString()}`, null, null, false, false);
      if (response?.success) {
        setReport(response);
        setShowParams(false);
        setShowReport(true);
      } else {
        setError(response?.message || 'Report fetch nahi ho saka');
      }
    } catch (e) {
      setError('Report fetch nahi ho saka');
    } finally {
      setLoading(false);
    }
  }, [cityCd, streetCd, collDays, transDays]);

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          ref={triggerRef}
          onClick={() => setShowParams(v => !v)}
          title="Sale & Collection Report"
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-colors ${
            showParams
              ? 'border-primary bg-primary/15 text-primary'
              : 'border-border bg-secondary hover:bg-secondary/70 text-foreground'
          }`}
        >
          <FileBarChart2 className="w-3.5 h-3.5 text-primary" />
          <span className="hidden sm:inline">Get Report</span>
          <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${showParams ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {showParams && (
            <motion.div
              ref={popoverRef}
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-1.5 w-60 rounded-xl border border-border bg-card shadow-xl z-[70] p-3"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2.5 flex items-center gap-1.5">
                <Settings2 className="w-3 h-3" /> Report Parameters
              </p>

              <div className="space-y-2.5">
                <div>
                  <label className="text-[10px] text-muted-foreground mb-1 block">Collection age (days)</label>
                  <input
                    type="number"
                    min={0}
                    value={collDays}
                    onChange={(e) => setCollDays(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-secondary text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground mb-1 block">Transaction age (days)</label>
                  <input
                    type="number"
                    min={0}
                    value={transDays}
                    onChange={(e) => setTransDays(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-secondary text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                {error && <p className="text-[10px] text-red-500">{error}</p>}

                <button
                  onClick={fetchReport}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileBarChart2 className="w-3.5 h-3.5" />}
                  {loading ? 'Fetching…' : 'Fetch Report'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {showReport && report && (
          <SaleCollectionReportModal
            report={report}
            locationName={streetName || cityName}
            onClose={() => setShowReport(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const flattenSalePnds = (nodes) => {
  const rows = [];
  const walk = (list, ctx) => {
    (list || []).forEach((node) => {
      if (!node) return;
      if (Array.isArray(node.streets)) {
        walk(node.streets, { ...ctx, cityName: node.city_name });
      } else if (Array.isArray(node.pnds)) {
        const nextCtx = { ...ctx, streetName: node.street_name };
        node.pnds.forEach((p) => rows.push({ ...p, ...nextCtx }));
      } else if (node.device_code) {
        rows.push({ ...node, ...ctx });
      }
    });
  };
  walk(nodes, {});
  return rows;
};

const csvEscape = (val) => {
  const s = val === null || val === undefined ? '' : String(val);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const toNumSafe = (v) => Number(String(v ?? 0).replace(/,/g, '')) || 0;

const buildHierarchy = (salePnds) => {
  const list = salePnds || [];
  if (list.length === 0) return [];
  const isCityGrouped   = Array.isArray(list[0]?.streets);
  const isStreetGrouped = !isCityGrouped && Array.isArray(list[0]?.pnds);

  if (isCityGrouped) return list;
  if (isStreetGrouped) return [{ city_name: null, city_cd: null, streets: list }];
  return [{ city_name: null, city_cd: null, streets: [{ street_name: null, street_cd: null, pnds: list }] }];
};

const calcTotals = (pnds) => (pnds || []).reduce((acc, p) => {
  acc.coins += toNumSafe(p.coins_sales);
  acc.notes += toNumSafe(p.notes_sales);
  acc.total += toNumSafe(p.total_sales);
  acc.count += 1;
  return acc;
}, { coins: 0, notes: 0, total: 0, count: 0 });

const exportReportToCSV = (report, locationName) => {
  const hierarchy = buildHierarchy(report?.sale_pnds);
  const params = report?.params || {};

  const headers = [
    'Project', 'Street', 'Device', 'Coins Sales', 'Notes Sales', 'Total Sales',
    'Collection Date', 'Collection Age (d)', 'Transaction Date', 'Transaction Age (d)',
    'Status Date', 'Device Status', 'Flag',
  ];

  const lines = [headers.map(csvEscape).join(',')];
  const grand = { coins: 0, notes: 0, total: 0, count: 0 };

  hierarchy.forEach((city) => {
    const cityTotals = { coins: 0, notes: 0, total: 0, count: 0 };

    (city.streets || []).forEach((street) => {
      const pnds = street.pnds || [];
      const streetTotals = calcTotals(pnds);

      pnds.forEach((p) => {
        lines.push([
          city.city_name || '',
          street.street_name || '',
          p.device_code,
          p.coins_sales ?? '',
          p.notes_sales ?? '',
          p.total_sales ?? '',
          p.collection_date || '',
          p.collection_age_days ?? '',
          p.last_trans_date || '',
          p.transaction_age_days ?? '',
          p.status_date || '',
          p.device_status || '',
          p.flag_type || '',
        ].map(csvEscape).join(','));
      });

      if (street.street_name) {
        lines.push([
          '', `Street Subtotal - ${street.street_name}`, `${streetTotals.count} device(s)`,
          streetTotals.coins.toFixed(2), streetTotals.notes.toFixed(2), streetTotals.total.toFixed(2),
          '', '', '', '', '', '', '',
        ].map(csvEscape).join(','));
      }

      cityTotals.coins += streetTotals.coins;
      cityTotals.notes += streetTotals.notes;
      cityTotals.total += streetTotals.total;
      cityTotals.count += streetTotals.count;
    });

    if (city.city_name) {
      lines.push([
        `Project Total - ${city.city_name}`, '', `${cityTotals.count} device(s)`,
        cityTotals.coins.toFixed(2), cityTotals.notes.toFixed(2), cityTotals.total.toFixed(2),
        '', '', '', '', '', '', '',
      ].map(csvEscape).join(','));
      lines.push('');
    }

    grand.coins += cityTotals.coins;
    grand.notes += cityTotals.notes;
    grand.total += cityTotals.total;
    grand.count += cityTotals.count;
  });

  lines.push([
    'GRAND TOTAL', '', `${grand.count} device(s)`,
    grand.coins.toFixed(2), grand.notes.toFixed(2), grand.total.toFixed(2),
    '', '', '', '', '', '', '',
  ].map(csvEscape).join(','));

  const csvContent = '\uFEFF' + lines.join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);

  const safeName = (locationName || 'All_Projects').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'Report';
  const fileName = `Sale_Collection_Report_${safeName}_${params.coll_days ?? ''}d_${params.trans_days ?? ''}d.csv`;

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const REPORT_PDF_FLAG_LABEL = {
  both: 'Collection + Transaction',
  collection: 'Collection',
  transaction: 'Transaction',
};

const exportReportToPDF = (report, locationName) => {
  const hierarchy = buildHierarchy(report?.sale_pnds);
  const params = report?.params || {};
  const summary = report?.summary || {};

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 10;
  const bottomLimit = pageHeight - 10;
  let y = 14;

  const pageCenterX = pageWidth / 2;
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(20);
  doc.text('Sale & Collection Report', pageCenterX, y, { align: 'center' });
  y += 6;

  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(locationName || 'All Projects', pageCenterX, y, { align: 'center' });
  y += 5;

  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(
    `Collection age >= ${params.coll_days ?? '-'}d  |  Transaction age >= ${params.trans_days ?? '-'}d  |  Generated: ${new Date().toLocaleString()}`,
    pageCenterX, y, { align: 'center' }
  );
  y += 6;

  doc.setFontSize(8);
  doc.setTextColor(0);
  doc.text(
    `Checked: ${summary.total_pnds_checked ?? '-'}   Flagged: ${summary.total_flagged ?? '-'}   Collection Only: ${summary.total_collection_only ?? '-'}   Transaction Only: ${summary.total_transaction_only ?? '-'}   Both: ${summary.total_both ?? '-'}`,
    pageCenterX, y, { align: 'center' }
  );
  y += 8;

  const columns = [
    { key: 'device_code',          label: 'Device',         width: 26 },
    { key: 'coins_sales',          label: 'Coins',          width: 20 },
    { key: 'notes_sales',          label: 'Notes',          width: 20 },
    { key: 'total_sales',          label: 'Total Sales',    width: 24 },
    { key: 'collection_date',      label: 'Coll. Date',     width: 21 },
    { key: 'collection_age_days',  label: 'Coll. Age',      width: 17 },
    { key: 'last_trans_date',      label: 'Trans. Date',    width: 21 },
    { key: 'transaction_age_days', label: 'Trans. Age',     width: 17 },
    { key: 'status_date',          label: 'Status Date',    width: 21 },
    { key: 'device_status',        label: 'Device Status',  width: 30 },
    { key: 'flag_type',            label: 'Flag',           width: 40 },
  ];
  const tableWidth = columns.reduce((s, c) => s + c.width, 0);
  const rowHeight = 6;
  const headerHeight = 7;
  const cellPad = 1.5;

  const colX = (idx) => marginX + columns.slice(0, idx).reduce((s, c) => s + c.width, 0);

  const fitText = (text, maxWidth) => {
    let t = String(text ?? '');
    if (doc.getTextWidth(t) <= maxWidth) return t;
    while (t.length > 0 && doc.getTextWidth(`${t}\u2026`) > maxWidth) {
      t = t.slice(0, -1);
    }
    return t.length ? `${t}\u2026` : '\u2026';
  };

  const ensureSpace = (needed) => {
    if (y + needed > bottomLimit) {
      doc.addPage();
      y = 14;
      return true;
    }
    return false;
  };

  const drawTableHeader = () => {
    let x = marginX;
    doc.setFillColor(243, 244, 246);
    doc.rect(marginX, y, tableWidth, headerHeight, 'F');
    doc.setFontSize(7.5);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(60);
    columns.forEach((col) => {
      doc.text(fitText(col.label, col.width - cellPad * 2), x + cellPad, y + 5);
      x += col.width;
    });
    y += headerHeight;
    doc.setFont(undefined, 'normal');
  };

  const drawGroupBar = (label, level, totals) => {
    const broke = ensureSpace(rowHeight + 8);
    if (broke) { drawTableHeader(); doc.setFontSize(7); }
    const shade = level === 'city' ? [191, 219, 254] : [221, 214, 254];
    const indent = level === 'city' ? 0 : 6;
    doc.setFillColor(...shade);
    doc.rect(marginX, y, tableWidth, rowHeight + 1, 'F');
    doc.setFontSize(level === 'city' ? 9 : 8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(20);

    let labelMaxWidth = tableWidth - indent - cellPad * 2;
    if (totals) {
      const summaryText = `${totals.count} device${totals.count === 1 ? '' : 's'}   \u2022   Coins: ${totals.coins.toFixed(2)}   Notes: ${totals.notes.toFixed(2)}   Total: ${totals.total.toFixed(2)}`;
      const summaryWidth = doc.getTextWidth(summaryText);
      const rightEdge = marginX + tableWidth - cellPad;
      doc.text(summaryText, rightEdge, y + 5, { align: 'right' });
      labelMaxWidth = tableWidth - indent - summaryWidth - cellPad * 4;
    }
    doc.text(fitText(label, labelMaxWidth), marginX + cellPad + indent, y + 5);
    y += rowHeight + 1;
    doc.setFont(undefined, 'normal');
  };

  const drawTotalsRow = (label, totals, shade, fontSize = 7.5) => {
    ensureSpace(rowHeight);
    doc.setFillColor(...shade);
    doc.rect(marginX, y, tableWidth, rowHeight, 'F');
    doc.setFontSize(fontSize);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(20);

    const summaryText = `Coins: ${totals.coins.toFixed(2)}    Notes: ${totals.notes.toFixed(2)}    Total: ${totals.total.toFixed(2)}`;
    const summaryWidth = doc.getTextWidth(summaryText);
    const rightEdge = marginX + tableWidth - cellPad;
    doc.text(summaryText, rightEdge, y + 4.2, { align: 'right' });

    const labelMaxWidth = tableWidth - summaryWidth - cellPad * 4;
    const fullLabel = `${label}  (${totals.count} device${totals.count === 1 ? '' : 's'})`;
    doc.text(fitText(fullLabel, labelMaxWidth), marginX + cellPad, y + 4.2);

    y += rowHeight;
    doc.setFont(undefined, 'normal');
  };

  const drawDeviceRow = (p, zebra) => {
    if (ensureSpace(rowHeight)) {
      drawTableHeader();
      doc.setFontSize(7);
    }
    if (zebra) {
      doc.setFillColor(250, 250, 250);
      doc.rect(marginX, y, tableWidth, rowHeight, 'F');
    }
    const moneyOrDash = (v) => (v != null && v !== '' ? Number(v).toFixed(2) : '-');
    const values = {
      device_code: p.device_code || '-',
      coins_sales: moneyOrDash(p.coins_sales),
      notes_sales: moneyOrDash(p.notes_sales),
      total_sales: moneyOrDash(p.total_sales),
      collection_date: p.collection_date || '-',
      collection_age_days: p.collection_age_days != null ? `${p.collection_age_days}d` : '-',
      last_trans_date: p.last_trans_date || '-',
      transaction_age_days: p.transaction_age_days != null ? `${p.transaction_age_days}d` : '-',
      status_date: p.status_date || '-',
      device_status: p.device_status || '-',
      flag_type: REPORT_PDF_FLAG_LABEL[p.flag_type] || p.flag_type || '-',
    };
    let x = marginX;
    doc.setTextColor(30);
    columns.forEach((col) => {
      const text = fitText(values[col.key], col.width - cellPad * 2);
      doc.text(text, x + cellPad, y + 4.2);
      x += col.width;
    });
    y += rowHeight;
  };

  drawTableHeader();
  doc.setFontSize(7);

  const hasAnyPnd = hierarchy.some((c) => (c.streets || []).some((s) => (s.pnds || []).length > 0));
  const grand = { coins: 0, notes: 0, total: 0, count: 0 };

  hierarchy.forEach((city) => {
    const cityPnds = (city.streets || []).flatMap((s) => s.pnds || []);
    const cityTotals = calcTotals(cityPnds);
    if (city.city_name) drawGroupBar(`Project: ${city.city_name}`, 'city', cityTotals);

    (city.streets || []).forEach((street) => {
      const pnds = street.pnds || [];
      const streetTotals = calcTotals(pnds);
      if (street.street_name) drawGroupBar(`Street: ${street.street_name}`, 'street', streetTotals);

      pnds.forEach((p, i) => drawDeviceRow(p, i % 2 === 1));
    });

    if (city.city_name) y += 2;
    grand.coins += cityTotals.coins;
    grand.notes += cityTotals.notes;
    grand.total += cityTotals.total;
    grand.count += cityTotals.count;
  });

  if (!hasAnyPnd) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text('No flagged devices found for this period.', marginX, y + 6);
  } else {
    ensureSpace(rowHeight + 2);
    y += 1;
    drawTotalsRow('GRAND TOTAL', grand, [209, 250, 229], 9);
  }

  const safeName = (locationName || 'All_Projects').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'Report';
  doc.save(`Sale_Collection_Report_${safeName}_${params.coll_days ?? ''}d_${params.trans_days ?? ''}d.pdf`);
};

const REPORT_FLAG_META = {
  both:        { label: 'Collection + Transaction', cls: 'bg-rose-500/15 text-rose-500' },
  collection:  { label: 'Collection',                cls: 'bg-amber-500/15 text-amber-500' },
  transaction: { label: 'Transaction',                cls: 'bg-orange-500/15 text-orange-500' },
};

const ReportSummaryStat = ({ label, value, tone = 'default' }) => {
  const toneClasses = {
    default: 'text-foreground',
    amber: 'text-amber-500',
    red: 'text-red-500',
    rose: 'text-rose-500',
  };
  return (
    <div className="text-center px-1">
      <p className="text-[9px] uppercase tracking-wide text-muted-foreground mb-0.5">{label}</p>
      <p className={`text-base font-bold ${toneClasses[tone] || toneClasses.default}`}>{formatCount(value)}</p>
    </div>
  );
};

const formatReportMoney = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? (v ?? '\u2014') : n.toLocaleString(undefined, { minimumFractionDigits: 2 });
};

const ReportDeviceTable = ({ rows }) => (
  <table className="w-full text-[11px]">
    <thead className="bg-secondary/30 border-b border-border text-muted-foreground">
      <tr>
        <th className="text-left font-semibold px-4 py-1.5 whitespace-nowrap">Device</th>
        <th className="text-right font-semibold px-2 py-1.5 whitespace-nowrap">Coins</th>
        <th className="text-right font-semibold px-2 py-1.5 whitespace-nowrap">Notes</th>
        <th className="text-right font-semibold px-2 py-1.5 whitespace-nowrap">Total Sales</th>
        <th className="text-left font-semibold px-2 py-1.5 whitespace-nowrap">Coll. Date</th>
        <th className="text-right font-semibold px-2 py-1.5 whitespace-nowrap">Coll. Age</th>
        <th className="text-left font-semibold px-2 py-1.5 whitespace-nowrap">Trans. Date</th>
        <th className="text-right font-semibold px-2 py-1.5 whitespace-nowrap">Trans. Age</th>
        <th className="text-left font-semibold px-2 py-1.5 whitespace-nowrap">Status Date</th>
        <th className="text-left font-semibold px-2 py-1.5 whitespace-nowrap">Device Status</th>
        <th className="text-left font-semibold px-4 py-1.5 whitespace-nowrap">Flag</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((p, i) => {
        const flagMeta = REPORT_FLAG_META[p.flag_type] || { label: p.flag_type || '\u2014', cls: 'bg-secondary text-muted-foreground' };
        return (
          <tr key={`${p.device_code}-${i}`} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
            <td className="px-4 py-2 font-mono text-foreground whitespace-nowrap">{p.device_code}</td>
            <td className="px-2 py-2 text-right text-foreground whitespace-nowrap">{formatReportMoney(p.coins_sales)}</td>
            <td className="px-2 py-2 text-right text-foreground whitespace-nowrap">{formatReportMoney(p.notes_sales)}</td>
            <td className="px-2 py-2 text-right font-semibold text-foreground whitespace-nowrap">{formatReportMoney(p.total_sales)}</td>
            <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{p.collection_date || '\u2014'}</td>
            <td className="px-2 py-2 text-right text-muted-foreground whitespace-nowrap">{p.collection_age_days != null ? `${p.collection_age_days}d` : '\u2014'}</td>
            <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{p.last_trans_date || '\u2014'}</td>
            <td className="px-2 py-2 text-right text-muted-foreground whitespace-nowrap">{p.transaction_age_days != null ? `${p.transaction_age_days}d` : '\u2014'}</td>
            <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{p.status_date || '\u2014'}</td>
            <td className="px-2 py-2">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold whitespace-nowrap ${
                p.device_status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'
              }`}>
                <CircleDot className="w-2 h-2" /> {p.device_status || '\u2014'}
              </span>
            </td>
            <td className="px-4 py-2">
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold whitespace-nowrap ${flagMeta.cls}`}>
                {flagMeta.label}
              </span>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const ReportStreetGroup = ({ street, cityLabel, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const rows  = street.pnds || [];
  const count = street.total ?? rows.length;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 px-4 py-2 bg-secondary/50 hover:bg-secondary/70 border-b border-border transition-colors text-left"
      >
        <ChevronDown className={`w-3 h-3 text-muted-foreground flex-shrink-0 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`} />
        <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
        <span className="text-xs font-semibold text-foreground">{street.street_name}</span>
        <span className="text-[9px] font-mono text-muted-foreground whitespace-nowrap">Code: {street.street_cd}</span>
        {cityLabel && <span className="text-[9px] text-muted-foreground whitespace-nowrap">• {cityLabel}</span>}
        <span className="ml-auto text-[9px] font-semibold px-1.5 py-0.5 rounded-md bg-primary/15 text-primary whitespace-nowrap">
          {count} PND{count === 1 ? '' : 's'}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <ReportDeviceTable rows={rows} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ReportCityGroup = ({ city, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const streets = city.streets || [];
  const count   = city.total ?? streets.reduce((s, st) => s + (st.pnds?.length || 0), 0);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-primary/10 hover:bg-primary/15 border-b border-border sticky top-0 z-10 transition-colors text-left"
      >
        <ChevronDown className={`w-3.5 h-3.5 text-primary flex-shrink-0 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`} />
        <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
        <span className="text-sm font-bold text-foreground">{city.city_name}</span>
        <span className="text-[9px] font-mono text-muted-foreground whitespace-nowrap">Code: {city.city_cd}</span>
        <span className="ml-auto text-[9px] font-semibold px-1.5 py-0.5 rounded-md bg-primary/15 text-primary whitespace-nowrap">
          {count} PNDs
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="divide-y divide-border">
              {streets.map((st, i) => <ReportStreetGroup key={st.street_cd || i} street={st} cityLabel={null} />)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SaleCollectionReportModal = ({ report, locationName, onClose }) => {
  const salePnds       = report?.sale_pnds || [];
  const isCityGrouped  = salePnds.length > 0 && Array.isArray(salePnds[0]?.streets);
  const isStreetGrouped = salePnds.length > 0 && !isCityGrouped && Array.isArray(salePnds[0]?.pnds);
  const isFlat         = salePnds.length > 0 && !isCityGrouped && !isStreetGrouped;
  const totalDeviceCount = useMemo(() => flattenSalePnds(salePnds).length, [salePnds]);
  const summary = report?.summary || {};
  const params  = report?.params || {};

  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportBtnRef  = useRef(null);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!showExportMenu) return;
    const onClickOutside = (e) => {
      if (
        exportMenuRef.current && !exportMenuRef.current.contains(e.target) &&
        exportBtnRef.current && !exportBtnRef.current.contains(e.target)
      ) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showExportMenu]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        transition={{ type: 'spring', damping: 26, stiffness: 260 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[75vw] max-h-[85vh] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden"
      >
        <div className="relative flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
              <FileBarChart2 className="w-4 h-4 text-primary" />
            </div>
          </div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center px-16">
            <h3 className="text-sm font-bold text-foreground truncate max-w-[60vw]">
              {locationName || 'Sale & Collection Report'}
            </h3>
            <p className="text-[10px] text-muted-foreground font-mono">
              {locationName && <>Sale &amp; Collection Report • </>}
              Collection age ≥ {params.coll_days ?? '—'}d • Transaction age ≥ {params.trans_days ?? '—'}d
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="relative">
              <button
                ref={exportBtnRef}
                onClick={() => setShowExportMenu(v => !v)}
                title="Export report"
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-colors ${
                  showExportMenu
                    ? 'border-primary bg-primary/15 text-primary'
                    : 'border-border bg-secondary hover:bg-secondary/70 text-foreground'
                }`}
              >
                <Download className="w-3.5 h-3.5 text-primary" />
                <span className="hidden sm:inline">Export</span>
                <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {showExportMenu && (
                  <motion.div
                    ref={exportMenuRef}
                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-1.5 w-40 rounded-xl border border-border bg-card shadow-xl z-[70] p-1.5"
                  >
                    <button
                      onClick={() => { exportReportToCSV(report, locationName); setShowExportMenu(false); }}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground hover:bg-secondary transition-colors text-left"
                    >
                      <FileBarChart2 className="w-3.5 h-3.5 text-primary" /> Export as CSV
                    </button>
                    <button
                      onClick={() => { exportReportToPDF(report, locationName); setShowExportMenu(false); }}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground hover:bg-secondary transition-colors text-left"
                    >
                      <FileBarChart2 className="w-3.5 h-3.5 text-primary" /> Export as PDF
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 px-5 py-3 border-b border-border bg-secondary/40 flex-shrink-0">
          <ReportSummaryStat label="Checked" value={summary.total_pnds_checked} />
          <ReportSummaryStat label="Flagged" value={summary.total_flagged} tone="amber" />
          <ReportSummaryStat label="Collection Only" value={summary.total_collection_only} tone="red" />
          <ReportSummaryStat label="Transaction Only" value={summary.total_transaction_only} tone="red" />
          <ReportSummaryStat label="Both" value={summary.total_both} tone="rose" />
        </div>

        <div className="flex-1 overflow-y-auto cb-scroll">
          {totalDeviceCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-muted-foreground">
              <CircleParking className="w-8 h-8 mb-2 opacity-40" />
              <p className="text-xs">No flagged devices found for this period.</p>
            </div>
          ) : isFlat ? (
            <ReportDeviceTable rows={salePnds} />
          ) : isCityGrouped ? (
            <div className="divide-y divide-border">
              {salePnds.map((ct, i) => <ReportCityGroup key={ct.city_cd || i} city={ct} />)}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {salePnds.map((st, i) => <ReportStreetGroup key={st.street_cd || i} street={st} />)}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

// ─── Draggable Legend ──────────────────────────────────────────────────────────
const BottomLeftLegend = ({ isDarkMode, sidebarOpen = true }) => {
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const legendRef  = useRef(null);
  const [legendPos, setLegendPos] = useState(null);
  const isDragging  = useRef(false);
  const dragStart   = useRef({ x: 0, y: 0, top: 0, left: 0 });

  const handleDragMouseDown = (e) => {
    const rect = legendRef.current?.getBoundingClientRect();
    if (!rect) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY, top: rect.top, left: rect.left };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setLegendPos({
        top:  Math.max(8, dragStart.current.top  + dy),
        left: Math.max(8, dragStart.current.left + dx),
      });
    };
    const onUp = () => { isDragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const items = [
    { color: '#8B5CF6', label: 'BNA', desc: 'BNA installed and active' },
    { color: '#F59E0B', label: 'TX', desc: 'TX installed and active' },
    { color: '#9CA3AF', label: 'Inactive', desc: 'PND is currently inactive' },
    { color: '#EF4444', label: 'No Transaction', desc: 'Installed but no transaction recorded' },
    { color: '#000000', label: 'Below Threshold', desc: 'Transaction count is below the set threshold' },
  ];

  const MiniMarker = ({ color, slash }) => (
    <svg width="14" height="14" viewBox="0 0 64 64" style={{ flexShrink: 0 }}>
      <path
        d="M32 3C20 3 10 13 10 25c0 18 22 36 22 36s22-18 22-36C54 13 44 3 32 3z"
        fill={color}
      />
      <circle cx="32" cy="25" r="11" fill="white" />
      <circle cx="32" cy="25" r="5" fill={color} />
      {slash && (
        <line
          x1="10" y1="14" x2="54" y2="46"
          stroke="#EF4444"
          strokeWidth="4.5"
          strokeLinecap="round"
        />
      )}
    </svg>
  );

  return (
    <div
      ref={legendRef}
      style={{
        position: 'fixed',
        ...(legendPos
          ? { top: `${legendPos.top}px`, left: `${legendPos.left}px` }
          : { top: '74px', right: '530px' }),
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '8px 14px',
        borderRadius: '8px',
        border: `1px solid ${isDarkMode ? 'rgba(75,85,99,0.3)' : 'rgba(209,213,219,0.5)'}`,
        backgroundColor: isDarkMode ? 'rgba(0,0,0,0.75)' : 'rgba(255,255,255,0.85)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        onMouseDown={handleDragMouseDown}
        title="Drag to move"
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: isDarkMode ? '#6B7280' : '#9CA3AF',
          marginRight: '2px',
          flexShrink: 0,
        }}
      >
        <GripVertical size={14} />
      </div>
      {items.map((item) => (
        <div
          key={item.label}
          onMouseEnter={() => setHoveredLabel(item.label)}
          onMouseLeave={() => setHoveredLabel(null)}
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            cursor: 'default',
          }}
        >
          <MiniMarker color={item.color} slash={item.slash} />
          <span
            style={{
              fontSize: '9px',
              fontWeight: 500,
              color: isDarkMode ? '#D1D5DB' : '#374151',
              whiteSpace: 'nowrap',
            }}
          >
            {item.label}
          </span>

          {hoveredLabel === item.label && (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '8px',
                padding: '6px 10px',
                borderRadius: '6px',
                backgroundColor: isDarkMode ? '#1F2937' : '#111827',
                color: '#fff',
                fontSize: '10px',
                fontWeight: 400,
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                zIndex: 999999,
                pointerEvents: 'none',
              }}
            >
              {item.desc}
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderTop: `5px solid ${isDarkMode ? '#1F2937' : '#111827'}`,
                }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================
// GoogleMapComponent
// ============================================================
const GoogleMapComponent = ({
  citiesData = [],
  countryInfo = null,
  selectedCity,
  selectedStreet,
  selectedPnd,
  onPndClick,
  onCityClick,
  onStreetClick,
  isDarkMode,
  belowThresholdPnds = new Set(),
  noTransactionPnds  = new Set(),
  shimmerMode = 'overview',
  pndStatusFilters = EMPTY_FILTERS,
  streetsOnlyMode = false,
  pndRevenueMap = new Map(),
  sidebarOpen = true,
  detailPanelWidth = 0,
  filteredMapData = null,
  filteredMapLoading = false,
}) => {
  const mapRef               = useRef(null);
  const mapInstanceRef       = useRef(null);
  const polygonsRef          = useRef([]);
  const markersRef           = useRef([]);
  const animationFrameRef    = useRef(null);
  const highlightedMarkerRef = useRef(null);
  const markerMapRef         = useRef({});
  const mapReadyRef          = useRef(false);
  const streetInfoWindowRef  = useRef(null);
  const pndInfoWindowRef     = useRef(null);
  const hoveredMarkerActiveRef = useRef(false);
  const __cdMapLabelsVisibleToggleRef = useRef(true);
  const belowThresholdRef = useRef(belowThresholdPnds);
  const noTransactionRef  = useRef(noTransactionPnds);
  const revenueMapRef     = useRef(pndRevenueMap);
  const pndStatusFiltersRef = useRef(pndStatusFilters);

  useEffect(() => { belowThresholdRef.current = belowThresholdPnds; }, [belowThresholdPnds]);
  useEffect(() => { noTransactionRef.current  = noTransactionPnds;  }, [noTransactionPnds]);
  useEffect(() => { revenueMapRef.current     = pndRevenueMap;      }, [pndRevenueMap]);
  useEffect(() => { pndStatusFiltersRef.current = pndStatusFilters; }, [pndStatusFilters]);

  const filteredMapDataRef = useRef(filteredMapData);
  useEffect(() => { filteredMapDataRef.current = filteredMapData; }, [filteredMapData]);

  const [mapLoading, setMapLoading] = useState(true);

  const getPndKey = (pnd) => `${pnd.latitude}_${pnd.longitude}`;

  useEffect(() => {
    if (!document.getElementById('google-maps-script')) {
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_KEY}&libraries=geometry`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const styleId = 'gm-infowindow-no-close-btn';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .gm-style-iw-chr {
        display: none !important;
        height: 0 !important;
        min-height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .gm-ui-hover-effect {
        display: none !important;
      }
      .gm-style-iw-c {
        padding: 6px !important;
        padding-top: 6px !important;
      }
      .gm-style-iw-d {
        overflow: hidden !important;
        padding: 0 !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

    useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const init = () => {
      if (!window.google) { setTimeout(init, 100); return; }
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 24.0, lng: 45.0 },
        zoom: 6,
        styles: isDarkMode
          ? getDarkMapStyles(!__cdMapLabelsVisibleToggleRef.current)
          : getLightMapStyles(!__cdMapLabelsVisibleToggleRef.current),
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: false,
      });

      const map = mapInstanceRef.current;

      // ── Toggle button hover style + Labels popup animation ──
      if (!document.getElementById('gm-view-toggle-style')) {
        const style = document.createElement('style');
        style.id = 'gm-view-toggle-style';
        style.textContent = `
          .gm-view-toggle-btn { transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.12s ease; }
          .gm-view-toggle-btn:hover:not(.gm-view-toggle-active) {
            background: ${isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.06)'} !important;
          }
          .gm-labels-popup {
            transition: opacity 0.25s ease, transform 0.25s ease;
          }
          .gm-labels-popup.gm-hidden {
            opacity: 0;
            transform: translateY(6px) scale(0.95);
            pointer-events: none;
          }
        `;
        document.head.appendChild(style);
      }

      // ── Outer vertical stack: Labels (upar) + Default/Satellite (neeche) ──
      const outerWrapper = document.createElement('div');
      outerWrapper.style.cssText = `
        display: flex; flex-direction: column; align-items: center; gap: 6px;
        margin: 12px;
        font-family: 'Inter', Roboto, Arial, sans-serif; user-select: none;
      `;

      // ── Labels wrapper (initially HIDDEN) ──
      const labelsWrapper = document.createElement('div');
      labelsWrapper.className = 'gm-labels-popup gm-hidden';
      labelsWrapper.style.cssText = `
        display: flex; gap: 2px; padding: 3px;
        background: ${isDarkMode ? 'rgba(31,41,55,0.95)' : 'rgba(255,255,255,0.97)'};
        backdrop-filter: blur(6px);
        border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'};
        border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
        font-size: 12px;
      `;

      // ── Default / Satellite wrapper ──
      const wrapper = document.createElement('div');
      wrapper.style.cssText = `
        display: flex; gap: 2px; padding: 3px;
        background: ${isDarkMode ? 'rgba(31,41,55,0.95)' : 'rgba(255,255,255,0.97)'};
        backdrop-filter: blur(6px);
        border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'};
        border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
        font-size: 13px;
      `;

      // ── Auto-hide timer ──
      let labelsHideTimer = null;
      const showLabelsBtnTemporarily = () => {
        labelsWrapper.classList.remove('gm-hidden');
        if (labelsHideTimer) clearTimeout(labelsHideTimer);
        labelsHideTimer = setTimeout(() => {
          labelsWrapper.classList.add('gm-hidden');
        }, 2500);
      };
      labelsWrapper.addEventListener('mouseenter', () => {
        if (labelsHideTimer) clearTimeout(labelsHideTimer);
      });
      labelsWrapper.addEventListener('mouseleave', () => {
        if (labelsHideTimer) clearTimeout(labelsHideTimer);
        labelsHideTimer = setTimeout(() => {
          labelsWrapper.classList.add('gm-hidden');
        }, 1500);
      });

      // ── Default/Satellite button banao ──
      const makeBtn = (label, type) => {
        const btn = document.createElement('div');
        btn.textContent = label;
        btn.dataset.type = type;
        btn.className = 'gm-view-toggle-btn';
        btn.style.cssText = `
          padding: 7px 16px; cursor: pointer; white-space: nowrap; border-radius: 999px;
          font-weight: 500; letter-spacing: 0.01em;
          color: ${isDarkMode ? '#cbd5e1' : '#475569'};
        `;
        btn.addEventListener('click', () => {
          map.setMapTypeId(type);
          updateActive(type);
          showLabelsBtnTemporarily();
        });
        return btn;
      };

      const defaultBtn   = makeBtn('Default View', window.google.maps.MapTypeId.ROADMAP);
      const satelliteBtn = makeBtn('Satellite View', 'hybrid');

      const updateActive = (activeType) => {
        [defaultBtn, satelliteBtn].forEach((btn) => {
          const isActive = btn.dataset.type === activeType;
          btn.classList.toggle('gm-view-toggle-active', isActive);
          btn.style.background = isActive ? 'var(--primary)' : 'transparent';
          btn.style.color = isActive ? 'var(--primary-foreground)' : (isDarkMode ? '#cbd5e1' : '#475569');
          btn.style.fontWeight = isActive ? '600' : '500';
          btn.style.boxShadow = isActive ? '0 1px 4px rgba(73,127,253,0.4)' : 'none';
        });
      };
      updateActive(window.google.maps.MapTypeId.ROADMAP);

      // ── Labels toggle button (initially hidden) ──
      const labelsBtn = document.createElement('div');
      labelsBtn.textContent = 'Labels';
      labelsBtn.className = 'gm-view-toggle-btn';
      labelsBtn.style.cssText = `
        padding: 5px 14px; cursor: pointer; white-space: nowrap; border-radius: 999px;
        font-weight: 500; letter-spacing: 0.01em;
        color: ${isDarkMode ? '#cbd5e1' : '#475569'};
      `;

      const applyMapStyles = () => {
        map.setOptions({
          styles: isDarkMode
            ? getDarkMapStyles(!__cdMapLabelsVisibleToggleRef.current)
            : getLightMapStyles(!__cdMapLabelsVisibleToggleRef.current),
        });
      };

      const updateLabelsBtnStyle = () => {
        const isOn = __cdMapLabelsVisibleToggleRef.current;
        labelsBtn.classList.toggle('gm-view-toggle-active', isOn);
        labelsBtn.style.background = isOn ? 'var(--primary)' : 'transparent';
        labelsBtn.style.color = isOn
          ? 'var(--primary-foreground)'
          : isDarkMode
            ? '#cbd5e1'
            : '#475569';
        labelsBtn.style.fontWeight = isOn ? '600' : '500';
        labelsBtn.style.boxShadow = isOn ? '0 1px 4px rgba(73,127,253,0.4)' : 'none';
      };
      updateLabelsBtnStyle();

      labelsBtn.addEventListener('click', () => {
        __cdMapLabelsVisibleToggleRef.current = !__cdMapLabelsVisibleToggleRef.current;
        applyMapStyles();
        updateLabelsBtnStyle();
        showLabelsBtnTemporarily();
      });

      // ── Assemble ──
      labelsWrapper.appendChild(labelsBtn);
      wrapper.appendChild(defaultBtn);
      wrapper.appendChild(satelliteBtn);
      outerWrapper.appendChild(labelsWrapper);
      outerWrapper.appendChild(wrapper);

      map.controls[window.google.maps.ControlPosition.BOTTOM_CENTER].push(outerWrapper);
    };
    init();
  }, [isDarkMode]);

    useEffect(() => {
    if (mapInstanceRef.current)
      mapInstanceRef.current.setOptions({
        styles: isDarkMode
          ? getDarkMapStyles(!__cdMapLabelsVisibleToggleRef.current)
          : getLightMapStyles(!__cdMapLabelsVisibleToggleRef.current),
      });
  }, [isDarkMode]);

  const createMarkerIcon = useCallback((deviceType, color, isSelected = false, isNoTransaction = false) => {
    const size    = isSelected ? 50 : 38;
    const opacity = isSelected ? '1' : '0.85';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64">
      <path d="M32 3C20 3 10 13 10 25c0 18 22 36 22 36s22-18 22-36C54 13 44 3 32 3z" fill="${color}" opacity="${opacity}"/>
      <circle cx="32" cy="25" r="11" fill="white" opacity="${opacity}"/>
      <circle cx="32" cy="25" r="5" fill="${color}" opacity="${opacity}"/>
    </svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size / 2, size),
    };
  }, []);

  const sortByPrincipalAxis = useCallback((coords) => {
    if (coords.length <= 2) return coords;
    const cx = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
    const cy = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
    let xx = 0, xy = 0, yy = 0;
    coords.forEach((c) => {
      const dx = c.lng - cx; const dy = c.lat - cy;
      xx += dx * dx; xy += dx * dy; yy += dy * dy;
    });
    const angle = Math.atan2(2 * xy, xx - yy) / 2;
    const ax = Math.cos(angle); const ay = Math.sin(angle);
    return [...coords].sort((a, b) => {
      const pa = (a.lng - cx) * ax + (a.lat - cy) * ay;
      const pb = (b.lng - cx) * ax + (b.lat - cy) * ay;
      return pa - pb;
    });
  }, []);

  const createRoadPolygon = useCallback((points, offsetAmount) => {
    const sorted = sortByPrincipalAxis(points);
    const leftSide = []; const rightSide = [];
    sorted.forEach((point, index) => {
      const prev = sorted[index - 1] || point;
      const next = sorted[index + 1] || point;
      const dx = next.lng - prev.lng; const dy = next.lat - prev.lat;
      const safeLength = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = -dy / safeLength; const ny = dx / safeLength;
      leftSide.push({ lat: point.lat + ny * offsetAmount, lng: point.lng + nx * offsetAmount });
      rightSide.push({ lat: point.lat - ny * offsetAmount, lng: point.lng - nx * offsetAmount });
    });
    return [...leftSide, ...rightSide.reverse()];
  }, [sortByPrincipalAxis]);

  const computeBoundingBox = (points) => {
    if (!points.length) return null;
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    points.forEach(p => {
      minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat);
      minLng = Math.min(minLng, p.lng); maxLng = Math.max(maxLng, p.lng);
    });
    return { minLat, maxLat, minLng, maxLng };
  };

  const expandBoundingBox = (bbox, meters = 150) => {
    if (!bbox) return null;
    const latCenter = (bbox.minLat + bbox.maxLat) / 2;
    const latDelta  = meters / 111320;
    const lngDelta  = meters / (111320 * Math.cos(latCenter * Math.PI / 180));
    return { minLat: bbox.minLat - latDelta, maxLat: bbox.maxLat + latDelta, minLng: bbox.minLng - lngDelta, maxLng: bbox.maxLng + lngDelta };
  };

  const bboxToLatLngArray = (bbox) => {
    if (!bbox) return [];
    return [
      { lat: bbox.minLat, lng: bbox.minLng }, { lat: bbox.minLat, lng: bbox.maxLng },
      { lat: bbox.maxLat, lng: bbox.maxLng }, { lat: bbox.maxLat, lng: bbox.minLng },
    ];
  };

  const buildBboxRect = useCallback((pnds, expandMeters = 300) => {
    const valid = pnds.filter(p => p.latitude && p.longitude && !isNaN(parseFloat(p.latitude)) && !isNaN(parseFloat(p.longitude)));
    if (!valid.length) return null;
    const raw = valid.map(p => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }));
    return bboxToLatLngArray(expandBoundingBox(computeBoundingBox(raw), expandMeters))
      .map(p => new window.google.maps.LatLng(p.lat, p.lng));
  }, []);

  const smoothFlyTo = useCallback((map, targetBounds, targetZoom, onComplete) => {
    if (!map || !window.google) return;
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

    const padding = {
      top: 60,
      bottom: 60,
      left: 40,
      right: detailPanelWidth > 0 ? detailPanelWidth + 40 : 40,
    };

    const startCenter   = map.getCenter();
    const startZoom      = map.getZoom();
    const prelimCenter   = targetBounds.getCenter();
    const midZoom         = Math.min(startZoom, targetZoom || 15) - 1.5;
    const duration         = 900;
    let startTime           = null;
    const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const step = (ts) => {
      if (!startTime) startTime = ts;
      const rawT = Math.min((ts - startTime) / duration, 1);
      const t    = ease(rawT);
      map.panTo({
        lat: startCenter.lat() + (prelimCenter.lat() - startCenter.lat()) * t,
        lng: startCenter.lng() + (prelimCenter.lng() - startCenter.lng()) * t,
      });
      map.setZoom(
        rawT < 0.4
          ? startZoom + (midZoom - startZoom) * ease(rawT / 0.4)
          : midZoom + ((targetZoom || 15) - midZoom) * ease((rawT - 0.4) / 0.6)
      );
      if (rawT < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else {
        window.google.maps.event.addListenerOnce(map, 'idle', () => {
          if (targetZoom) {
            const fittedZoom = map.getZoom();
            if (fittedZoom > targetZoom) {
              map.setZoom(targetZoom);
            }
          }
        });
        map.fitBounds(targetBounds, padding);
        onComplete?.();
      }
    };
    animationFrameRef.current = requestAnimationFrame(step);
  }, [detailPanelWidth]);

  const clearAll = useCallback(() => {
    polygonsRef.current.forEach(p => p?.setMap?.(null)); polygonsRef.current = [];
    markersRef.current.forEach(m => m?.setMap?.(null));  markersRef.current  = [];
    markerMapRef.current = {}; highlightedMarkerRef.current = null;
    streetInfoWindowRef.current?.close();
    pndInfoWindowRef.current?.close();
  }, []);

  const highlightMarker = useCallback((pnd) => {
    if (!pnd || !window.google) return;
    const key = getPndKey(pnd);
    Object.values(markerMapRef.current).forEach(m => {
      m.setIcon(createMarkerIcon(m.__pndDeviceType, m.__deviceColor || getDeviceColor(m.__pndDeviceType), false, m.__isNoTransaction || false));
      m.setZIndex(100);
    });
    const m = markerMapRef.current[key];
    if (m) {
      m.setIcon(createMarkerIcon(pnd.device_type, m.__deviceColor || getDeviceColor(pnd.device_type), true, m.__isNoTransaction || false));
      m.setZIndex(300);
      highlightedMarkerRef.current = m;
    }
  }, [createMarkerIcon]);

  const getMarkerColor = useCallback((deviceType, isBelowThreshold, isNoTransaction, isInactive) => {
    if (isInactive)       return '#9CA3AF';
    if (isNoTransaction)  return '#EF4444';
    if (isBelowThreshold) return '#000000';
    return getDeviceColor(deviceType);
  }, []);

  const addPndMarker = useCallback((pnd, index, map) => {
    const lat = parseFloat(pnd.latitude); const lng = parseFloat(pnd.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    const pndNumStr        = String(pnd.pnd_number ?? pnd.pnd ?? '');
    const isBelowThreshold = belowThresholdRef.current.has(pndNumStr);
    const isNoTransaction  = noTransactionRef.current.has(pndNumStr);
    const isInactive       = pnd.device_status === 'INACTIVE';

    const deviceColor = getMarkerColor(pnd.device_type, isBelowThreshold, isNoTransaction, isInactive);

    const pndKey = getPndKey(pnd);
    const marker = new window.google.maps.Marker({
      position: { lat, lng }, map,
      icon: createMarkerIcon(pnd.device_type, deviceColor, false, isNoTransaction),
      zIndex: 100 + index,
    });
    marker.__pndKey          = pndKey;
    marker.__pndDeviceType   = pnd.device_type;
    marker.__deviceColor     = deviceColor;
    marker.__isNoTransaction = isNoTransaction;
    marker.__isInactive      = isInactive;
    marker.__pndNumber       = pndNumStr;
    markerMapRef.current[pndKey] = marker;

    marker.addListener('mouseover', () => {
      hoveredMarkerActiveRef.current = true;
      streetInfoWindowRef.current?.close();

      if (highlightedMarkerRef.current !== marker) {
        marker.setIcon(createMarkerIcon(pnd.device_type, marker.__deviceColor, true, marker.__isNoTransaction));
      }

      const revInfo   = revenueMapRef.current.get(pndNumStr);
      const dTypeName = getDeviceTypeName(pnd.device_type);

      if (!pndInfoWindowRef.current) {
        pndInfoWindowRef.current = new window.google.maps.InfoWindow({ disableAutoPan: true });
      }

      const revenueHtml = revInfo
        ? `<div style="margin-top:3px;font-size:11px;font-weight:700;color:#059669;">Revenue: ${revInfo.collection ?? '0'}</div>
           <div style="font-size:10px;color:#6b7280;margin-top:1px;">Tickets: ${revInfo.ticket_count ?? '0'}</div>`
        : `<div style="margin-top:3px;font-size:10px;color:#9ca3af;">No revenue data for this period</div>`;

      const datesHtml = `<div style="font-size:10px;color:#6b7280;margin-top:3px;">Inactive Date: ${revInfo?.device_status_date ?? pnd.device_status_date ?? '—'}</div>
           <div style="font-size:10px;color:#6b7280;margin-top:1px;">Active Date: ${revInfo?.device_active_date ?? pnd.device_active_date ?? '—'}</div>`;

      pndInfoWindowRef.current.setContent(
        `<div style="padding:3px 5px;min-width:120px;font-family:sans-serif;">
          <div style="font-size:12px;font-weight:700;color:#111827;">${pnd.pnd_number ? '#' + pnd.pnd_number + ' — ' : ''}${dTypeName}</div>
          ${revenueHtml}
          ${datesHtml}
        </div>`
      );
      pndInfoWindowRef.current.open({ anchor: marker, map });
    });

    marker.addListener('mouseout', () => {
      hoveredMarkerActiveRef.current = false;

      if (highlightedMarkerRef.current !== marker) {
        marker.setIcon(createMarkerIcon(pnd.device_type, marker.__deviceColor, false, marker.__isNoTransaction));
      }
      pndInfoWindowRef.current?.close();
    });

    marker.addListener('click', () => { highlightMarker(pnd); onPndClick(pnd); });
    markersRef.current.push(marker);
  }, [createMarkerIcon, highlightMarker, onPndClick, getMarkerColor]);

  const filterPndsForCityStreet = useCallback((allStreetPnds, streetCd) => {
    if (!pndStatusFilters || pndStatusFilters.length === 0) return allStreetPnds;
    if (pndStatusFilters.includes('all')) return allStreetPnds;

    const fmd = filteredMapDataRef.current;
    if (!fmd || fmd.type !== 'city') return [];

    const filteredStreet = fmd.streets.find(s => s.street_cd === streetCd);
    if (!filteredStreet) return [];

    const isBelowThresholdFilter = pndStatusFilters.length === 1 && pndStatusFilters[0] === 'below_threshold';

    const backendPnds = (filteredStreet.pnds || []).filter(p => {
      if (!isBelowThresholdFilter) return true;
      const tc = parseFloat(String(p.ticket_count ?? '0').replace(/,/g, ''));
      return !isNaN(tc) && tc > 0;
    });

    const allowedPndNums = new Set(backendPnds.map(p => String(p.pnd)));
    return allStreetPnds.filter(p => allowedPndNums.has(String(p.pnd_number ?? p.pnd)));
  }, [pndStatusFilters]);

  const filterPndsForStreet = useCallback((allPnds) => {
    if (!pndStatusFilters || pndStatusFilters.length === 0) return allPnds;
    if (pndStatusFilters.includes('all')) return allPnds;

    const fmd = filteredMapDataRef.current;
    if (!fmd || fmd.type !== 'street') return [];

    const isBelowThresholdFilter = pndStatusFilters.length === 1 && pndStatusFilters[0] === 'below_threshold';

    const backendPnds = (fmd.pnds || []).filter(p => {
      if (!isBelowThresholdFilter) return true;
      const tc = parseFloat(String(p.ticket_count ?? '0').replace(/,/g, ''));
      return !isNaN(tc) && tc > 0;
    });

    const allowedPndNums = new Set(backendPnds.map(p => String(p.pnd)));
    return allPnds.filter(p => allowedPndNums.has(String(p.pnd_number ?? p.pnd)));
  }, [pndStatusFilters]);


  
  

 // Polygon border pe halka glow / shadow (Parking page jaisa)
const addPolygonGlow = useCallback((map, paths, zIndexBase = 1) => {
  if (!map || !window.google) return [];
  const glowLayers = [
    { weight: 20, opacity: 0.06 },
    { weight: 16, opacity: 0.1 },
    { weight: 12, opacity: 0.16 },
    { weight: 8, opacity: 0.24 },
    { weight: 5, opacity: 0.34 },
  ];
  return glowLayers.map(
    (layer, i) =>
      new window.google.maps.Polygon({
        paths,
        strokeColor: '#000000',
        strokeOpacity: layer.opacity,
        strokeWeight: layer.weight,
        fillOpacity: 0,
        map,
        zIndex: zIndexBase - (glowLayers.length - i),
        clickable: false,
      })
  );
}, []);

// API coordinates → valid {lat,lng}[] (null/empty/invalid → [])
const parseApiCoords = (coords) => {
  if (!Array.isArray(coords) || !coords.length) return [];
  return coords
    .map((c) => {
      const lat = parseFloat(c?.lat ?? c?.latitude);
      const lng = parseFloat(c?.lng ?? c?.longitude);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { lat, lng };
    })
    .filter(Boolean);
};

const drawAllCities = useCallback(() => {
  const map = mapInstanceRef.current;
  if (!map || !window.google) return;
  setMapLoading(true);
  clearAll();

  const bounds = new window.google.maps.LatLngBounds();
  let hasBounds = false;

  // Country boundary polygon from API (`data.coordinates`)
  const countryPath = parseApiCoords(countryInfo?.coordinates);
  if (countryPath.length >= 3) {
    polygonsRef.current.push(...addPolygonGlow(map, countryPath, 0));
    polygonsRef.current.push(
      new window.google.maps.Polygon({
        paths: countryPath,
        strokeColor: '#3B82F6',
        strokeOpacity: 0.9,
        strokeWeight: 2.5,
        fillColor: '#3B82F6',
        fillOpacity: 0.12,
        map,
        zIndex: 0,
        clickable: false,
      })
    );
    countryPath.forEach((p) => {
      bounds.extend(p);
      hasBounds = true;
    });
  }

  (citiesData || []).forEach((city, idx) => {
    const color = STREET_COLORS[idx % STREET_COLORS.length];

    const allPnds = (city.streets || []).flatMap((s) => s.pnds || []);
    const valid = allPnds.filter(
      (p) =>
        p.latitude &&
        p.longitude &&
        !isNaN(parseFloat(p.latitude)) &&
        !isNaN(parseFloat(p.longitude))
    );

    // ── City polygon: API coordinates prefer, warna PND-based bbox default ──
    const apiCityPath = parseApiCoords(city?.coordinates);
    let cityPath =
      apiCityPath.length >= 3
        ? apiCityPath
        : (() => {
            const rect = buildBboxRect(valid, 300);
            if (!rect || rect.length < 3) return [];
            return rect.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
          })();
    let centLat = null;
    let centLng = null;

    if (cityPath.length >= 3) {
      cityPath.forEach((p) => {
        bounds.extend(p);
        hasBounds = true;
      });
      polygonsRef.current.push(...addPolygonGlow(map, cityPath, 1));
      const cityPoly = new window.google.maps.Polygon({
        paths: cityPath,
        strokeColor: color,
        strokeOpacity: 0.95,
        strokeWeight: 2.5,
        fillColor: color,
        fillOpacity: 0.15,
        map,
        zIndex: 1,
        clickable: true,
      });
      cityPoly.addListener('click', () => onCityClick?.(city));
      polygonsRef.current.push(cityPoly);

      centLat = cityPath.reduce((s, p) => s + p.lat, 0) / cityPath.length;
      centLng = cityPath.reduce((s, p) => s + p.lng, 0) / cityPath.length;
    }

    // Label centroid: API path → warna PNDs average
    if (centLat === null || centLng === null) {
      if (!valid.length) return;
      centLat = valid.reduce((s, p) => s + parseFloat(p.latitude), 0) / valid.length;
      centLng = valid.reduce((s, p) => s + parseFloat(p.longitude), 0) / valid.length;
    }

    valid.forEach((p) => {
      bounds.extend({
        lat: parseFloat(p.latitude),
        lng: parseFloat(p.longitude),
      });
      hasBounds = true;
    });

    const name = (city.city_name || 'City').slice(0, 14);
    const pndCnt = city.total_pnds || valid.length;

    const labelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="36" viewBox="0 0 90 36">
      <rect x="0" y="0" width="90" height="36" rx="8" fill="${color}" opacity="0.9"/>
      <text x="45" y="13" font-family="sans-serif" font-size="9" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${name}</text>
      <text x="45" y="26" font-family="sans-serif" font-size="8" fill="white" opacity="0.9" text-anchor="middle" dominant-baseline="central">${pndCnt} PNDs</text>
    </svg>`;

    const labelMarker = new window.google.maps.Marker({
      position: { lat: centLat, lng: centLng },
      map,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(labelSvg)}`,
        scaledSize: new window.google.maps.Size(90, 36),
        anchor: new window.google.maps.Point(45, 18),
      },
      title: city.city_name,
      zIndex: 50,
    });

    labelMarker.addListener('click', () => onCityClick?.(city));
    markersRef.current.push(labelMarker);
  });

  if (hasBounds && !bounds.isEmpty()) {
    map.fitBounds(bounds, 48);
  }
  setTimeout(() => setMapLoading(false), 400);
}, [citiesData, countryInfo, clearAll, onCityClick, addPolygonGlow, buildBboxRect]);

 const drawCityView = useCallback(
  (city) => {
    const map = mapInstanceRef.current;
    if (!map || !window.google || !city) return;
    setMapLoading(true);
    clearAll();

    const allPnds = (city.streets || []).flatMap((s) => s.pnds || []);
    const validAll = allPnds.filter(
      (p) =>
        p.latitude &&
        p.longitude &&
        !isNaN(parseFloat(p.latitude)) &&
        !isNaN(parseFloat(p.longitude))
    );
    if (!validAll.length) {
      setMapLoading(false);
      return;
    }

    const cityBounds = new window.google.maps.LatLngBounds();
    validAll.forEach((p) =>
      cityBounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
    );

    // ── City backdrop: API coordinates prefer, warna PND-based bbox default ──
    const apiCityPath = parseApiCoords(city?.coordinates);
    const cityPath =
      apiCityPath.length >= 3
        ? apiCityPath
        : (() => {
            const rect = buildBboxRect(validAll, 300);
            if (!rect || rect.length < 3) return [];
            return rect.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
          })();
    if (cityPath.length >= 3) {
      cityPath.forEach((p) => cityBounds.extend(p));
      polygonsRef.current.push(...addPolygonGlow(map, cityPath));
      polygonsRef.current.push(
        new window.google.maps.Polygon({
          paths: cityPath,
          strokeColor: '#6366F1',
          strokeOpacity: 0.35,
          strokeWeight: 1.5,
          fillColor: '#6366F1',
          fillOpacity: 0.03,
          map,
          zIndex: 1,
          clickable: false,
        })
      );
    }

    (city.streets || []).forEach((street, si) => {
      const allStreetPnds = (street.pnds || []).filter(
        (p) =>
          p.latitude &&
          p.longitude &&
          !isNaN(parseFloat(p.latitude)) &&
          !isNaN(parseFloat(p.longitude))
      );
      let sPnds = filterPndsForCityStreet(allStreetPnds, street.street_cd);

      if (selectedPnd) {
        const selKey = getPndKey(selectedPnd);
        const alreadyThere = sPnds.some((p) => getPndKey(p) === selKey);
        if (!alreadyThere) {
          const match = allStreetPnds.find((p) => getPndKey(p) === selKey);
          if (match) sPnds = [...sPnds, match];
        }
      }

      if (!sPnds.length) return;
      const sColor = STREET_COLORS[si % STREET_COLORS.length];

      // ── Street polygon: API coordinates prefer, warna createRoadPolygon default ──
      const apiStreetPath = parseApiCoords(street?.coordinates);
      const streetPath =
        apiStreetPath.length >= 3
          ? apiStreetPath
          : (() => {
              const pts = sPnds.map((p) => ({
                lat: parseFloat(p.latitude),
                lng: parseFloat(p.longitude),
              }));
              if (pts.length < 2) return [];
              return createRoadPolygon(pts, 0.00018);
            })();
      if (streetPath.length >= 3) {
        streetPath.forEach((p) => cityBounds.extend(p));
        polygonsRef.current.push(...addPolygonGlow(map, streetPath, 2));
        const streetPolygon = new window.google.maps.Polygon({
          paths: streetPath,
          strokeColor: sColor,
          strokeOpacity: 0.95,
          strokeWeight: 2,
          fillColor: sColor,
          fillOpacity: 0.22,
          map,
          zIndex: 2,
          clickable: true,
        });

        const openStreetTooltip = (e) => {
          if (!streetInfoWindowRef.current) {
            streetInfoWindowRef.current = new window.google.maps.InfoWindow({
              disableAutoPan: true,
            });
          }
          streetInfoWindowRef.current.setContent(
            `<div style="font-size:12px;font-weight:600;padding:2px 4px;white-space:nowrap;">${street.street_name || 'Street'}</div>`
          );
          streetInfoWindowRef.current.setPosition(e.latLng);
          streetInfoWindowRef.current.open({ map, shouldFocus: false });
        };

        streetPolygon.addListener('mouseover', (e) => {
          streetPolygon.setOptions({ fillOpacity: 0.45, strokeWeight: 3 });
          if (hoveredMarkerActiveRef.current) return;
          openStreetTooltip(e);
        });
        streetPolygon.addListener('mousemove', (e) => {
          if (hoveredMarkerActiveRef.current) {
            streetInfoWindowRef.current?.close();
            return;
          }
          openStreetTooltip(e);
        });
        streetPolygon.addListener('mouseout', () => {
          streetPolygon.setOptions({ fillOpacity: 0.22, strokeWeight: 2 });
          streetInfoWindowRef.current?.close();
        });
        streetPolygon.addListener('click', () => {
          onStreetClick?.(street);
        });

        polygonsRef.current.push(streetPolygon);
      }

      sPnds.forEach((pnd, pi) => addPndMarker(pnd, pi, map));
    });

    const sw = cityBounds.getSouthWest();
    const ne = cityBounds.getNorthEast();
    const pad = 0.005;
    smoothFlyTo(
      map,
      new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(sw.lat() - pad, sw.lng() - pad),
        new window.google.maps.LatLng(ne.lat() + pad, ne.lng() + pad)
      ),
      19,
      () => {
        setMapLoading(false);
        if (selectedPnd) highlightMarker(selectedPnd);
      }
    );
  },
  [
    clearAll,
    addPolygonGlow,
    addPndMarker,
    smoothFlyTo,
    onStreetClick,
    filterPndsForCityStreet,
    selectedPnd,
    highlightMarker,
    buildBboxRect,
    createRoadPolygon,
  ]
);

 const drawStreetView = useCallback(
  (street) => {
    const map = mapInstanceRef.current;
    if (!map || !window.google || !street) return;
    setMapLoading(true);
    clearAll();

    const valid = (street.pnds || []).filter(
      (p) =>
        p.latitude &&
        p.longitude &&
        !isNaN(parseFloat(p.latitude)) &&
        !isNaN(parseFloat(p.longitude))
    );
    let filteredValid = filterPndsForStreet(valid);

    if (selectedPnd) {
      const selKey = getPndKey(selectedPnd);
      const alreadyThere = filteredValid.some((p) => getPndKey(p) === selKey);
      if (!alreadyThere) {
        const match = valid.find((p) => getPndKey(p) === selKey);
        if (match) filteredValid = [...filteredValid, match];
      }
    }

    if (!filteredValid.length) {
      setMapLoading(false);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    filteredValid.forEach((p) =>
      bounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
    );

    // ── Street polygon: API coordinates prefer, warna createRoadPolygon default ──
    const apiStreetPath = parseApiCoords(street?.coordinates);
    const streetPath =
      apiStreetPath.length >= 3
        ? apiStreetPath
        : (() => {
            const pts = filteredValid.map((p) => ({
              lat: parseFloat(p.latitude),
              lng: parseFloat(p.longitude),
            }));
            if (pts.length < 2) return [];
            return createRoadPolygon(pts, 0.00018);
          })();
    if (streetPath.length >= 3) {
      streetPath.forEach((p) => bounds.extend(p));
      polygonsRef.current.push(...addPolygonGlow(map, streetPath));
      polygonsRef.current.push(
        new window.google.maps.Polygon({
          paths: streetPath,
          strokeColor: '#6366F1',
          strokeOpacity: 0.9,
          strokeWeight: 2.5,
          fillColor: '#6366F1',
          fillOpacity: 0.18,
          map,
          zIndex: 1,
          clickable: false,
        })
      );
    }

    filteredValid.forEach((pnd, i) => addPndMarker(pnd, i, map));

    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const pad = 0.004;
    smoothFlyTo(
      map,
      new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(sw.lat() - pad, sw.lng() - pad),
        new window.google.maps.LatLng(ne.lat() + pad, ne.lng() + pad)
      ),
      18,
      () => {
        setMapLoading(false);
        if (selectedPnd) highlightMarker(selectedPnd);
      }
    );
  },
  [
    clearAll,
    addPolygonGlow,
    addPndMarker,
    smoothFlyTo,
    filterPndsForStreet,
    selectedPnd,
    highlightMarker,
    createRoadPolygon,
  ]
);

  const drawSinglePnd = useCallback((pnd) => {
    const map = mapInstanceRef.current;
    if (!map || !window.google || !pnd?.latitude || !pnd?.longitude) return;
    setMapLoading(true);
    clearAll();
    const lat = parseFloat(pnd.latitude); const lng = parseFloat(pnd.longitude);
    if (isNaN(lat) || isNaN(lng)) { setMapLoading(false); return; }
    const dc = getDeviceColor(pnd.device_type);
    markersRef.current.push(new window.google.maps.Marker({
      position: { lat, lng }, map,
      icon: createMarkerIcon(pnd.device_type, dc, true),
      animation: window.google.maps.Animation.DROP, zIndex: 200,
    }));
    smoothFlyTo(
      map,
      new window.google.maps.LatLngBounds({ lat, lng }, { lat, lng }),
      19,
      () => setMapLoading(false)
    );
  }, [clearAll, createMarkerIcon, smoothFlyTo]);

  useEffect(() => {
    if (mapReadyRef.current) return;
    const check = () => {
      if (!window.google || !mapInstanceRef.current) { setTimeout(check, 200); return; }
      mapReadyRef.current = true;
      drawAllCities();
    };
    check();
  }, [citiesData, drawAllCities]);

  useEffect(() => {
    if (!mapReadyRef.current) return;
    if (!selectedCity && !selectedStreet && !selectedPnd) drawAllCities();
    else if (selectedCity && !selectedStreet && !selectedPnd) drawCityView(selectedCity);
  }, [selectedCity, selectedStreet, selectedPnd, drawAllCities, drawCityView]);

  const prevStreetRef = useRef(null);

  useEffect(() => {
    if (!mapReadyRef.current || !selectedStreet) return;
    if (prevStreetRef.current === selectedStreet.street_cd) return;
    prevStreetRef.current = selectedStreet.street_cd;
    drawStreetView(selectedStreet);
  }, [selectedStreet, drawStreetView]);

  useEffect(() => {
    if (!mapReadyRef.current || !selectedCity || selectedStreet || selectedPnd) return;
    if (pndStatusFilters.length > 0 && !pndStatusFilters.includes('all') && filteredMapLoading) return;
    drawCityView(selectedCity);
  }, [pndStatusFilters, filteredMapData, filteredMapLoading, drawCityView, selectedCity, selectedStreet, selectedPnd]);

  useEffect(() => {
    if (!mapReadyRef.current || !selectedStreet) return;
    if (pndStatusFilters.length > 0 && !pndStatusFilters.includes('all') && filteredMapLoading) return;
    drawStreetView(selectedStreet);
  }, [pndStatusFilters, filteredMapData, filteredMapLoading, drawStreetView, selectedStreet]);

  useEffect(() => {
    if (!mapReadyRef.current) return;
    if (selectedCity && !selectedStreet && !selectedPnd) drawCityView(selectedCity);
    else if (selectedStreet) drawStreetView(selectedStreet);
  }, [streetsOnlyMode]);

  useEffect(() => {
    if (!mapReadyRef.current || !selectedPnd) return;
    const hasMarkers = Object.keys(markerMapRef.current).length > 0;
    if (hasMarkers) {
      setMapLoading(true);
      highlightMarker(selectedPnd);
      const t = setTimeout(() => setMapLoading(false), 600);
      return () => clearTimeout(t);
    } else {
      drawSinglePnd(selectedPnd);
    }
  }, [selectedPnd, highlightMarker, drawSinglePnd]);

  useEffect(() => { return () => { if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current); }; }, []);

  useEffect(() => {
    if (!mapReadyRef.current || !window.google) return;
    Object.values(markerMapRef.current).forEach((marker) => {
      const pndNum     = marker.__pndNumber || '';
      const isBelowThr = belowThresholdPnds.has(pndNum);
      const isNoTran   = noTransactionPnds.has(pndNum);
      const isInactive = marker.__isInactive;

      const newColor = getMarkerColor(marker.__pndDeviceType, isBelowThr, isNoTran, isInactive);

      marker.__deviceColor     = newColor;
      marker.__isNoTransaction = isNoTran;
      marker.setIcon(createMarkerIcon(marker.__pndDeviceType, newColor, false, isNoTran));
    });
  }, [belowThresholdPnds, noTransactionPnds, createMarkerIcon, getMarkerColor]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      <div ref={mapRef} className="w-full h-full" />
      {selectedCity && (
        <BottomLeftLegend isDarkMode={isDarkMode} sidebarOpen={sidebarOpen} />
      )}
      <MapShimmer
        visible={mapLoading}
        isDarkMode={isDarkMode}
        mode={shimmerMode}
      />
    </div>
  );
};

// ============================================
// City & Street Sidebar
// ============================================
const StatCardShimmer = () => (
  <div className="rounded-lg px-2 py-1.5 bg-muted/40 border border-border/60 animate-pulse">
    <div className="h-2 w-3/5 rounded bg-muted-foreground/20 mb-1.5" />
    <div className="h-4 w-2/5 rounded bg-muted-foreground/25" />
  </div>
);

const CityStreetSidebar = ({
  citiesData = [], countryInfo = null,
  allCitiesData, fullCountryInfo,
  selectedCity, selectedStreet, selectedPnd,
  onSelectCity, onSelectStreet, onSelectPnd, onSelectCountry,
  activeFilters, onFiltersChange,
  onToggle,
  baysPerPnd: baysPerPndProp, onBaysPerPndChange,
  monthGroups = [], yearOptions = [], optLoading = false, optError = null,
}) => {
  // Stat cards (Projects / Streets / Total PNDs / Active / Inactive) always show
  // the TOTAL count regardless of the logged-in user's assigned permissions.
  // The tree below (city -> street -> pnd list) still uses the permission-filtered
  // `citiesData` / `countryInfo` so navigation stays scoped to what's assigned.
  const statsCitiesData = allCitiesData ?? citiesData;
  const statsCountryInfo = fullCountryInfo !== undefined ? fullCountryInfo : countryInfo;
  const [expandedCountry, setExpandedCountry] = useState(true);
  const toggleCountry = () => {
    setExpandedCountry(p => !p);
    onSelectCountry?.();
  };
  const [expandedCities, setExpandedCities]   = useState(new Set());
  const [expandedStreets, setExpandedStreets] = useState(new Set());
  const [searchQuery, setSearchQuery]         = useState('');
  const [showFilters, setShowFilters]         = useState(false);
  const [filterPanelTop, setFilterPanelTop]   = useState(12);
  const filterBtnRef = useRef(null);

  const hasActiveFilter = !!(activeFilters?.month && activeFilters?.year);

  useEffect(() => {
    if (showFilters && filterBtnRef.current) {
      const rect = filterBtnRef.current.getBoundingClientRect();
      const panelHeight = 390;
      const maxTop = window.innerHeight - panelHeight - 12;
      setFilterPanelTop(Math.min(Math.max(rect.top, 12), maxTop));
    }
  }, [showFilters]);

  // NOTE: these drive the "Projects / Streets / Total PNDs / Active / Inactive" stat
  // cards at the top of the sidebar. They intentionally read from statsCitiesData /
  // statsCountryInfo (unfiltered) so they always show the grand total, regardless of
  // which cities/streets the logged-in user has permission to see. The tree below
  // (citiesListContent) still uses the permission-filtered `citiesData`.
  const totalCities  = statsCountryInfo?.total_cities  ?? statsCitiesData.length;
  const totalStreets = statsCountryInfo?.total_streets ?? statsCitiesData.reduce((s, c) => s + (c.total_streets || 0), 0);
  const totalPnds    = statsCountryInfo?.total_pnds    ?? statsCitiesData.reduce((s, c) => s + (c.total_pnds    || 0), 0);

  const sidebarMonth = activeFilters?.month || MONTH_NAMES[new Date().getMonth()];
  const sidebarYear  = activeFilters?.year  || String(new Date().getFullYear());
  const { countryParkingBaysCount: totalParkingBays, countryParkingBaysLoading: parkingBaysCardLoading } = useCountryParkingBaysCount(sidebarMonth, sidebarYear);

  const [localBaysPerPnd, setLocalBaysPerPnd] = useState(25);
  const baysPerPnd = baysPerPndProp !== undefined ? baysPerPndProp : localBaysPerPnd;
  const setBaysPerPnd = onBaysPerPndChange || setLocalBaysPerPnd;

  const activePnds   = statsCitiesData.reduce((s, c) =>
    s + (c.streets || []).reduce((ss, st) =>
      ss + (st.pnds || []).filter(p => p.device_status === 'ACTIVE').length, 0), 0);

  const inactivePnds = statsCitiesData.reduce((s, c) =>
    s + (c.streets || []).reduce((ss, st) =>
      ss + (st.pnds || []).filter(p => p.device_status === 'INACTIVE').length, 0), 0);

  const pndCoverage = useMemo(() => {
    const bays  = parseCount(totalParkingBays);
    const ratio = Number(baysPerPnd) > 0 ? Number(baysPerPnd) : 25;
    if (bays === null) return { requiredPnds: null, diff: null };
    const requiredPnds = Math.ceil(bays / ratio);
    const diff = activePnds - requiredPnds;
    return { requiredPnds, diff };
  }, [totalParkingBays, baysPerPnd, activePnds]);

  const filteredCities = citiesData.filter(city => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      city.city_name?.toLowerCase().includes(q) ||
      city.streets?.some(s => s.street_name?.toLowerCase().includes(q) ||
        s.pnds?.some(p =>
          p.device_type?.toLowerCase().includes(q) ||
          p.pnd_number?.toString().toLowerCase().includes(q)
        )
      )
    );
  });

  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      setExpandedCities(new Set());
      setExpandedStreets(new Set());
      return;
    }

    const newExpandedCities = new Set();

    citiesData.forEach(city => {
      const hasStreetNameMatch = city.streets?.some(s =>
        s.street_name?.toLowerCase().includes(q)
      );

      if (hasStreetNameMatch) {
        newExpandedCities.add(city.city_cd);
      }
    });

    setExpandedCities(newExpandedCities);
    setExpandedStreets(new Set());
  }, [searchQuery, citiesData]);

  const toggleCity   = (cd) => setExpandedCities(p => { const n = new Set(p); n.has(cd) ? n.delete(cd) : n.add(cd); return n; });
  const toggleStreet = (cd) => setExpandedStreets(p => { const n = new Set(p); n.has(cd) ? n.delete(cd) : n.add(cd); return n; });

  const handleCityClick = (city) => { 
    onSelectCity(city); 
    onSelectStreet(null); 
    onSelectPnd(null); 
  };
  const handleStreetClick = (street, city) => { onSelectCity(city); onSelectStreet(street); onSelectPnd(null); if (!expandedStreets.has(street.street_cd)) toggleStreet(street.street_cd); };
  const handlePndClick    = (pnd)          => { onSelectPnd(pnd); };

  const highlight = (text, query) => {
    if (!query || !text) return String(text);
    const q = query.trim().toLowerCase();
    const str = String(text);
    const idx = str.toLowerCase().indexOf(q);
    if (idx === -1) return str;
    return (<span>{str.slice(0, idx)}<mark style={{ background: '#6366F133', color: 'inherit', borderRadius: '2px', padding: '0 1px' }}>{str.slice(idx, idx + q.length)}</mark>{str.slice(idx + q.length)}</span>);
  };

  const citiesListContent = (
    <>
      {filteredCities.length === 0 && searchQuery.trim() ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
          <Search className="w-6 h-6 opacity-30" />
          <p className="text-xs">No results for "{searchQuery}"</p>
        </div>
      ) : (
        filteredCities.map((city, ci) => {
          const expanded = expandedCities.has(city.city_cd);
          const selected = selectedCity?.city_cd === city.city_cd;
          const q        = searchQuery.trim().toLowerCase();
          const visibleStreets = q
            ? city.city_name?.toLowerCase().includes(q)
              ? (city.streets || [])
              : (city.streets || []).filter(s =>
                  s.street_name?.toLowerCase().includes(q) ||
                  s.pnds?.some(p =>
                    p.device_type?.toLowerCase().includes(q) ||
                    p.pnd_number?.toString().toLowerCase().includes(q)
                  )
                )
            : city.streets || [];

          return (
            <div key={`city_${city.city_cd ?? ci}`}>
              <button
                onClick={() => handleCityClick(city)}
                className={`w-full text-left rounded-lg px-3 py-2.5 transition-all ${selected ? 'bg-sidebar-accent border border-primary/30' : 'hover:bg-sidebar-accent/50 border border-transparent'}`}
              >
                <div className="flex items-center gap-2">
                  <button onClick={e => { e.stopPropagation(); toggleCity(city.city_cd); }} className="flex-shrink-0 text-muted-foreground hover:text-foreground">
                    {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                  <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-sidebar-foreground truncate">{highlight(city.city_name, searchQuery)}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] font-mono text-muted-foreground">Code: {city.city_cd}</span>
                      <span className="text-[10px] text-muted-foreground">•</span>
                      <span className="text-[10px] text-muted-foreground">{city.total_streets} streets</span>
                    </div>
                  </div>
                  <span className="text-[10px] font-mono font-semibold text-primary flex-shrink-0">{city.total_pnds} PNDs</span>
                  <AddStreetIcon city={city} onAdd={(c, s) => console.log('new street:', c, s)} />
                </div>
              </button>

              <AnimatePresence>
                {expanded && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                    <div className="ml-4 pl-3 border-l border-sidebar-border space-y-0.5 py-1">
                      {visibleStreets.map((street, si) => {
                        const sExpanded = expandedStreets.has(street.street_cd);
                        const sSelected = selectedStreet?.street_cd === street.street_cd;
                        const bnaCount  = street.pnds?.filter(p => p.device_type?.includes('BNA')).length || 0;
                        const txCount   = street.pnds?.filter(p => p.device_type?.includes('TX')).length  || 0;
                        const actCount  = street.pnds?.filter(p => p.device_type).length || 0;
                        const visiblePnds = q
                          ? (street.pnds || []).filter(p => street.street_name?.toLowerCase().includes(q) || city.city_name?.toLowerCase().includes(q) || p.device_type?.toLowerCase().includes(q) || p.pnd_number?.toString().toLowerCase().includes(q))
                          : street.pnds || [];

                        return (
                          <div key={`street_${street.street_cd ?? si}`}>
                            <button
                              onClick={() => handleStreetClick(street, city)}
                              className={`w-full text-left rounded-md px-2.5 py-1.5 transition-all ${sSelected ? 'bg-primary/10 border border-primary/20' : 'hover:bg-sidebar-accent/40'}`}
                            >
                              <div className="flex items-center gap-2">
                                <button onClick={e => { e.stopPropagation(); toggleStreet(street.street_cd); }} className="flex-shrink-0 text-muted-foreground">
                                  {sExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                </button>
                                <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-[11px] text-sidebar-foreground truncate">{highlight(street.street_name, searchQuery)}</p>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    {bnaCount > 0 && <span className="text-[8px] font-semibold px-1 py-0.5 rounded-full" style={{ background: '#8B5CF622', color: '#8B5CF6' }}>BNA {bnaCount}</span>}
                                    {txCount  > 0 && <span className="text-[8px] font-semibold px-1 py-0.5 rounded-full" style={{ background: '#F59E0B22', color: '#F59E0B' }}>TX {txCount}</span>}
                                  </div>
                                </div>
                                <span className="text-[9px] font-mono text-muted-foreground flex-shrink-0">{actCount}/{street.total_pnds}</span>
                                <AddPndIcon street={street} onAdd={(s, p) => console.log('new pnd:', s, p)} />
                              </div>
                            </button>

                            <AnimatePresence>
                              {sExpanded && (
                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                                  <div className="ml-6 pl-3 border-l border-sidebar-border space-y-0.5 py-1">
                                    {visiblePnds.map((pnd, pi) => {
                                      const pSelected = selectedPnd?.pnd_number != null && selectedPnd.pnd_number === pnd.pnd_number;
                                      const dColor    = getDeviceColor(pnd.device_type);
                                      const dType     = getDeviceTypeName(pnd.device_type);
                                      const hasCoords = pnd.latitude && pnd.longitude;
                                      return (
                                        <button
                                          key={`pnd_${pnd.pnd_number ?? pnd.device_type ?? ''}_${pi}`}
                                          onClick={() => handlePndClick(pnd)}
                                          className={`w-full text-left rounded-md px-2 py-1.5 transition-all flex items-center gap-2 ${pSelected ? 'bg-primary/10 border border-primary/20' : 'hover:bg-sidebar-accent/40'}`}
                                        >
                                          <CircleDot className="w-2.5 h-2.5 flex-shrink-0" style={{ color: hasCoords ? (pnd.device_type ? '#22C55E' : '#EF4444') : '#9CA3AF' }} />
                                          <span className="text-[7px] font-bold px-1 py-0.5 rounded" style={{ background: `${dColor}22`, color: dColor }}>{dType}</span>
                                          <div className="flex-1 min-w-0">
                                            {pnd.pnd_number && <p className="text-[10px] font-mono font-semibold text-sidebar-foreground truncate">#{highlight(pnd.pnd_number.toString(), searchQuery)}</p>}
                                            <p className="text-[9px] text-muted-foreground truncate">{highlight(pnd.device_type, searchQuery)}</p>
                                          </div>
                                          {hasCoords && <Navigation className="w-2.5 h-2.5 text-primary flex-shrink-0" />}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })
      )}
    </>
  );

  return (
    <div className="h-full flex flex-col bg-sidebar overflow-hidden border-r border-sidebar-border">
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-sidebar-foreground tracking-tight">On Street PND Explorer</h1>
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Street-wise PNDs</p>
          </div>
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-all flex-shrink-0"
            title="Close sidebar"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-1.5">
          {[
            { label: 'Projects',     value: totalCities  },
            { label: 'Streets',    value: totalStreets },
            { label: 'Total PNDs', value: totalPnds    },
            { label: 'Active PNDs',   value: activePnds,   color: 'emerald' },
            { label: 'Inactive PNDs', value: inactivePnds, color: 'red'     },
            { label: 'Parking Bays',  value: totalParkingBays, color: 'amber', loading: parkingBaysCardLoading },
          ].map(({ label, value, color, loading }) => (
            loading ? (
              <StatCardShimmer key={label} />
            ) : (
              <div key={label} className={`rounded-lg px-2 py-1.5 ${
                color === 'emerald' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                color === 'red'     ? 'bg-red-500/10 border border-red-500/30' :
                color === 'amber'   ? 'bg-amber-500/10 border border-amber-500/30' :
                'bg-card border border-border'
              }`}>
                <p className={`text-[9px] uppercase tracking-wider leading-tight truncate ${
                  color === 'emerald' ? 'text-emerald-500' :
                  color === 'red'     ? 'text-red-500' :
                  color === 'amber'   ? 'text-amber-500' :
                  'text-muted-foreground'
                }`}>{label}</p>
                <p className={`text-base font-bold leading-tight ${
                  color === 'emerald' ? 'text-emerald-500' :
                  color === 'red'     ? 'text-red-500' :
                  color === 'amber'   ? 'text-amber-500' :
                  'text-foreground'
                }`}>{formatCount(value)}</p>
              </div>
            )
          ))}
        </div>

        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
          {parkingBaysCardLoading ? (
            <>
              <StatCardShimmer />
              <StatCardShimmer />
            </>
          ) : (
            <>
              <div className="relative rounded-lg px-2 py-1.5 bg-secondary border border-border">
                <div className="flex items-center justify-between gap-1">
                  <p className="text-[9px] uppercase tracking-wider text-muted-foreground leading-tight truncate">Required PNDs</p>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <input
                      type="number"
                      min={1}
                      value={baysPerPnd}
                      onChange={e => setBaysPerPnd(e.target.value === '' ? '' : Number(e.target.value))}
                      title="Bays per PND"
                      className="w-11 px-1 py-0 text-[10px] text-right rounded bg-background border border-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
                <p className="text-base font-bold text-foreground leading-tight">
                  {pndCoverage.requiredPnds === null ? '—' : formatCount(pndCoverage.requiredPnds)}
                </p>
              </div>
              <div className={`rounded-lg px-2 py-1.5 border ${
                pndCoverage.diff === null ? 'bg-secondary border-border' :
                pndCoverage.diff > 0 ? 'bg-emerald-500/10 border-emerald-500/30' :
                pndCoverage.diff < 0 ? 'bg-red-500/10 border-red-500/30' :
                'bg-secondary border-border'
              }`}>
                <p className={`text-[9px] uppercase tracking-wider leading-tight truncate ${
                  pndCoverage.diff === null ? 'text-muted-foreground' :
                  pndCoverage.diff > 0 ? 'text-emerald-500' :
                  pndCoverage.diff < 0 ? 'text-red-500' :
                  'text-muted-foreground'
                }`}>PND Status</p>
                <p className={`text-base font-bold leading-tight truncate ${
                  pndCoverage.diff === null ? 'text-foreground' :
                  pndCoverage.diff > 0 ? 'text-emerald-500' :
                  pndCoverage.diff < 0 ? 'text-red-500' :
                  'text-foreground'
                }`}>
                  {pndCoverage.diff === null ? '—' :
                    pndCoverage.diff > 0 ? `+${formatCount(pndCoverage.diff)} Extra` :
                    pndCoverage.diff < 0 ? `-${formatCount(Math.abs(pndCoverage.diff))} Shortage` :
                    'Balanced'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="px-3 py-2.5 border-b border-sidebar-border">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search project, street, device, PND#..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <button
            ref={filterBtnRef}
            onClick={() => setShowFilters(v => !v)}
            className={`relative flex-shrink-0 p-1.5 rounded-lg border transition-all ${hasActiveFilter ? 'bg-primary/15 border-primary/40 text-primary' : showFilters ? 'bg-secondary border-border text-foreground' : 'bg-background border-border text-muted-foreground hover:text-foreground hover:bg-secondary'}`}
            title=" Date Filter"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {hasActiveFilter && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-primary" />}
          </button>
        </div>

        {hasActiveFilter && (
          <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-lg bg-primary/8 border border-primary/15">
            <Calendar className="w-3 h-3 text-primary flex-shrink-0" />
            <span className="text-[9px] text-primary font-semibold truncate">
              {activeFilters.month} {activeFilters.year}
              {activeFilters.threshold && ` • Threshold: ${activeFilters.threshold}`}
            </span>
            <button onClick={() => onFiltersChange({})} className="ml-auto text-muted-foreground hover:text-foreground flex-shrink-0">
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}

        {searchQuery.trim() && (
          <p className="text-[10px] text-muted-foreground mt-1.5 px-0.5">
            {filteredCities.length === 0 ? 'No results found' : `${filteredCities.length} cit${filteredCities.length === 1 ? 'y' : 'ies'} matched`}
          </p>
        )}
        <AddProjectButton onAdd={(newCity) => console.log('new project:', newCity)} />
      </div>

      <AnimatePresence>
        {showFilters && (
          <DateFilterPanel
            topPos={filterPanelTop}
            activeMonth={activeFilters?.month}
            activeYear={activeFilters?.year}
            activeThreshold={activeFilters?.threshold}
            initialGroup={activeFilters?.type || ''}
            initialMonth={activeFilters?.panelMonth || activeFilters?.displayMonth || activeFilters?.month || ''}
            initialDay={activeFilters?.month ? { value: activeFilters.month, label: activeFilters.month } : null}
            onApply={f => { onFiltersChange(f); setShowFilters(false); }}
            onReset={() => { onFiltersChange({}); setShowFilters(false); }}
            onClose={() => setShowFilters(false)}
            monthGroups={monthGroups}
            yearOptions={yearOptions}
            optLoading={optLoading}
            optError={optError}
          />
        )}
      </AnimatePresence>

      <div className="flex-1 overflow-y-auto cb-scroll p-2 space-y-1">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1.5 font-semibold">Country, Projects &amp; Streets</p>

        {countryInfo ? (
          <div>
            <button
              onClick={toggleCountry}
              className="w-full text-left rounded-lg px-3 py-2.5 transition-all bg-primary/5 hover:bg-primary/10 border border-primary/15"
            >
              <div className="flex items-center gap-2">
                <span className="flex-shrink-0 text-muted-foreground">
                  {expandedCountry ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </span>
                <Globe className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-sidebar-foreground truncate">{countryInfo.country_name || 'All Countries'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {countryInfo.country_cd && (
                      <>
                        <span className="text-[10px] font-mono text-muted-foreground">Code: {countryInfo.country_cd}</span>
                        <span className="text-[10px] text-muted-foreground">•</span>
                      </>
                    )}
                    <span className="text-[10px] text-muted-foreground">{totalCities} projects</span>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-semibold text-primary flex-shrink-0">{totalPnds} PNDs</span>
              </div>
            </button>

            <AnimatePresence>
              {expandedCountry && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                  <div className="ml-4 pl-3 border-l border-sidebar-border space-y-1 py-1">
                    {citiesListContent}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <div className="space-y-1">
            {citiesListContent}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// CityRevenueOverview
// ============================================
const CityRevenueOverview = ({ city, filterMonth, filterYear, filterThreshold, pndStatusFilters = EMPTY_FILTERS, filteredMapData = null, filteredMapLoading = false }) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear  = filterYear  || String(now.getFullYear());
  const threshold    = filterThreshold || '';

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasFetchedRef = useRef(false);

  const cityId = city?.city_send_name || city?.city_cd;

  const hasStatusFilter = pndStatusFilters.length > 0;

  const formatValue = (value) => {
    if (!value) return '0';
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    return isNaN(num) ? '0' : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fetchRevenue = useCallback(async () => {
    if (!cityId) return;
    setLoading(true);
    setError(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      const url = `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}`;
      const response = await apiCall('get', url, null, null, false, false);

      if (response?.success) {
        setRevenueData(response);
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to fetch revenue');
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [cityId, displayMonth, displayYear, threshold]);

  useEffect(() => {
    if (hasStatusFilter) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchRevenue();
  }, [fetchRevenue, hasStatusFilter]);

  const effectiveLoading = hasStatusFilter ? filteredMapLoading : loading;
  const effectiveData    = hasStatusFilter
    ? (filteredMapData?.type === 'city' ? filteredMapData : null)
    : revenueData;

  if (effectiveLoading) {
    return <StatsGridShimmer count={4} />;
  }

  if (!hasStatusFilter && error) {
    return (
      <div className="flex flex-col items-center gap-2 py-5">
        <p className="text-xs text-muted-foreground text-center">{error}</p>
        <button onClick={fetchRevenue} className="text-[10px] font-semibold text-primary hover:underline">Retry</button>
      </div>
    );
  }

  if (!effectiveData) {
    return (
      <div className="text-center py-5">
        <p className="text-xs text-muted-foreground">No revenue data available</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2.5">
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Total Revenue</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.collection)}</p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Ticket Count</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.ticket_count)}</p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ALS</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.als)}</p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ATV</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.atv)}</p>
      </div>
    </div>
  );
};

// ============================================
// StreetRevenueOverview
// ============================================
const StreetRevenueOverview = ({ street, filterMonth, filterYear, filterThreshold, pndStatusFilters = EMPTY_FILTERS, filteredMapData = null, filteredMapLoading = false }) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear  = filterYear  || String(now.getFullYear());
  const threshold    = filterThreshold || '';

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasFetchedRef = useRef(false);

  const streetId = street?.street_send_name || street?.street_cd;

  const hasStatusFilter = pndStatusFilters.length > 0;

  const formatValue = (value) => {
    if (!value) return '0';
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    return isNaN(num) ? '0' : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fetchRevenue = useCallback(async () => {
    if (!streetId) return;
    setLoading(true);
    setError(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      const url = `/get-pnd-collection?street=true&device_id=${streetId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}`;
      const response = await apiCall('get', url, null, null, false, false);

      if (response?.success) {
        setRevenueData(response);
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to fetch revenue');
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [streetId, displayMonth, displayYear, threshold]);

  useEffect(() => {
    if (hasStatusFilter) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchRevenue();
  }, [fetchRevenue, hasStatusFilter]);

  const effectiveLoading = hasStatusFilter ? filteredMapLoading : loading;
  const effectiveData    = hasStatusFilter
    ? (filteredMapData?.type === 'street' ? filteredMapData : null)
    : revenueData;

  if (effectiveLoading) {
    return <StatsGridShimmer count={4} />;
  }

  if (!hasStatusFilter && error) {
    return (
      <div className="flex flex-col items-center gap-2 py-5">
        <p className="text-xs text-muted-foreground text-center">{error}</p>
        <button onClick={fetchRevenue} className="text-[10px] font-semibold text-primary hover:underline">Retry</button>
      </div>
    );
  }

  if (!effectiveData) {
    return (
      <div className="text-center py-5">
        <p className="text-xs text-muted-foreground">No revenue data available</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2.5">
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Total Revenue</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.collection)}</p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Ticket Count</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.ticket_count)}</p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ALS</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.als)}</p>
      </div>
      <div className="rounded-lg bg-secondary p-3">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">ATV</p>
        <p className="text-xl font-bold text-foreground">{formatValue(effectiveData.atv)}</p>
      </div>
    </div>
  );
};

// ============================================
// CityRevenueSummaryRow — with permission filtering
// ============================================
const CityRevenueSummaryRow = ({ code, stats, city, onSelectCity }) => {
  const { userContracts } = usePermissions();
  const [open, setOpen] = useState(false);
  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, '')) || 0;

  // ============================================
  // Check if this city is allowed based on permissions
  // ============================================
  const isAllowed = useMemo(() => {
    if (!userContracts) return true;

    const normalized = normalizePermissions(userContracts);
    const allowedKeys = new Set([
      ...normalized.onstreet.map(c => getContractKey(c)),
      ...normalized.offstreet.map(c => getContractKey(c)),
    ]);

    if (allowedKeys.size === 0) return true;

    const cityName = normalizeName(city?.city_name || code);
    
    for (const contract of ONSTREET_CONTRACTS.children) {
      const contractKey = getContractKey(contract);
      if (allowedKeys.has(contractKey) && normalizeName(contract.fullName) === cityName) {
        return true;
      }
    }
    
    return allowedKeys.has(code);
  }, [userContracts, code, city]);

  if (!isAllowed) return null;

  const contributionPct = parseFloat(stats?.contribution) || 0;
  const collection   = stats?.collection   ?? '0';
  const ticketCount  = stats?.ticket_count ?? '0';
  const contribution = stats?.contribution ?? '0.00%';
  const als = (stats?.als !== null && stats?.als !== undefined && stats?.als !== '') ? String(stats.als) : '\u2014';
  const atv = (stats?.atv !== null && stats?.atv !== undefined && stats?.atv !== '') ? String(stats.atv) : '\u2014';

  const handleNameClick = () => {
    if (city) onSelectCity?.(city);
    setOpen(true);
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
        </button>

        <button
          onClick={handleNameClick}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
          title="View project details"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {city?.city_name || code}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {collection}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contribution}
            </span>
          </div>
        </button>

        <button
          onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-2">
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Revenue</p>
                <p className="text-lg font-bold tabular-nums leading-none text-primary">{collection}</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Tickets</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{ticketCount}</p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Contribution</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{contribution}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">ALS</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{als}</p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">ATV</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{atv}</p>
                </div>
              </div>

              {city && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Streets</p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">{city.total_streets ?? 0}</p>
                  </div>
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">PNDs</p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">{city.total_pnds ?? 0}</p>
                  </div>
                </div>
              )}

              {city && (
                <button
                  onClick={() => onSelectCity?.(city)}
                  className="w-full text-center text-[10px] font-semibold text-primary hover:underline py-1"
                >
                  Open Project Details 
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// ProjectCard — with permission filtering
// ============================================
const ProjectCard = ({ project }) => {
  const { userContracts } = usePermissions();
  const [open, setOpen] = useState(false);
  const contribution = parseFloat(project.contribution_percent) || 0;

  // ============================================
  // Check if this project is allowed based on permissions
  // ============================================
  const isAllowed = useMemo(() => {
    if (!userContracts) return true;

    const normalized = normalizePermissions(userContracts);
    const allowedKeys = new Set([
      ...normalized.onstreet.map(c => getContractKey(c)),
      ...normalized.offstreet.map(c => getContractKey(c)),
    ]);

    if (allowedKeys.size === 0) return true;

    const projectName = normalizeName(project.project_name);
    
    for (const contract of ONSTREET_CONTRACTS.children) {
      const contractKey = getContractKey(contract);
      if (allowedKeys.has(contractKey) && normalizeName(contract.fullName) === projectName) {
        return true;
      }
    }
    
    return allowedKeys.has(project.project_code);
  }, [userContracts, project]);

  if (!isAllowed) return null;

  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, '')) || 0;
  const formatMoney = (v) => toNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const matchedCity = null; // This will be passed from parent

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
        </button>

        <div className="flex-1 flex items-center gap-2 text-left min-w-0">
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {project.project_name}
          </span>
          <span className="text-[9px] font-mono text-muted-foreground flex-shrink-0">
            {project.project_code}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {formatMoney(project.total_sales)}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {project.contribution_percent}%
            </span>
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(contribution, 100)}%` }}
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Total PNDs</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{project.total_pnds}</p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Active PNDs</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{project.active_pnds}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Coins Sales</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{formatMoney(project.total_coins_sales)}</p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Notes Sales</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{formatMoney(project.total_notes_sales)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Total Sales</p>
                  <p className="text-lg font-bold tabular-nums leading-none text-primary">{formatMoney(project.total_sales)}</p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Latest Collection Date</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{project.latest_collection_date || '—'}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// CountryDetailPanel
// ============================================
const CountryDetailPanel = ({ countryInfo, citiesData = [], activeFilters, onClose, onSelectCity }) => {
  const { userContracts } = usePermissions();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasFetchedRef = useRef(false);
  
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const hasFetchedSummaryRef = useRef(false);
  
  // ✅ SINGLE STRING — sirf ek section open hoga
  const [openSection, setOpenSection] = useState('overallSummary');
  const handleToggleSection = (id) => setOpenSection(prev => (prev === id ? null : id));
  
  const [citySearch, setCitySearch] = useState('');
  const [projectSearch, setProjectSearch] = useState('');

  const panelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(440);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onResizeMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startW.current = panelRef.current?.offsetWidth || 440;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const diff = startX.current - e.clientX;
      const newW = Math.min(900, Math.max(320, startW.current + diff));
      setPanelWidth(newW);
    };
    const onUp = () => { isDragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const now = new Date();
  const displayMonth = activeFilters?.month || MONTH_NAMES[now.getMonth()];
  const displayYear  = activeFilters?.year  || String(now.getFullYear());
  const threshold     = activeFilters?.threshold || '';
  const countryCd = countryInfo?.country_cd;

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);
    const monthParam = buildMonthParam(displayMonth) ? displayMonth : displayMonth.includes('-') ? displayMonth : `01-${displayMonth}`;
    const url = `/get-pnd-collection?device_id=${countryCd}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}`;
    apiCall('get', url, null, null, false, false).then(response => {
      if (!response?.success) { setPayload(null); return; }
      setPayload(response);
    }).catch(() => setPayload(null)).finally(() => {
      setLoading(false);
      hasFetchedRef.current = false;
    });
  }, [countryCd, displayMonth, displayYear, threshold]);

  useEffect(() => {
    if (hasFetchedSummaryRef.current) return;
    hasFetchedSummaryRef.current = true;
    setSummaryLoading(true);
    setSummaryError(null);
    apiCall('get', '/cp-map/collection-summary', {}, null, false, false).then(response => {
      if (!response?.success) { setSummaryData(null); setSummaryError('Failed to load summary'); return; }
      setSummaryData(response);
    }).catch(() => {
      setSummaryData(null);
      setSummaryError('Failed to load summary');
    }).finally(() => {
      setSummaryLoading(false);
      hasFetchedSummaryRef.current = false;
    });
  }, []);

  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, '')) || 0;
  const formatMoney = (v) => toNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const summary = summaryData?.summary || null;
  
  // ============================================
  // FILTER PROJECTS BASED ON PERMISSIONS
  // ============================================
  const allProjects = summaryData?.projects || [];
  const filteredProjects = useMemo(() => {
    if (!userContracts) return allProjects;

    const normalized = normalizePermissions(userContracts);
    const allowedKeys = new Set([
      ...normalized.onstreet.map(c => getContractKey(c)),
      ...normalized.offstreet.map(c => getContractKey(c)),
    ]);

    if (allowedKeys.size === 0) return allProjects;

    return allProjects.filter(project => {
      const projectName = normalizeName(project.project_name);
      
      for (const contract of ONSTREET_CONTRACTS.children) {
        const contractKey = getContractKey(contract);
        if (allowedKeys.has(contractKey) && normalizeName(contract.fullName) === projectName) {
          return true;
        }
      }
      
      return allowedKeys.has(project.project_code);
    });
  }, [userContracts, allProjects]);

  const totalCities  = countryInfo?.total_cities  ?? citiesData.length;
  const totalStreets = countryInfo?.total_streets ?? citiesData.reduce((s, c) => s + (c.total_streets || 0), 0);
  const totalPnds    = countryInfo?.total_pnds    ?? citiesData.reduce((s, c) => s + (c.total_pnds    || 0), 0);
  const activePnds    = citiesData.reduce((s, c) =>
    s + (c.streets || []).reduce((ss, st) => ss + (st.pnds || []).filter(p => p.device_status === 'ACTIVE').length, 0), 0);
  const inactivePnds  = citiesData.reduce((s, c) =>
    s + (c.streets || []).reduce((ss, st) => ss + (st.pnds || []).filter(p => p.device_status === 'INACTIVE').length, 0), 0);

  const collection    = payload?.collection;
  const ticketCount   = payload?.ticket_count;
  const als           = payload?.als;
  const atv           = payload?.atv;
  const cityBreakdown = payload?.payment_breakdown?.cities || {};

  const mergedCities = Object.entries(cityBreakdown)
    .map(([code, stats]) => {
      const match = citiesData.find(c =>
        c.city_send_name?.toUpperCase().startsWith(`${code.toUpperCase()}-`) ||
        c.city_cd === code
      );
      return { code, stats, city: match };
    })
    .sort((a, b) => toNum(b.stats?.collection) - toNum(a.stats?.collection));

  const rows = mergedCities.length ? mergedCities : citiesData.map(c => ({ code: c.city_cd, stats: null, city: c }));
  const q = citySearch.trim().toLowerCase();
  const filteredRows = q
    ? rows.filter(r => (r.city?.city_name || r.code || '').toLowerCase().includes(q))
    : rows;

  const projectQ = projectSearch.trim().toLowerCase();
  const filteredProjectList = projectQ
    ? filteredProjects.filter(p => (p.project_name || '').toLowerCase().includes(projectQ) || (p.project_code || '').toLowerCase().includes(projectQ))
    : filteredProjects;

  return (
    <motion.div
      ref={panelRef}
      style={{ width: panelWidth }}
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed top-14 right-0 bottom-0 z-[40] overflow-y-auto cb-scroll bg-card border-l border-border shadow-2xl"
    >
      <div
        onMouseDown={onResizeMouseDown}
        style={{ position: 'fixed', top: 65, bottom: 0, width: '6px', left: `calc(100vw - ${panelWidth}px)`, cursor: 'col-resize', zIndex: 9999 }}
        className="hover:bg-primary/20 transition-colors"
      />

      <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
          <Globe className="w-4.5 h-4.5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-foreground truncate">{countryInfo?.country_name || 'Country'}</p>
          {countryInfo?.country_cd && (
            <p className="text-[10px] font-mono text-muted-foreground">Code: {countryInfo.country_cd}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
  <SaleCollectionReportButton cityName={countryInfo?.country_name || 'All Projects'} />
  <button onClick={onClose} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all flex-shrink-0">
    <X className="w-4 h-4" />
  </button>
</div>
      </div>

      {loading ? (
        <div className="p-4"><StatsGridShimmer /></div>
      ) : (
        <div className="p-4 space-y-3">
          <AccordionSection id="overview" openId={openSection} onToggle={handleToggleSection} icon={<BarChart2 className="w-3.5 h-3.5" />} title="Overview" defaultOpen>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Collection', value: collection != null ? `${toNum(collection).toLocaleString()}` : '—' },
                  { label: 'Tickets',    value: ticketCount != null ? toNum(ticketCount).toLocaleString() : '—' },
                  { label: 'ALS',        value: als ?? '—' },
                  { label: 'ATV',        value: atv ?? '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded-lg p-3 bg-secondary/40 border border-border">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
                    <p className="text-lg font-bold text-foreground truncate">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </AccordionSection>

          <AccordionSection id="overallSummary" openId={openSection} onToggle={handleToggleSection} icon={<Layers className="w-3.5 h-3.5" />} title="Overall Collection Summary">
            {summaryLoading ? (
              <StatsGridShimmer count={8} />
            ) : summaryError ? (
              <div className="flex flex-col items-center gap-2 py-5">
                <p className="text-xs text-muted-foreground text-center">{summaryError}</p>
              </div>
            ) : !summary ? (
              <div className="text-center py-5">
                <p className="text-xs text-muted-foreground">No summary data available</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                    <div className="flex items-center gap-1 mb-1">
                      <CircleDot className="w-3.5 h-3.5" />
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total PNDs</p>
                    </div>
                    <p className="text-base font-bold text-foreground truncate">{formatCount(summary.total_pnds)}</p>
                  </div>
                  <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                    <div className="flex items-center gap-1 mb-1">
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Active PNDs</p>
                    </div>
                    <p className="text-base font-bold text-foreground truncate">{formatCount(summary.active_pnds)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                    <div className="flex items-center gap-1 mb-1">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Inactive PNDs</p>
                    </div>
                    <p className="text-base font-bold text-foreground truncate">{formatCount(summary.inactive_pnds)}</p>
                  </div>
                  <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                    <div className="flex items-center gap-1 mb-1">
                      <CreditCard className="w-3.5 h-3.5" />
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Coins Sales</p>
                    </div>
                    <p className="text-base font-bold text-foreground truncate">{formatMoney(summary.total_coins_sales)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                    <div className="flex items-center gap-1 mb-1">
                      <Wallet className="w-3.5 h-3.5" />
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Notes Sales</p>
                    </div>
                    <p className="text-base font-bold text-foreground truncate">{formatMoney(summary.total_notes_sales)}</p>
                  </div>
                  <div className="rounded-lg p-3 bg-primary/10 border border-primary/30">
                    <div className="flex items-center gap-1.5 mb-1">
                      <TrendingUp className="w-3.5 h-3.5 text-primary" />
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Sales</p>
                    </div>
                    <p className="text-xl font-bold text-foreground truncate">{formatMoney(summary.total_sales)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Latest Collection Date</p>
                    </div>
                    <p className="text-sm font-bold text-foreground truncate">{summary.latest_collection_date || '—'}</p>
                  </div>
                </div>
              </div>
            )}
          </AccordionSection>

          <AccordionSection id="projectsList" openId={openSection} onToggle={handleToggleSection} icon={<Building2 className="w-3.5 h-3.5" />} title={`Projects Breakdown (${filteredProjects.length})`}>
            {summaryLoading ? (
              <StatsGridShimmer count={4} />
            ) : summaryError ? (
              <div className="flex flex-col items-center gap-2 py-5">
                <p className="text-xs text-muted-foreground text-center">{summaryError}</p>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-xs text-muted-foreground">No projects data available</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={projectSearch}
                    onChange={e => setProjectSearch(e.target.value)}
                    className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                  />
                  {projectSearch && (
                    <button
                      onClick={() => setProjectSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {projectSearch && (
                  <p className="text-[10px] text-muted-foreground px-0.5">
                    {filteredProjectList.length === 0
                      ? `No projects matched "${projectSearch}"`
                      : `${filteredProjectList.length} of ${filteredProjects.length} projects`}
                  </p>
                )}

                <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
                  {filteredProjectList.length === 0 && projectSearch ? (
                    <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
                      <Search className="w-5 h-5 opacity-30" />
                      <p className="text-xs">No results for "{projectSearch}"</p>
                    </div>
                  ) : (
                    filteredProjectList.map((project, idx) => (
                      <ProjectCard key={idx} project={project} />
                    ))
                  )}
                </div>
              </div>
            )}
          </AccordionSection>
        </div>
      )}
    </motion.div>
  );
};

// ============================================
// SummaryStreetRow
// ============================================
const SummaryStreetRow = ({ street, city, onSelectStreet, staleThresholdDays = 30, noTransactionThresholdDays = 60 }) => {
  const [open, setOpen] = useState(false);
  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, '')) || 0;
  const formatMoney = (v) => toNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const contributionPct = parseFloat(street.contribution_percent) || 0;
  const totalSales = formatMoney(street.total_sales);
  const contribution = street.contribution_percent != null ? `${street.contribution_percent}%` : '—';
  const pnds = useMemo(() => {
    const list = street.pnds || [];
    return [...list].sort((a, b) => toNum(b?.total_sales) - toNum(a?.total_sales));
  }, [street.pnds]);

  const fullStreet = (city?.streets || []).find(s => String(s.street_cd) === String(street.street_cd));
  const handleOpenStreet = () => {
    if (fullStreet) onSelectStreet?.(fullStreet);
    setOpen(true);
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
        </button>

        <button
          onClick={handleOpenStreet}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
          title="View street details"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate">{street.street_name}</span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {totalSales}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contribution}
            </span>
          </div>
        </button>

        <button
          onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Total Sales</p>
                <p className="text-lg font-bold tabular-nums leading-none text-primary">{totalSales}</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Total PNDs</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{formatCount(street.total_pnds)}</p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Active PNDs</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{formatCount(street.active_pnds)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Inactive PNDs</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{formatCount(street.inactive_pnds)}</p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Collection Date</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground truncate">{street.latest_collection_date || '—'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Coins Sales</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{formatMoney(street.total_coins_sales)}</p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Notes Sales</p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{formatMoney(street.total_notes_sales)}</p>
                </div>
              </div>

              {pnds.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="grid grid-cols-[1.1fr_0.9fr_0.8fr_0.8fr_0.9fr_0.9fr_0.9fr_0.9fr] gap-1 px-2 py-2 bg-secondary/60 border-b border-border">
                    {['PND', 'Total Sales', 'Coins', 'Notes', 'Coll. Date', 'Status Date', 'Last Trans', 'Status'].map(h => (
                      <p key={h} className="text-[7px] uppercase tracking-tight text-muted-foreground font-semibold truncate">{h}</p>
                    ))}
                  </div>
                  <div className="max-h-52 overflow-y-auto cb-scroll divide-y divide-border">
                    {pnds.map((p, pi) => {
                      const stale = isStaleCollectionPnd(p, staleThresholdDays);
                      const noTrans = isNoTransactionPnd(p, noTransactionThresholdDays);
                      const rowClass = stale
                        ? 'bg-red-500/10 hover:bg-red-500/15'
                        : noTrans
                        ? 'bg-amber-500/10 hover:bg-amber-500/15'
                        : 'hover:bg-secondary/40';
                      const rowTitle = stale
                        ? `Not collected for over ${staleThresholdDays} days and still holds cash`
                        : noTrans
                        ? `No transaction for over ${noTransactionThresholdDays} days`
                        : undefined;
                      return (
                        <div
                          key={p.device_code || pi}
                          className={`px-2 py-2.5 transition-colors ${rowClass}`}
                          title={rowTitle}
                        >
                          <div className="grid grid-cols-[1.1fr_0.9fr_0.8fr_0.8fr_0.9fr_0.9fr_0.9fr_0.9fr] gap-1 items-center">
                            <p className={`text-[9px] font-mono font-semibold truncate flex items-center gap-1 ${stale ? 'text-red-500' : noTrans ? 'text-amber-500' : 'text-foreground'}`}>
                              {stale && <AlertTriangle className="w-2.5 h-2.5 flex-shrink-0" />}
                              {!stale && noTrans && <Clock className="w-2.5 h-2.5 flex-shrink-0" />}
                              {p.device_code}
                            </p>
                            <p className="text-[9px] font-semibold tabular-nums text-primary truncate">{formatMoney(p.total_sales)}</p>
                            <p className={`text-[9px] tabular-nums truncate ${stale ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>{formatMoney(p.coins_sales)}</p>
                            <p className={`text-[9px] tabular-nums truncate ${stale ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>{formatMoney(p.notes_sales)}</p>
                            <p className={`text-[8px] tabular-nums truncate ${stale ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>{p.collection_date || '—'}</p>
                            <p className="text-[8px] text-muted-foreground tabular-nums truncate">{p.status_date || '—'}</p>
                            <p className={`text-[8px] tabular-nums truncate ${noTrans ? 'text-amber-500 font-semibold' : 'text-muted-foreground'}`}>{p.last_trans_date || '—'}</p>
                            <p className={`text-[8px] font-semibold tabular-nums truncate ${p.device_status === 'ACTIVE' ? 'text-emerald-500' : p.device_status === 'INACTIVE' ? 'text-red-400' : 'text-muted-foreground'}`}>{p.device_status || '—'}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {fullStreet ? (
                <button
                  onClick={() => onSelectStreet?.(fullStreet)}
                  className="w-full text-[10px] font-semibold text-primary hover:underline text-center py-1 flex items-center justify-center gap-1"
                >
                  View Street Detail <ChevronRight className="w-3 h-3" />
                </button>
              ) : (
                <p className="text-[9px] text-muted-foreground text-center py-1">
                  Street detail not available
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// CityDetailPanel
// ============================================
const CityDetailPanel = ({ city, onClose, activeFilters, onSelectStreet, onBack, onBreakdownLoad, pndStatusFilters = EMPTY_FILTERS, onTogglePndStatusFilter, onClearPndStatusFilters, streetsOnlyMode = false, onToggleStreetsOnly, filteredMapData = null, filteredMapLoading = false, baysPerPnd = 25, staleThresholdDays = 30, onStaleThresholdChange, noTransactionThresholdDays = 60, onNoTransactionThresholdChange }) => {
  const [streetSearch, setStreetSearch] = useState('');
  const [openSection, setOpenSection] = useState('overallSummary');
  const handleToggleSection = (id) => setOpenSection(prev => (prev === id ? null : id));
  const [noTransactionCount,  setNoTransactionCount]  = useState(null);
  const [belowThresholdCount, setBelowThresholdCount] = useState(null);
  const [activePndsCount,   setActivePndsCount]   = useState(null);
  const [inactivePndsCount, setInactivePndsCount] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const hasFetchedSummaryRef = useRef(false);

  const now = new Date();
  const displayMonth = activeFilters?.month || MONTH_NAMES[now.getMonth()];
  const displayYear  = activeFilters?.year  || String(now.getFullYear());
  const cityId = city?.city_send_name || city?.city_cd;
  const hasThreshold = !!(activeFilters?.threshold);
  const { parkingBaysCount } = useParkingBaysCount(city, displayMonth, displayYear);
  const pndCoverage = useMemo(() => {
    const bays  = parseCount(parkingBaysCount);
    const ratio = Number(baysPerPnd) > 0 ? Number(baysPerPnd) : 25;
    if (bays === null || activePndsCount === null) return { requiredPnds: null, diff: null };
    const requiredPnds = Math.ceil(bays / ratio);
    const diff = activePndsCount - requiredPnds;
    return { requiredPnds, diff };
  }, [parkingBaysCount, baysPerPnd, activePndsCount]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setOverviewLoading(true);
    setNoTransactionCount(null);
    setBelowThresholdCount(null);
    onBreakdownLoad?.({ belowThreshold: new Set(), noTransaction: new Set(), revenueMap: new Map() });
    if (!cityId) { setOverviewLoading(false); return; }
    const monthParam = buildMonthParam(displayMonth) ? displayMonth : displayMonth.includes('-') ? displayMonth : `01-${displayMonth}`;
    const threshold  = activeFilters?.threshold || '';
    const url = `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}`;
    apiCall('get', url, null, null, false, false).then(response => {
      if (!response?.success) return;
      const payload  = response.data?.city_breakdown ?? response.city_breakdown ?? response.data ?? response;

      setNoTransactionCount(payload.no_tran_pnd_count ?? 0);
      setBelowThresholdCount(payload.below_threshold_pnd_count ?? 0);

      setActivePndsCount(payload.active_pnds_count ?? 0);
      setInactivePndsCount(payload.inactive_pnds_count ?? 0);

      const belowSet = new Set();
      const noTxSet  = new Set();
      const revMap   = new Map();
      (payload.streets || []).forEach(st => {
        (st.pnds || []).forEach(p => {
          if (p.below_threshold === true) belowSet.add(String(p.pnd));
          if (p.no_transaction  === true) noTxSet.add(String(p.pnd));
          revMap.set(String(p.pnd), {
            collection: p.collection,
            ticket_count: p.ticket_count,
            als: p.als,
            atv: p.atv,
            contribution: p.contribution,
            device_status_date: p.device_status_date,
            device_active_date: p.device_active_date,
          });
        });
      });
      onBreakdownLoad?.({ belowThreshold: belowSet, noTransaction: noTxSet, revenueMap: revMap });
    }).catch(() => {}).finally(() => {
      setOverviewLoading(false);
      hasFetchedRef.current = false;
    });
  }, [cityId, displayMonth, displayYear, activeFilters?.threshold]);

  const cityCdForSummary = city?.city_cd;
  useEffect(() => {
    if (!cityCdForSummary) { setSummaryData(null); setSummaryLoading(false); return; }
    hasFetchedSummaryRef.current = false;
    if (hasFetchedSummaryRef.current) return;
    hasFetchedSummaryRef.current = true;
    setSummaryLoading(true);
    setSummaryError(null);
    apiCall('get', `/cp-map/collection-summary?city_cd=${cityCdForSummary}`, {}, null, false, false).then(response => {
      if (!response?.success) { setSummaryData(null); setSummaryError('Failed to load summary'); return; }
      setSummaryData(response);
    }).catch(() => {
      setSummaryData(null);
      setSummaryError('Failed to load summary');
    }).finally(() => {
      setSummaryLoading(false);
      hasFetchedSummaryRef.current = false;
    });
  }, [cityCdForSummary]);

  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, '')) || 0;
  const formatMoney = (v) => toNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const summary = summaryData?.summary || null;
  const summaryStreets = summaryData?.streets || [];

  const activeTotal = city.total_pnds || 0;

  const toggleFilter = (key) => {
    onTogglePndStatusFilter?.(key);
  };

  const panelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(520);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onResizeMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startW.current = panelRef.current?.offsetWidth || 520;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const diff = startX.current - e.clientX;
      const newW = Math.min(900, Math.max(320, startW.current + diff));
      setPanelWidth(newW);
    };
    const onUp = () => { isDragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const bnaTotal = (city.streets || []).reduce((s, st) => s + (st.pnds?.filter(p => p.device_type?.includes('BNA')).length || 0), 0);
  const txTotal = (city.streets || []).reduce((s, st) => s + (st.pnds?.filter(p => p.device_type?.includes('TX')).length || 0), 0);

  const filteredStreets = (city.streets || []).filter(street =>
    !streetSearch.trim() || street.street_name?.toLowerCase().includes(streetSearch.trim().toLowerCase())
  );

  return (
    <motion.div
      ref={panelRef}
      style={{ width: panelWidth }}
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed top-14 right-0 bottom-0 z-[40] overflow-y-auto cb-scroll bg-card border-l border-border shadow-2xl"
    >
      <div
        onMouseDown={onResizeMouseDown}
        style={{ position: 'fixed', top: 65, bottom: 0, width: '6px', left: `calc(100vw - ${panelWidth}px)`, cursor: 'col-resize', zIndex: 9999 }}
        className="hover:bg-primary/20 transition-colors"
      />

      <div className="sticky top-0 z-10 backdrop-blur-md border-b border-border bg-card/80">
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2.5">
              {onBack && (
                <button onClick={onBack} className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-foreground">{city.city_name}</h2>
                <p className="text-[10px] font-mono text-muted-foreground">Project Code: {city.city_cd}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
  <SaleCollectionReportButton cityCd={city?.city_cd} cityName={city?.city_name} />
  <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
    <X className="w-4 h-4" />
  </button>
</div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-2.5">
        <AccordionSection id="overview" openId={openSection} onToggle={handleToggleSection} icon={<BarChart2 className="w-3.5 h-3.5" />} title="Overview">
          {overviewLoading ? (
            <StatsGridShimmer count={6} />
          ) : (
            <>
              {streetsOnlyMode && (
                <div className="flex items-center gap-1.5 mb-2.5 px-2.5 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/25">
                  <span className="text-[10px] text-indigo-500 font-semibold flex-1">
                    Showing: All Streets & PNDs
                  </span>
                  <button
                    onClick={onToggleStreetsOnly}
                    className="text-indigo-500 hover:text-indigo-500/70 flex-shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {pndStatusFilters.length > 0 && !streetsOnlyMode && (
                <div className="flex items-center gap-1.5 mb-2.5 px-2.5 py-1.5 rounded-lg bg-primary/10 border border-primary/25">
                  <span className="text-[10px] text-primary font-semibold flex-1">
                    Showing: {pndStatusFilters.map(f => PND_STATUS_LABELS[f] || f).join(' + ')}
                  </span>
                  <button
                    onClick={onClearPndStatusFilters}
                    className="text-primary hover:text-primary/70 flex-shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {pndStatusFilters.length === 0 && !streetsOnlyMode && (
                <div className="flex items-center gap-1.5 mb-2.5">
                  <SlidersHorizontal className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                  <p className="text-[10px] text-muted-foreground">Tap a card below to filter the map & data</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2.5">
                <button
                  onClick={onToggleStreetsOnly}
                  title="Click to view all streets & PNDs"
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    streetsOnlyMode
                      ? 'border-indigo-500 bg-indigo-500/15 ring-1 ring-indigo-500'
                      : 'border-border bg-secondary hover:bg-secondary/70 cursor-pointer'
                  }`}
                >
                  {streetsOnlyMode && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-indigo-500 flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!streetsOnlyMode && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-muted-foreground/0 group-hover:text-muted-foreground/60 transition-colors" />
                  )}
                  <p className={`text-[9px] uppercase tracking-widest mb-1 ${streetsOnlyMode ? 'text-indigo-500' : 'text-muted-foreground'}`}>
                    Total Streets
                  </p>
                  <p className={`text-2xl font-bold leading-none ${streetsOnlyMode ? 'text-indigo-500' : 'text-foreground'}`}>
                    {city.total_streets || 0}
                  </p>
                </button>

                <button
                  onClick={() => toggleFilter('all')}
                  title="Click to view all PNDs"
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    pndStatusFilters.includes('all') && !streetsOnlyMode
                      ? 'border-primary bg-primary/15 ring-1 ring-primary'
                      : 'border-border bg-secondary hover:bg-secondary/70 cursor-pointer'
                  }`}
                >
                  {pndStatusFilters.includes('all') && !streetsOnlyMode && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!(pndStatusFilters.includes('all') && !streetsOnlyMode) && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-muted-foreground/0 group-hover:text-muted-foreground/60 transition-colors" />
                  )}
                  <p className={`text-[9px] uppercase tracking-widest mb-1 ${pndStatusFilters.includes('all') && !streetsOnlyMode ? 'text-primary' : 'text-muted-foreground'}`}>
                    Total PNDs
                  </p>
                  <p className={`text-2xl font-bold leading-none ${pndStatusFilters.includes('all') && !streetsOnlyMode ? 'text-primary' : 'text-foreground'}`}>
                    {city.total_pnds || 0}
                  </p>
                </button>

                <button
                  disabled={!activePndsCount}
                  onClick={() => toggleFilter('active')}
                  title={activePndsCount ? 'Click to filter map by Active PNDs' : 'No active PNDs in this period'}
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    !activePndsCount
                      ? 'border-emerald-500/15 bg-emerald-500/5 opacity-50 cursor-not-allowed'
                      : pndStatusFilters.includes('active')
                        ? 'border-emerald-500 bg-emerald-500/20 ring-1 ring-emerald-500'
                        : 'border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/15 cursor-pointer'
                  }`}
                >
                  {pndStatusFilters.includes('active') && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!pndStatusFilters.includes('active') && !!activePndsCount && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-emerald-500/0 group-hover:text-emerald-500/60 transition-colors" />
                  )}
                  <div className="flex items-center gap-1 mb-1">
                    <CircleDot className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-emerald-500">Active PNDs</p>
                  </div>
                  <p className="text-2xl font-bold text-emerald-500 leading-none">
                    {activePndsCount === null ? '—' : activePndsCount}
                  </p>
                </button>

                <button
                  disabled={!inactivePndsCount}
                  onClick={() => toggleFilter('inactive')}
                  title={inactivePndsCount ? 'Click to filter map by Inactive PNDs' : 'No inactive PNDs in this period'}
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    !inactivePndsCount
                      ? 'border-red-400/15 bg-red-400/5 opacity-50 cursor-not-allowed'
                      : pndStatusFilters.includes('inactive')
                        ? 'border-red-400 bg-red-400/20 ring-1 ring-red-400'
                        : 'border-red-400/30 bg-red-400/10 hover:bg-red-400/15 cursor-pointer'
                  }`}
                >
                  {pndStatusFilters.includes('inactive') && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-400 flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!pndStatusFilters.includes('inactive') && !!inactivePndsCount && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-red-400/0 group-hover:text-red-400/60 transition-colors" />
                  )}
                  <div className="flex items-center gap-1 mb-1">
                    <Ban className="w-3 h-3 text-red-400 flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-red-400">Inactive PNDs</p>
                  </div>
                  <p className="text-2xl font-bold text-red-400 leading-none">
                    {inactivePndsCount === null ? '—' : inactivePndsCount}
                  </p>
                </button>

                <button
                  disabled={!noTransactionCount}
                  onClick={() => toggleFilter('no_transaction')}
                  title={noTransactionCount ? 'Click to filter map by No Transaction PNDs' : 'No such PNDs in this period'}
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    !noTransactionCount
                      ? 'border-red-500/15 bg-red-500/5 opacity-50 cursor-not-allowed'
                      : pndStatusFilters.includes('no_transaction')
                        ? 'border-red-500 bg-red-500/20 ring-1 ring-red-500'
                        : 'border-red-500/30 bg-red-500/10 hover:bg-red-500/15 cursor-pointer'
                  }`}
                >
                  {pndStatusFilters.includes('no_transaction') && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!pndStatusFilters.includes('no_transaction') && !!noTransactionCount && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-red-500/0 group-hover:text-red-500/60 transition-colors" />
                  )}
                  <div className="flex items-center gap-1 mb-1">
                    <Ban className="w-3 h-3 text-red-500 flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-red-500">No Transaction</p>
                  </div>
                  <p className="text-2xl font-bold text-red-500 leading-none">
                    {noTransactionCount === null ? '—' : noTransactionCount}
                  </p>
                </button>

                <button
                  disabled={!belowThresholdCount}
                  onClick={() => toggleFilter('below_threshold')}
                  title={belowThresholdCount ? 'Click to filter map by Below Threshold PNDs' : 'No such PNDs in this period'}
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    !belowThresholdCount
                      ? 'border-black/15 bg-black/5 opacity-50 cursor-not-allowed'
                      : pndStatusFilters.includes('below_threshold')
                        ? 'border-black bg-black/20 ring-1 ring-black'
                        : 'border-black/30 bg-black/10 hover:bg-black/15 cursor-pointer'
                  }`}
                >
                  {pndStatusFilters.includes('below_threshold') && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-black flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!pndStatusFilters.includes('below_threshold') && !!belowThresholdCount && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-black/0 group-hover:text-black/60 transition-colors" />
                  )}
                  <div className="flex items-center gap-1 mb-1">
                    <AlertTriangle className="w-3 h-3 text-black flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-black">Below Threshold</p>
                  </div>
                  <p className="text-2xl font-bold text-black leading-none">
                    {belowThresholdCount === null ? '—' : belowThresholdCount}
                  </p>
                </button>

                <div
                  title="Parking bays for this contract (from Enforcement)"
                  className="group relative text-left rounded-xl border border-amber-500/30 bg-amber-500/10 p-3"
                >
                  <div className="flex items-center gap-1 mb-1">
                    <CircleParking className="w-3 h-3 text-amber-500 flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-amber-500">Parking Bays</p>
                  </div>
                  <p className="text-2xl font-bold text-amber-500 leading-none">
                    {formatCount(parkingBaysCount)}
                  </p>
                </div>

                <div className="rounded-xl border border-border bg-secondary p-3">
                  <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Required PNDs</p>
                  <p className="text-2xl font-bold text-foreground leading-none">
                    {pndCoverage.requiredPnds === null ? '—' : formatCount(pndCoverage.requiredPnds)}
                  </p>
                </div>

                <div className={`rounded-xl border p-3 ${
                  pndCoverage.diff === null ? 'border-border bg-secondary' :
                  pndCoverage.diff > 0 ? 'border-emerald-500/30 bg-emerald-500/10' :
                  pndCoverage.diff < 0 ? 'border-red-500/30 bg-red-500/10' :
                  'border-border bg-secondary'
                }`}>
                  <p className={`text-[9px] uppercase tracking-widest mb-1 ${
                    pndCoverage.diff === null ? 'text-muted-foreground' :
                    pndCoverage.diff > 0 ? 'text-emerald-500' :
                    pndCoverage.diff < 0 ? 'text-red-500' :
                    'text-muted-foreground'
                  }`}>PND Status</p>
                  <p className={`text-1=2xl font-bold leading-none ${
                    pndCoverage.diff === null ? 'text-foreground' :
                    pndCoverage.diff > 0 ? 'text-emerald-500' :
                    pndCoverage.diff < 0 ? 'text-red-500' :
                    'text-foreground'
                  }`}>
                    {pndCoverage.diff === null ? '—' :
                      pndCoverage.diff > 0 ? `+${formatCount(pndCoverage.diff)} Extra` :
                      pndCoverage.diff < 0 ? `-${formatCount(Math.abs(pndCoverage.diff))} Shortage` :
                      'Balanced'}
                  </p>
                </div>
              </div>
            </>
          )}
        </AccordionSection>

        <AccordionSection id="deviceType" openId={openSection} onToggle={handleToggleSection} icon={<Layers className="w-3.5 h-3.5" />} title="Device Type Breakdown">
          <div className="grid grid-cols-2 gap-2.5">
            <div className="rounded-lg bg-secondary p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: '#8B5CF6' }} />
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground">BNA Devices</p>
              </div>
              <p className="text-[22px] font-medium leading-none">{bnaTotal}</p>
            </div>
            <div className="rounded-lg bg-secondary p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: '#F59E0B' }} />
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground">TX Devices</p>
              </div>
              <p className="text-[22px] font-medium leading-none">{txTotal}</p>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection id="overallSummary" openId={openSection} onToggle={handleToggleSection} icon={<Layers className="w-3.5 h-3.5" />} title="Overall Project Summary">
          {summaryLoading ? (
            <StatsGridShimmer count={8} />
          ) : summaryError ? (
            <div className="flex flex-col items-center gap-2 py-5">
              <p className="text-xs text-muted-foreground text-center">{summaryError}</p>
            </div>
          ) : !summary ? (
            <div className="text-center py-5">
              <p className="text-xs text-muted-foreground">No summary data available</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total Streets</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatCount(summary.total_streets)}</p>
                </div>
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <CircleDot className="w-3.5 h-3.5" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total PNDs</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatCount(summary.total_pnds)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Active PNDs</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatCount(summary.active_pnds)}</p>
                </div>
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Inactive PNDs</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatCount(summary.inactive_pnds)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <CreditCard className="w-3.5 h-3.5" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Coins Sales</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatMoney(summary.total_coins_sales)}</p>
                </div>
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <Wallet className="w-3.5 h-3.5" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Notes Sales</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatMoney(summary.total_notes_sales)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg p-3 bg-primary/10 border border-primary/30">
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendingUp className="w-3.5 h-3.5 text-primary" />
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Sales</p>
                  </div>
                  <p className="text-xl font-bold text-foreground truncate">{formatMoney(summary.total_sales)}</p>
                </div>
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Calendar className="w-3.5 h-3.5" />
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Latest Collection Date</p>
                  </div>
                  <p className="text-sm font-bold text-foreground truncate">{summary.latest_collection_date || '—'}</p>
                </div>
              </div>
            </div>
          )}
        </AccordionSection>

        <AccordionSection id="streetsBreakdown" openId={openSection} onToggle={handleToggleSection} icon={<MapPin className="w-3.5 h-3.5" />} title={`Streets Breakdown (${summaryStreets.length})`}>
          <div className="flex items-center justify-between gap-3 mb-2.5 px-0.5 flex-wrap">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">Not collected over</span>
              <input
                type="number"
                min={1}
                value={staleThresholdDays}
                onChange={(e) => onStaleThresholdChange?.(e.target.value)}
                className="w-12 text-[11px] font-semibold text-center rounded-md border border-border bg-secondary/60 px-1 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="text-[10px] text-muted-foreground">days</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-amber-500 flex-shrink-0" />
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">No transaction over</span>
              <input
                type="number"
                min={1}
                value={noTransactionThresholdDays}
                onChange={(e) => onNoTransactionThresholdChange?.(e.target.value)}
                className="w-12 text-[11px] font-semibold text-center rounded-md border border-border bg-secondary/60 px-1 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="text-[10px] text-muted-foreground">days</span>
            </div>
          </div>

          {summaryLoading ? (
            <StatsGridShimmer count={4} />
          ) : summaryError ? (
            <div className="flex flex-col items-center gap-2 py-5">
              <p className="text-xs text-muted-foreground text-center">{summaryError}</p>
            </div>
          ) : summaryStreets.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-xs text-muted-foreground">No streets data available</p>
            </div>
          ) : (
            <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
              {summaryStreets.map((st, idx) => (
                <SummaryStreetRow key={st.street_cd || idx} street={st} city={city} onSelectStreet={onSelectStreet} staleThresholdDays={staleThresholdDays} noTransactionThresholdDays={noTransactionThresholdDays} />
              ))}
            </div>
          )}
        </AccordionSection>
      </div>
    </motion.div>
  );
};

// ============================================
// StreetDetailPanel
// ============================================
const StreetDetailPanel = ({ street, city, onClose, activeFilters, onSelectPnd, onBack, onBreakdownLoad, pndStatusFilters = EMPTY_FILTERS, onTogglePndStatusFilter, onClearPndStatusFilters, filteredMapData = null, filteredMapLoading = false, baysPerPnd = 25, staleThresholdDays = 30, onStaleThresholdChange, noTransactionThresholdDays = 60, onNoTransactionThresholdChange }) => {
  const [openSection, setOpenSection] = useState('overallSummary');
  const handleToggleSection = (id) => setOpenSection(prev => (prev === id ? null : id));
  const [noTransactionCount,  setNoTransactionCount]  = useState(null);
  const [belowThresholdCount, setBelowThresholdCount] = useState(null);

  const [activePndsCount,   setActivePndsCount]   = useState(null);
  const [inactivePndsCount, setInactivePndsCount] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const hasFetchedRef = useRef(false);

  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const hasFetchedSummaryRef = useRef(false);

  const hasThreshold = !!(activeFilters?.threshold);
  const _now         = new Date();
  const displayMonth = activeFilters?.month || MONTH_NAMES[_now.getMonth()];
  const displayYear  = activeFilters?.year  || String(_now.getFullYear());
  const threshold    = activeFilters?.threshold || '';
  const streetId     = street?.street_send_name || street?.street_cd;
  const { parkingBaysCount } = useParkingBaysCount(city, displayMonth, displayYear);
  const pndCoverage = useMemo(() => {
    const bays  = parseCount(parkingBaysCount);
    const ratio = Number(baysPerPnd) > 0 ? Number(baysPerPnd) : 25;
    if (bays === null || activePndsCount === null) return { requiredPnds: null, diff: null };
    const requiredPnds = Math.ceil(bays / ratio);
    const diff = activePndsCount - requiredPnds;
    return { requiredPnds, diff };
  }, [parkingBaysCount, baysPerPnd, activePndsCount]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    if (!streetId) { setOverviewLoading(false); return; }
    setOverviewLoading(true);
    setNoTransactionCount(null);
    setBelowThresholdCount(null);

    const monthParam = buildMonthParam(displayMonth);
    const url = `/get-pnd-collection?street=true&device_id=${streetId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}`;

    apiCall('get', url, null, null, false, false).then(response => {
      if (!response?.success) return;

      const breakdown = response.street_breakdown;

      const noTranCount   = (breakdown?.pnds || []).filter(p => p.no_transaction  === true).length;
      const belowThrCount = (breakdown?.pnds || []).filter(p => p.below_threshold === true).length;
      setNoTransactionCount(noTranCount);
      setBelowThresholdCount(belowThrCount);

      setActivePndsCount(breakdown?.summary?.active_pnds_count ?? 0);
      setInactivePndsCount(breakdown?.summary?.inactive_pnds_count ?? 0);

      const belowSet = new Set();
      const noTxSet  = new Set();
      const revMap   = new Map();
      (breakdown?.pnds || []).forEach(p => {
        if (p.below_threshold === true) belowSet.add(String(p.pnd));
        if (p.no_transaction  === true) noTxSet.add(String(p.pnd));
        revMap.set(String(p.pnd), {
          collection: p.collection,
          ticket_count: p.ticket_count,
          als: p.als,
          atv: p.atv,
          contribution: p.contribution,
          device_status_date: p.device_status_date,
          device_active_date: p.device_active_date,
        });
      });
      onBreakdownLoad?.({ belowThreshold: belowSet, noTransaction: noTxSet, revenueMap: revMap });
    }).catch(() => {}).finally(() => {
      setOverviewLoading(false);
      hasFetchedRef.current = false;
    });
  }, [streetId, displayMonth, displayYear, threshold]);

  const cityCdForSummary   = city?.city_cd;
  const streetCdForSummary = street?.street_cd;
  useEffect(() => {
    if (!cityCdForSummary || !streetCdForSummary) { setSummaryData(null); setSummaryLoading(false); return; }
    hasFetchedSummaryRef.current = false;
    if (hasFetchedSummaryRef.current) return;
    hasFetchedSummaryRef.current = true;
    setSummaryLoading(true);
    setSummaryError(null);
    apiCall('get', `/cp-map/collection-summary?city_cd=${cityCdForSummary}&street_cd=${streetCdForSummary}`, {}, null, false, false).then(response => {
      if (!response?.success) { setSummaryData(null); setSummaryError('Failed to load summary'); return; }
      setSummaryData(response);
    }).catch(() => {
      setSummaryData(null);
      setSummaryError('Failed to load summary');
    }).finally(() => {
      setSummaryLoading(false);
      hasFetchedSummaryRef.current = false;
    });
  }, [cityCdForSummary, streetCdForSummary]);

  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, '')) || 0;
  const formatMoney = (v) => toNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const summary = summaryData?.summary || null;
  const summaryPnds = useMemo(() => {
    const list = summaryData?.streets?.[0]?.pnds || summaryData?.pnds || [];
    return [...list].sort((a, b) => (parseCount(b?.total_sales) || 0) - (parseCount(a?.total_sales) || 0));
  }, [summaryData]);

  const panelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(520);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onResizeMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startW.current = panelRef.current?.offsetWidth || 520;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const diff = startX.current - e.clientX;
      const newW = Math.min(900, Math.max(320, startW.current + diff));
      setPanelWidth(newW);
    };
    const onUp = () => { isDragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const bnaCount    = street.pnds?.filter(p => p.device_type?.includes('BNA')).length || 0;
  const txCount     = street.pnds?.filter(p => p.device_type?.includes('TX')).length  || 0;

  const handleBreakdownLoad = useCallback(({ belowThreshold, noTransaction }) => {
    onBreakdownLoad?.({ belowThreshold, noTransaction });
  }, [onBreakdownLoad]);

  const handlePndSelect = (breakdownItem) => {
    const targetPndNumber = String(breakdownItem.pnd);
    const fullPnd = street.pnds?.find(p => String(p.pnd_number) === targetPndNumber);
    if (fullPnd) onSelectPnd(fullPnd);
  };

  const toggleFilter = (key) => {
    onTogglePndStatusFilter?.(key);
  };

  return (
    <motion.div
      ref={panelRef}
      style={{ width: panelWidth }}
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed top-14 right-0 bottom-0 z-[40] overflow-y-auto cb-scroll bg-card border-l border-border shadow-2xl"
    >
      <div
        onMouseDown={onResizeMouseDown}
        style={{ position: 'fixed', top: 65, bottom: 0, width: '6px', left: `calc(100vw - ${panelWidth}px)`, cursor: 'col-resize', zIndex: 9999 }}
        className="hover:bg-primary/20 transition-colors"
      />

      <div className="sticky top-0 z-10 backdrop-blur-md border-b border-border bg-card/80">
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2.5">
              {onBack && (
                <button onClick={onBack} className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-foreground">{street.street_name}</h2>
                <p className="text-[10px] font-mono text-muted-foreground">{city?.city_name} • Code: {street.street_cd}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
  <SaleCollectionReportButton cityCd={city?.city_cd} streetCd={street?.street_cd} cityName={city?.city_name} streetName={street?.street_name} />
  <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
    <X className="w-4 h-4" />
  </button>
</div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-2.5">
        <AccordionSection id="overview" openId={openSection} onToggle={handleToggleSection} icon={<BarChart2 className="w-3.5 h-3.5" />} title="Overview">
          {overviewLoading ? (
            <StatsGridShimmer count={5} />
          ) : (
            <>
              {pndStatusFilters.length > 0 && (
                <div className="flex items-center gap-1.5 mb-2.5 px-2.5 py-1.5 rounded-lg bg-primary/10 border border-primary/25">
                  <span className="text-[10px] text-primary font-semibold flex-1">
                    Showing: {pndStatusFilters.map(f => PND_STATUS_LABELS[f] || f).join(' + ')}
                  </span>
                  <button
                    onClick={onClearPndStatusFilters}
                    className="text-primary hover:text-primary/70 flex-shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {pndStatusFilters.length === 0 && (
                <div className="flex items-center gap-1.5 mb-2.5">
                  <SlidersHorizontal className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                  <p className="text-[10px] text-muted-foreground">Tap a card below to filter the map & data</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2.5">
                <button
                  onClick={() => toggleFilter('all')}
                  title="Click to view all PNDs"
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    pndStatusFilters.includes('all')
                      ? 'border-primary bg-primary/15 ring-1 ring-primary'
                      : 'border-border bg-secondary hover:bg-secondary/70 cursor-pointer'
                  }`}
                >
                  {pndStatusFilters.includes('all') && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!pndStatusFilters.includes('all') && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-muted-foreground/0 group-hover:text-muted-foreground/60 transition-colors" />
                  )}
                  <p className={`text-[9px] uppercase tracking-widest mb-1 ${pndStatusFilters.includes('all') ? 'text-primary' : 'text-muted-foreground'}`}>
                    Total PNDs
                  </p>
                  <p className={`text-2xl font-bold leading-none ${pndStatusFilters.includes('all') ? 'text-primary' : 'text-foreground'}`}>
                    {street.total_pnds || 0}
                  </p>
                </button>

                <button
                  disabled={!activePndsCount}
                  onClick={() => toggleFilter('active')}
                  title={activePndsCount ? 'Click to filter map by Active PNDs' : 'No active PNDs in this period'}
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    !activePndsCount
                      ? 'border-emerald-500/15 bg-emerald-500/5 opacity-50 cursor-not-allowed'
                      : pndStatusFilters.includes('active')
                        ? 'border-emerald-500 bg-emerald-500/20 ring-1 ring-emerald-500'
                        : 'border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/15 cursor-pointer'
                  }`}
                >
                  {pndStatusFilters.includes('active') && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!pndStatusFilters.includes('active') && !!activePndsCount && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-emerald-500/0 group-hover:text-emerald-500/60 transition-colors" />
                  )}
                  <div className="flex items-center gap-1 mb-1">
                    <CircleDot className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-emerald-500">Active PNDs</p>
                  </div>
                  <p className="text-2xl font-bold text-emerald-500 leading-none">
                    {activePndsCount === null ? '—' : activePndsCount}
                  </p>
                </button>

                <button
                  disabled={!inactivePndsCount}
                  onClick={() => toggleFilter('inactive')}
                  title={inactivePndsCount ? 'Click to filter map by Inactive PNDs' : 'No inactive PNDs in this period'}
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    !inactivePndsCount
                      ? 'border-red-400/15 bg-red-400/5 opacity-50 cursor-not-allowed'
                      : pndStatusFilters.includes('inactive')
                        ? 'border-red-400 bg-red-400/20 ring-1 ring-red-400'
                        : 'border-red-400/30 bg-red-400/10 hover:bg-red-400/15 cursor-pointer'
                  }`}
                >
                  {pndStatusFilters.includes('inactive') && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-400 flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!pndStatusFilters.includes('inactive') && !!inactivePndsCount && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-red-400/0 group-hover:text-red-400/60 transition-colors" />
                  )}
                  <div className="flex items-center gap-1 mb-1">
                    <Ban className="w-3 h-3 text-red-400 flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-red-400">Inactive PNDs</p>
                  </div>
                  <p className="text-2xl font-bold text-red-400 leading-none">
                    {inactivePndsCount === null ? '—' : inactivePndsCount}
                  </p>
                </button>

                <button
                  disabled={!noTransactionCount}
                  onClick={() => toggleFilter('no_transaction')}
                  title={noTransactionCount ? 'Click to filter map by No Transaction PNDs' : 'No such PNDs in this period'}
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    !noTransactionCount
                      ? 'border-red-500/15 bg-red-500/5 opacity-50 cursor-not-allowed'
                      : pndStatusFilters.includes('no_transaction')
                        ? 'border-red-500 bg-red-500/20 ring-1 ring-red-500'
                        : 'border-red-500/30 bg-red-500/10 hover:bg-red-500/15 cursor-pointer'
                  }`}
                >
                  {pndStatusFilters.includes('no_transaction') && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!pndStatusFilters.includes('no_transaction') && !!noTransactionCount && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-red-500/0 group-hover:text-red-500/60 transition-colors" />
                  )}
                  <div className="flex items-center gap-1 mb-1">
                    <Ban className="w-3 h-3 text-red-500 flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-red-500">No Transaction</p>
                  </div>
                  <p className="text-2xl font-bold text-red-500 leading-none">
                    {noTransactionCount === null ? '—' : noTransactionCount}
                  </p>
                </button>

                <button
                  disabled={!belowThresholdCount}
                  onClick={() => toggleFilter('below_threshold')}
                  title={belowThresholdCount ? 'Click to filter map by Below Threshold PNDs' : 'No such PNDs in this period'}
                  className={`group relative text-left rounded-xl border p-3 transition-all ${
                    !belowThresholdCount
                      ? 'border-black/15 bg-black/5 opacity-50 cursor-not-allowed'
                      : pndStatusFilters.includes('below_threshold')
                        ? 'border-black bg-black/20 ring-1 ring-black'
                        : 'border-black/30 bg-black/10 hover:bg-black/15 cursor-pointer'
                  }`}
                >
                  {pndStatusFilters.includes('below_threshold') && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-black flex items-center justify-center shadow-sm">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {!pndStatusFilters.includes('below_threshold') && !!belowThresholdCount && (
                    <SlidersHorizontal className="absolute top-2 right-2 w-3 h-3 text-black/0 group-hover:text-black/60 transition-colors" />
                  )}
                  <div className="flex items-center gap-1 mb-1">
                    <AlertTriangle className="w-3 h-3 text-black flex-shrink-0" />
                    <p className="text-[9px] uppercase tracking-widest text-black">Below Threshold</p>
                  </div>
                  <p className="text-2xl font-bold text-black leading-none">
                    {belowThresholdCount === null ? '—' : belowThresholdCount}
                  </p>
                </button>
              </div>
            </>
          )}
        </AccordionSection>

        <AccordionSection id="deviceType" openId={openSection} onToggle={handleToggleSection} icon={<Layers className="w-3.5 h-3.5" />} title="Device Type Breakdown">
          <div className="grid grid-cols-2 gap-2.5">
            <div className="rounded-lg bg-secondary p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: '#8B5CF6' }} />
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground">BNA Devices</p>
              </div>
              <p className="text-[22px] font-medium leading-none">{bnaCount}</p>
            </div>
            <div className="rounded-lg bg-secondary p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: '#F59E0B' }} />
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground">TX Devices</p>
              </div>
              <p className="text-[22px] font-medium leading-none">{txCount}</p>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection id="overallSummary" openId={openSection} onToggle={handleToggleSection} icon={<Layers className="w-3.5 h-3.5" />} title="Overall Street Summary">
          {summaryLoading ? (
            <StatsGridShimmer count={8} />
          ) : summaryError ? (
            <div className="flex flex-col items-center gap-2 py-5">
              <p className="text-xs text-muted-foreground text-center">{summaryError}</p>
            </div>
          ) : !summary ? (
            <div className="text-center py-5">
              <p className="text-xs text-muted-foreground">No summary data available</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <CircleDot className="w-3.5 h-3.5" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total PNDs</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatCount(summary.total_pnds)}</p>
                </div>
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Active PNDs</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatCount(summary.active_pnds)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Inactive PNDs</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatCount(summary.inactive_pnds)}</p>
                </div>
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <CreditCard className="w-3.5 h-3.5" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Coins Sales</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatMoney(summary.total_coins_sales)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1 mb-1">
                    <Wallet className="w-3.5 h-3.5" />
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Notes Sales</p>
                  </div>
                  <p className="text-base font-bold text-foreground truncate">{formatMoney(summary.total_notes_sales)}</p>
                </div>
                <div className="rounded-lg p-3 bg-primary/10 border border-primary/30">
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendingUp className="w-3.5 h-3.5 text-primary" />
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Sales</p>
                  </div>
                  <p className="text-xl font-bold text-foreground truncate">{formatMoney(summary.total_sales)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Calendar className="w-3.5 h-3.5" />
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Latest Collection Date</p>
                  </div>
                  <p className="text-sm font-bold text-foreground truncate">{summary.latest_collection_date || '—'}</p>
                </div>
              </div>
            </div>
          )}
        </AccordionSection>

        <AccordionSection id="pndsBreakdown" openId={openSection} onToggle={handleToggleSection} icon={<CircleDot className="w-3.5 h-3.5" />} title={`PNDs Breakdown (${summaryPnds.length})`}>
          <div className="flex items-center justify-between gap-3 mb-2.5 px-0.5 flex-wrap">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">Not collected over</span>
              <input
                type="number"
                min={1}
                value={staleThresholdDays}
                onChange={(e) => onStaleThresholdChange?.(e.target.value)}
                className="w-12 text-[11px] font-semibold text-center rounded-md border border-border bg-secondary/60 px-1 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="text-[10px] text-muted-foreground">days</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-amber-500 flex-shrink-0" />
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">No transaction over</span>
              <input
                type="number"
                min={1}
                value={noTransactionThresholdDays}
                onChange={(e) => onNoTransactionThresholdChange?.(e.target.value)}
                className="w-12 text-[11px] font-semibold text-center rounded-md border border-border bg-secondary/60 px-1 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="text-[10px] text-muted-foreground">days</span>
            </div>
          </div>

          {summaryLoading ? (
            <StatsGridShimmer count={4} />
          ) : summaryError ? (
            <div className="flex flex-col items-center gap-2 py-5">
              <p className="text-xs text-muted-foreground text-center">{summaryError}</p>
            </div>
          ) : summaryPnds.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-xs text-muted-foreground">No PNDs data available</p>
            </div>
          ) : (
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="grid grid-cols-[1.1fr_0.9fr_0.8fr_0.8fr_0.9fr_0.9fr_0.9fr_0.9fr] gap-1 px-2 py-2 bg-secondary/60 border-b border-border">
                {['PND', 'Total Sales', 'Coins', 'Notes', 'Coll. Date', 'Status Date', 'Last Trans', 'Status'].map(h => (
                  <p key={h} className="text-[7px] uppercase tracking-tight text-muted-foreground font-semibold truncate">{h}</p>
                ))}
              </div>
              <div className="max-h-72 overflow-y-auto cb-scroll divide-y divide-border">
                {summaryPnds.map((p, pi) => {
                  const handleOpenPnd = () => {
                    const fullPnd = (street.pnds || []).find(fp => String(fp.pnd_number) === String(p.device_code));
                    if (fullPnd) onSelectPnd?.(fullPnd);
                  };
                  const stale = isStaleCollectionPnd(p, staleThresholdDays);
                  const noTrans = isNoTransactionPnd(p, noTransactionThresholdDays);
                  const rowClass = stale
                    ? 'bg-red-500/10 hover:bg-red-500/15'
                    : noTrans
                    ? 'bg-amber-500/10 hover:bg-amber-500/15'
                    : 'hover:bg-secondary/40';
                  const rowTitle = stale
                    ? `Not collected for over ${staleThresholdDays} days and still holds cash`
                    : noTrans
                    ? `No transaction for over ${noTransactionThresholdDays} days`
                    : 'Open PND';
                  return (
                    <button
                      key={p.device_code || pi}
                      onClick={handleOpenPnd}
                      className={`w-full text-left px-2 py-2.5 transition-colors cursor-pointer ${rowClass}`}
                      title={rowTitle}
                    >
                      <div className="grid grid-cols-[1.1fr_0.9fr_0.8fr_0.8fr_0.9fr_0.9fr_0.9fr_0.9fr] gap-1 items-center">
                        <p className={`text-[9px] font-mono font-semibold truncate hover:underline flex items-center gap-1 ${stale ? 'text-red-500' : noTrans ? 'text-amber-500' : 'text-foreground'}`}>
                          {stale && <AlertTriangle className="w-2.5 h-2.5 flex-shrink-0" />}
                          {!stale && noTrans && <Clock className="w-2.5 h-2.5 flex-shrink-0" />}
                          {p.device_code}
                        </p>
                        <p className="text-[9px] font-semibold tabular-nums text-primary truncate">{formatMoney(p.total_sales)}</p>
                        <p className={`text-[9px] tabular-nums truncate ${stale ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>{formatMoney(p.coins_sales)}</p>
                        <p className={`text-[9px] tabular-nums truncate ${stale ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>{formatMoney(p.notes_sales)}</p>
                        <p className={`text-[8px] tabular-nums truncate ${stale ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>{p.collection_date || '—'}</p>
                        <p className="text-[8px] text-muted-foreground tabular-nums truncate">{p.status_date || '—'}</p>
                        <p className={`text-[8px] tabular-nums truncate ${noTrans ? 'text-amber-500 font-semibold' : 'text-muted-foreground'}`}>{p.last_trans_date || '—'}</p>
                        <p className={`text-[8px] font-semibold tabular-nums truncate ${p.device_status === 'ACTIVE' ? 'text-emerald-500' : p.device_status === 'INACTIVE' ? 'text-red-400' : 'text-muted-foreground'}`}>{p.device_status || '—'}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </AccordionSection>
      </div>
    </motion.div>
  );
};

// ============================================
// PndDetailPanel
// ============================================
const PndDetailPanel = ({ pnd, street, city, onClose, activeFilters, onBack }) => {
  const [openSection, setOpenSection] = useState('overallSummary');
  const handleToggleSection = (id) => setOpenSection(prev => (prev === id ? null : id));
  const deviceColor = getDeviceColor(pnd.device_type);
  const deviceType  = getDeviceTypeName(pnd.device_type);

  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const hasFetchedSummaryRef = useRef(false);
  const pndNumberForSummary = pnd?.pnd_number;

  useEffect(() => {
    if (!pndNumberForSummary) { setSummaryData(null); setSummaryLoading(false); return; }
    hasFetchedSummaryRef.current = false;
    if (hasFetchedSummaryRef.current) return;
    hasFetchedSummaryRef.current = true;
    setSummaryLoading(true);
    setSummaryError(null);
    apiCall('get', `/cp-map/collection-summary?pnd_number=${pndNumberForSummary}`, {}, null, false, false).then(response => {
      if (!response?.success) { setSummaryData(null); setSummaryError('Failed to load summary'); return; }
      setSummaryData(response);
    }).catch(() => {
      setSummaryData(null);
      setSummaryError('Failed to load summary');
    }).finally(() => {
      setSummaryLoading(false);
      hasFetchedSummaryRef.current = false;
    });
  }, [pndNumberForSummary]);

  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, '')) || 0;
  const formatMoney = (v) => toNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatMoneyOrNA = (v) => (v === null || v === undefined || v === '') ? 'Not Available' : formatMoney(v);
  const formatDateOrNA  = (v) => (v === null || v === undefined || v === '') ? 'Not Available' : v;
  const summary = summaryData?.summary || null;
  const summaryDevice = summaryData?.data || null;

  const panelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(520);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onResizeMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startW.current = panelRef.current?.offsetWidth || 520;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const diff = startX.current - e.clientX;
      const newW = Math.min(900, Math.max(320, startW.current + diff));
      setPanelWidth(newW);
    };
    const onUp = () => { isDragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  return (
    <motion.div
      ref={panelRef}
      style={{ width: panelWidth }}
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed top-14 right-0 bottom-0 z-[40] overflow-y-auto cb-scroll bg-card border-l border-border shadow-2xl"
    >
      <div
        onMouseDown={onResizeMouseDown}
        style={{ position: 'fixed', top: 65, bottom: 0, width: '6px', left: `calc(100vw - ${panelWidth}px)`, cursor: 'col-resize', zIndex: 9999 }}
        className="hover:bg-primary/20 transition-colors"
      />

      <div className="sticky top-0 z-10 backdrop-blur-md border-b border-border bg-card/80">
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2.5">
              {onBack && (
                <button onClick={onBack} className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: deviceColor, boxShadow: `0 0 12px ${deviceColor}66` }} />
              <div>
                <h2 className="text-sm font-bold text-foreground">PND {pnd.device_type}</h2>
                <p className="text-[10px] font-mono text-muted-foreground">{deviceType}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-2.5">
        <AccordionSection id="location" openId={openSection} onToggle={handleToggleSection} icon={<MapPin className="w-3.5 h-3.5" />} title="Location Details">
          <div className="space-y-2.5">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Project</p>
              <p className="text-sm font-medium text-foreground">{city?.city_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Street</p>
              <p className="text-sm font-medium text-foreground">{street?.street_name || 'N/A'}</p>
            </div>
            {pnd.device_zone && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Zone</p>
                <p className="text-sm font-medium text-foreground">{pnd.device_zone}</p>
              </div>
            )}
          </div>
        </AccordionSection>

        <AccordionSection id="deviceInfo" openId={openSection} onToggle={handleToggleSection} icon={<Layers className="w-3.5 h-3.5" />} title="Device Information">
          {pnd.pnd_number && (
            <div className="mb-3 pb-3 border-b border-border">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">PND Number</p>
              <p className="text-sm font-mono font-semibold text-primary">#{pnd.pnd_number}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Device Type</p>
              <p className="text-sm font-medium text-foreground">{pnd.device_type || 'Not Installed'}</p>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection id="overallSummary" openId={openSection} onToggle={handleToggleSection} icon={<Layers className="w-3.5 h-3.5" />} title="PND Overall Summary">
          {summaryLoading ? (
            <StatsGridShimmer count={6} />
          ) : summaryError ? (
            <div className="flex flex-col items-center gap-2 py-5">
              <p className="text-xs text-muted-foreground text-center">{summaryError}</p>
            </div>
          ) : !summary && !summaryDevice ? (
            <div className="text-center py-5">
              <p className="text-xs text-muted-foreground">No summary data available</p>
            </div>
          ) : (
            <div className="space-y-2">
              {summaryDevice && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                      <div className="flex items-center gap-1 mb-1">
                        <CreditCard className="w-3.5 h-3.5" />
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Coins Sales</p>
                      </div>
                      <p className="text-base font-bold text-foreground truncate">{formatMoneyOrNA(summaryDevice.coins_sales)}</p>
                    </div>
                    <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                      <div className="flex items-center gap-1 mb-1">
                        <Wallet className="w-3.5 h-3.5" />
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Notes Sales</p>
                      </div>
                      <p className="text-base font-bold text-foreground truncate">{formatMoneyOrNA(summaryDevice.notes_sales)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg p-3 bg-primary/10 border border-primary/30">
                      <div className="flex items-center gap-1.5 mb-1">
                        <TrendingUp className="w-3.5 h-3.5 text-primary" />
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total Sales</p>
                      </div>
                      <p className="text-base font-bold text-foreground truncate">{formatMoneyOrNA(summaryDevice.total_sales)}</p>
                    </div>
                    <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                      <div className="flex items-center gap-1 mb-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Collection Date</p>
                      </div>
                      <p className="text-sm font-bold text-foreground truncate">{formatDateOrNA(summaryDevice.collection_date)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                      <div className="flex items-center gap-1 mb-1">
                        <Clock className="w-3.5 h-3.5" />
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Status Date</p>
                      </div>
                      <p className="text-sm font-bold text-foreground truncate">{formatDateOrNA(summaryDevice.status_date)}</p>
                    </div>
                    <div className="rounded-lg p-2.5 bg-secondary/40 border border-border">
                      <div className="flex items-center gap-1 mb-1">
                        <Check className="w-3.5 h-3.5" />
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Transaction Date</p>
                      </div>
                      <p className="text-sm font-bold text-foreground truncate">{formatDateOrNA(summaryDevice.last_trans_date)}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </AccordionSection>
      </div>
    </motion.div>
  );
};

// ============================================
// API Hook
// ============================================
const usePndData = (enabled = true) => {
  const [citiesData, setCitiesData] = useState([]);
  const [countryInfo, setCountryInfo] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]           = useState(null);
  const hasLoadedOnce = useRef(false);
  const hasFetchedRef = useRef(false);
const fetchPndData = async (filters = {}) => {
    if (hasLoadedOnce.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const params = {};
      if (filters?.month && filters?.year) {
        params.month = filters.month;
        params.year  = filters.year;
      }
      if (filters?.city_send_name)   params.city_send_name   = filters.city_send_name;
      if (filters?.street_send_name) params.street_send_name = filters.street_send_name;
      if (filters?.country_cd)       params.country_cd       = filters.country_cd;
      if (Object.prototype.hasOwnProperty.call(filters || {}, 'city')) params.city = filters.city;

      const response = await apiCall('get', '/cp-map/data', Object.keys(params).length ? params : null, null, false, false);
      if (response?.success && response?.data) {
        const payload = response.data;
        // ── NEW: API ab country wrapper ke sath aati hai
        // { country_cd, country_name, total_cities, total_streets, total_pnds, cities: [...] }
        // Purane shape (direct array) ke liye bhi backward-compatible rakha hai.
        if (Array.isArray(payload)) {
          setCitiesData(payload);
          setCountryInfo(null);
        } else {
          setCitiesData(payload.cities || []);
          setCountryInfo({
  country_cd:     payload.country_cd,
  country_name:   payload.country_name,
  total_cities:   payload.total_cities,
  total_streets:  payload.total_streets,
  total_pnds:     payload.total_pnds,
  coordinates: Array.isArray(payload.coordinates) ? payload.coordinates : null,
  overview: payload.overview || null,
});
        }
      } else {
        setError('Failed to load PND data');
      }
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
      hasLoadedOnce.current = true;
      hasFetchedRef.current = false;

    }
  };

  useEffect(() => { 
    if (!enabled) return; // 👈 NAYA — jab tak Parking tab active nahi, fetch mat karo
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchPndData(); 
  }, [enabled]);
  return { citiesData, countryInfo, loading, refreshing, error, refetch: fetchPndData };
};

// ============================================
// CollectionDetails — "CollectionDetails" tab (map + sidebar)
// ============================================
const CollectionDetails = ({
  onCityChange,
  onStreetChange,              // 👈 NAYA
  onFiltersChangeExternal,
  selectedCityNameExternal,
  selectedStreetInfoExternal,  // 👈 NAYA
  selectedDateFilterExternal,
  onReady,
  isActive = true,
  monthGroups = [],
  yearOptions = [],
  optLoading = false,
  optError = null,
} = {}) => {
  const [selectedCity, setSelectedCity]     = useState(null);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [selectedPnd, setSelectedPnd]       = useState(null);
  // ✅ FIX: Sidebar mein Country section ab default open hai, isliye uska
  // detail panel (CountryDetailPanel) bhi shuru se hi open rehna chahiye.
  const [detailView, setDetailView]         = useState('country');
  const [isDarkMode, setIsDarkMode]         = useState(true);
  const [activeFilters, setActiveFilters]   = useState({});
  const [sidebarOpen, setSidebarOpen]       = useState(true);

  // ✅ NEW — Stale-collection threshold: ek hi jagah state, City/Street dono panels
  // isi ko use karte hain — kahin bhi change karo, sab jagah apply hoga, aur jab tak
  // dobara change na karo tab tak yehi rahega (localStorage mein bhi save hota hai).
  const [staleThresholdDays, setStaleThresholdDays] = useState(() => {
    const saved = Number(localStorage.getItem('pnd_stale_threshold_days'));
    return Number.isFinite(saved) && saved > 0 ? saved : 30;
  });
  const handleStaleThresholdChange = useCallback((days) => {
    const safe = Math.max(1, Number(days) || 1);
    setStaleThresholdDays(safe);
    localStorage.setItem('pnd_stale_threshold_days', String(safe));
  }, []);

  // ✅ NEW — No-transaction threshold: PND highlight hogi agar itne din se koi
  // transaction hi nahi hui — alag state, alag input box, lekin same pattern
  // (ek jagah change karo, sab jagah apply, localStorage mein persist).
  const [noTransactionThresholdDays, setNoTransactionThresholdDays] = useState(() => {
    const saved = Number(localStorage.getItem('pnd_no_transaction_threshold_days'));
    return Number.isFinite(saved) && saved > 0 ? saved : 60;
  });
  const handleNoTransactionThresholdChange = useCallback((days) => {
    const safe = Math.max(1, Number(days) || 1);
    setNoTransactionThresholdDays(safe);
    localStorage.setItem('pnd_no_transaction_threshold_days', String(safe));
  }, []);

  // ── Left sidebar drag-to-resize (same pattern as right-side detail panels) ──
  const sidebarWidthRef = useRef(320);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const isSidebarDragging = useRef(false);
  const sidebarStartX = useRef(0);
  const sidebarStartW = useRef(0);

  const onSidebarResizeMouseDown = (e) => {
    isSidebarDragging.current = true;
    sidebarStartX.current = e.clientX;
    sidebarStartW.current = sidebarWidthRef.current;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isSidebarDragging.current) return;
      // left sidebar → drag right se width badhti hai (right panels ke ulta)
      const diff = e.clientX - sidebarStartX.current;
      const newW = Math.min(600, Math.max(240, sidebarStartW.current + diff));
      sidebarWidthRef.current = newW;
      setSidebarWidth(newW);
    };
    const onUp = () => { isSidebarDragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);
  const [belowThresholdPnds, setBelowThresholdPnds] = useState(new Set());
  const [noTransactionPnds,  setNoTransactionPnds]  = useState(new Set());
  const [pndRevenueMap, setPndRevenueMap] = useState(new Map()); // ✅ NEW — pnd_number → revenue info

  // ── PND status filters — multi-select array: ['active', 'below_threshold'] etc. ──
  const [pndStatusFilters, setPndStatusFilters] = useState(EMPTY_FILTERS);

  // ── NEW: "Total Streets" card click → sirf street polygons dikhao, PND markers nahi ──
  const [streetsOnlyMode, setStreetsOnlyMode] = useState(false);

  // ── Shared "1 PND covers X bays" ratio — sidebar mein change hote hi city/street
  // level Required-PNDs calculation bhi auto update ho jaye ──
  const [baysPerPnd, setBaysPerPnd] = useState(25);

 const handleTogglePndStatusFilter = useCallback((key) => {
  setStreetsOnlyMode(false); // status filter select hote hi streets-only mode band
  setFilteredMapLoading(true); // ✅ NEW — turant loading true, taake map stale data se pehla draw skip kare


  // Multiple
  //  setPndStatusFilters(prev => {
  //   if (key === 'all') {
  //     return (prev.length === 1 && prev[0] === 'all') ? EMPTY_FILTERS : ['all'];
  //   }
  //   const withoutAll = prev.filter(f => f !== 'all');
  //   return withoutAll.includes(key)
  //     ? withoutAll.filter(f => f !== key)
  //     : [...withoutAll, key];
  // });
  // Single
  setPndStatusFilters(prev => (prev.length === 1 && prev[0] === key) ? EMPTY_FILTERS : [key]);
}, []);

  const handleClearPndStatusFilters = useCallback(() => setPndStatusFilters(EMPTY_FILTERS), []);

  // ── NEW: Filtered map data — /get-pnd-collection se fetch hota hai jab
// bhi pndStatusFilters ya activeFilters change hon. Yehi map ka
// single source of truth banega jab koi status filter active ho.
const [filteredMapData, setFilteredMapData] = useState(null);
const [filteredMapLoading, setFilteredMapLoading] = useState(false);
const filteredMapFetchIdRef = useRef(0);

useEffect(() => {
  const cityId   = selectedCity?.city_send_name   || selectedCity?.city_cd;
  const streetId = selectedStreet?.street_send_name || selectedStreet?.street_cd;

  if (pndStatusFilters.length === 0 || (!cityId && !streetId)) {
    setFilteredMapData(null);
    setFilteredMapLoading(false);
    return;
  }

  const fetchId = ++filteredMapFetchIdRef.current;
  setFilteredMapLoading(true);

  const now = new Date();
  const displayMonth = activeFilters?.month     || MONTH_NAMES[now.getMonth()];
  const displayYear  = activeFilters?.year      || String(now.getFullYear());
  const threshold    = activeFilters?.threshold || '';
  const monthParam   = buildMonthParam(displayMonth);
  const filtersParam = buildFiltersParam(pndStatusFilters);

  const url = streetId
    ? `/get-pnd-collection?street=true&device_id=${streetId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}${filtersParam}`
    : `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ''}${filtersParam}`;

  apiCall('get', url, null, null, false, false)
    .then(response => {
      if (fetchId !== filteredMapFetchIdRef.current) return;
      if (!response?.success) { setFilteredMapData({ type: streetId ? 'street' : 'city', pnds: [], streets: [] }); return; }

      if (streetId) {
        setFilteredMapData({
          type: 'street',
          pnds: response.street_breakdown?.pnds || [],
          collection: response.collection,
          ticket_count: response.ticket_count,
          als: response.als,
          atv: response.atv,
        });
      } else {
        const payload = response.data?.city_breakdown ?? response.city_breakdown ?? response.data ?? response;
        setFilteredMapData({
          type: 'city',
          streets: payload.streets || [],
          collection: response.collection,
          ticket_count: response.ticket_count,
          als: response.als,
          atv: response.atv,
        });
      }
    })
    .catch(() => {
      if (fetchId === filteredMapFetchIdRef.current) setFilteredMapData({ type: streetId ? 'street' : 'city', pnds: [], streets: [] });
    })
    .finally(() => {
      if (fetchId === filteredMapFetchIdRef.current) setFilteredMapLoading(false);
    });
}, [selectedCity, selectedStreet, activeFilters, pndStatusFilters]);

  const handleToggleStreetsOnly = useCallback(() => {
    const next = !streetsOnlyMode;
    // ON hote hi 'all' filter bhi lag jaye (jaisa Total PNDs card karta hai),
    // OFF hote hi filters clear
    setStreetsOnlyMode(next);
    setPndStatusFilters(next ? ['all'] : EMPTY_FILTERS);
  }, [streetsOnlyMode]);

  const { citiesData: allCitiesData, countryInfo, loading, refreshing, error, refetch: fetchData } = usePndData(isActive);

  useEffect(() => {
  if (!loading) {
    onReady?.();
  }
}, [loading, onReady]);

const { theme: appTheme } = useTheme();

// ── NEW: userContracts se cities filter karo ──────────────────────────────
const { userContracts } = usePermissions();

const citiesData = useMemo(() => {
  if (!userContracts) return allCitiesData; // Admin ya not loaded — sab dikhao

  const onstreetList  = userContracts['onstreet']  ?? [];
  const offstreetList = userContracts['offstreet'] ?? [];
  const allowedSet    = new Set([...onstreetList, ...offstreetList]);

  if (allowedSet.size === 0) return allCitiesData; // kuch assign nahi — sab dikhao

  return allCitiesData.filter(city =>
    allowedSet.has(city.city_send_name) || allowedSet.has(city.city_cd)
  );
}, [allCitiesData, userContracts]);

// Country khud assign hai ya nahi — agar nahi, to country ka naam/section
// nahi dikhana, sirf assigned cities dikhengi (bina country context ke)
const isCountryAssigned = useMemo(() => {
  if (!userContracts) return true;
  const onstreetList  = userContracts['onstreet']  ?? [];
  const offstreetList = userContracts['offstreet'] ?? [];
  const allowedSet    = new Set([...onstreetList, ...offstreetList]);
  if (allowedSet.size === 0) return true;
  return !!(countryInfo?.country_name && allowedSet.has(countryInfo.country_name));
}, [countryInfo, userContracts]);

const visibleCountryInfo = isCountryAssigned ? countryInfo : null;

  const handleBreakdownLoad = useCallback(({ belowThreshold, noTransaction, revenueMap }) => {
    setBelowThresholdPnds(new Set(belowThreshold));
    setNoTransactionPnds(new Set(noTransaction));
    if (revenueMap) setPndRevenueMap(new Map(revenueMap)); // ✅ NEW
  }, []);

  // ── Reverse sync: contract selected/cleared in Enforcement tab carries over here ──
  useEffect(() => {
    console.log('[OnStreetParkingPage] selectedCityNameExternal prop:', selectedCityNameExternal);
    if (!citiesData || citiesData.length === 0) return;

    if (!selectedCityNameExternal) {
      // Country assign nahi h (sirf cities hain, koi country wrapper nahi) —
      // to koi "country" view nahi, pehli assigned city hi by default select ho
      if (!isCountryAssigned) {
        const alreadyValid = selectedCity && citiesData.some(c => c.city_cd === selectedCity.city_cd);
        if (!alreadyValid) {
          setSelectedCity(citiesData[0]);
          setSelectedStreet(null);
          setSelectedPnd(null);
          setDetailView('city');
          setPndStatusFilters(EMPTY_FILTERS);
          setStreetsOnlyMode(false);
        }
        return;
      }

      if (selectedCity) {
        setSelectedCity(null);
        setSelectedStreet(null);
        setSelectedPnd(null);
        setDetailView('country');
        setPndStatusFilters(EMPTY_FILTERS);
        setStreetsOnlyMode(false);
      }
      return;
    }

    if (selectedCity?.city_name && normalizeName(selectedCity.city_name) === normalizeName(selectedCityNameExternal)) return; // already in sync

    const target = normalizeName(selectedCityNameExternal);
    const match = citiesData.find(c => normalizeName(c.city_name) === target)
      || citiesData.find(c => normalizeName(c.city_name).includes(target) || target.includes(normalizeName(c.city_name)));

    if (match) {
      console.log('[OnStreetParkingPage] matched city from Enforcement selection:', match);
      setSelectedCity(match);
      setSelectedStreet(null);
      setSelectedPnd(null);
      setDetailView('city');
      setPndStatusFilters(EMPTY_FILTERS);
      setStreetsOnlyMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCityNameExternal, citiesData, isCountryAssigned]);

  // ── Reverse sync: contract selected/cleared in Enforcement tab carries over here ──
useEffect(() => {
  if (!citiesData || citiesData.length === 0) return;

  if (!selectedCityNameExternal) {
    if (!isCountryAssigned) {
      const alreadyValid = selectedCity && citiesData.some(c => c.city_cd === selectedCity.city_cd);
      if (!alreadyValid) {
        setSelectedCity(citiesData[0]);
        setSelectedStreet(null);
        setSelectedPnd(null);
        setDetailView('city');
        setPndStatusFilters(EMPTY_FILTERS);
        setStreetsOnlyMode(false);
      }
      return;
    }

    if (selectedCity) {
      setSelectedCity(null);
      setSelectedStreet(null);
      setSelectedPnd(null);
      setDetailView('country');
      setPndStatusFilters(EMPTY_FILTERS);
      setStreetsOnlyMode(false);
    }
    return;
  }

  if (selectedCity?.city_name && normalizeName(selectedCity.city_name) === normalizeName(selectedCityNameExternal)) return; // already in sync

  const target = normalizeName(selectedCityNameExternal);
  const match = citiesData.find(c => normalizeName(c.city_name) === target)
    || citiesData.find(c => normalizeName(c.city_name).includes(target) || target.includes(normalizeName(c.city_name)));

  if (match) {
    setSelectedCity(match);
    setSelectedStreet(null);
    setSelectedPnd(null);
    setDetailView('city');
    setPndStatusFilters(EMPTY_FILTERS);
    setStreetsOnlyMode(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedCityNameExternal, citiesData, isCountryAssigned]);

// ── Reverse sync: street selected/cleared in the other tab carries over here ──
useEffect(() => {
  if (!selectedCity) return; // city pehle sync hone do

  if (!selectedStreetInfoExternal) {
    if (selectedStreet) {
      setSelectedStreet(null);
      setSelectedPnd(null);
      setDetailView('city');
    }
    return;
  }

  const alreadySynced =
    selectedStreet &&
    (selectedStreet.street_cd === selectedStreetInfoExternal.streetCd ||
      normalizeName(selectedStreet.street_name) ===
        normalizeName(selectedStreetInfoExternal.streetName));
  if (alreadySynced) return;

  const streets = selectedCity.streets || [];
  const match =
    streets.find(s => s.street_cd === selectedStreetInfoExternal.streetCd) ||
    streets.find(s => normalizeName(s.street_name) === normalizeName(selectedStreetInfoExternal.streetName));

  if (match) {
    setSelectedStreet(match);
    setSelectedPnd(null);
    setDetailView('street');
  }
}, [selectedStreetInfoExternal, selectedCity]);

// ── Reverse sync: date filter applied/cleared in Enforcement tab carries over here ──
useEffect(() => {
  const hasIncoming = !!(selectedDateFilterExternal && selectedDateFilterExternal.month && selectedDateFilterExternal.year);
  const alreadySynced = hasIncoming
    ? (activeFilters?.month === selectedDateFilterExternal.month && activeFilters?.year === selectedDateFilterExternal.year)
    : (!activeFilters?.month && !activeFilters?.year);
  if (alreadySynced) return;

  const nextFilters = hasIncoming ? selectedDateFilterExternal : {};
  setActiveFilters(nextFilters);
  if (pndStatusFilters.length > 0) setFilteredMapLoading(true);
  const { threshold, ...mapFilters } = nextFilters;
  fetchData({
    ...mapFilters,
    ...(selectedCity   && { city_send_name:   selectedCity.city_send_name   }),
    ...(selectedStreet && { street_send_name: selectedStreet.street_send_name }),
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedDateFilterExternal]);

  // ── Reverse sync: date filter applied/cleared in Enforcement tab carries over here ──
  useEffect(() => {
    console.log('[OnStreetParkingPage] selectedDateFilterExternal prop:', selectedDateFilterExternal);
    const hasIncoming = !!(selectedDateFilterExternal && selectedDateFilterExternal.month && selectedDateFilterExternal.year);
    const alreadySynced = hasIncoming
      ? (activeFilters?.month === selectedDateFilterExternal.month && activeFilters?.year === selectedDateFilterExternal.year)
      : (!activeFilters?.month && !activeFilters?.year);
    if (alreadySynced) return;

    const nextFilters = hasIncoming ? selectedDateFilterExternal : {};
    setActiveFilters(nextFilters);
    if (pndStatusFilters.length > 0) setFilteredMapLoading(true);
    const { threshold, ...mapFilters } = nextFilters;
    fetchData({
      ...mapFilters,
      ...(selectedCity   && { city_send_name:   selectedCity.city_send_name   }),
      ...(selectedStreet && { street_send_name: selectedStreet.street_send_name }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateFilterExternal]);

  useEffect(() => { setIsDarkMode(appTheme === 'dark'); }, [appTheme]);

  // ── NEW: Country pe click → country view panel khol do (get-pnd-collection wahin call hoti hai country_cd + city:false ke sath) ──
  const handleSelectCountry = useCallback(() => {
  setSelectedCity(null);
  setSelectedStreet(null);
  setSelectedPnd(null);
  setDetailView('country');
  setPndStatusFilters(EMPTY_FILTERS);
  setStreetsOnlyMode(false);
  onCityChange?.(null);
  onStreetChange?.(null); // 👈 NAYA
}, [onCityChange, onStreetChange]);

const handleSelectCity = useCallback((city) => {
  setSelectedCity(city);
  setSelectedStreet(null);
  setSelectedPnd(null);
  setDetailView('city');
  setPndStatusFilters(EMPTY_FILTERS);
  setStreetsOnlyMode(false);
  onCityChange?.(city);
  onStreetChange?.(null); // 👈 NAYA
}, [onCityChange, onStreetChange]);

const handleSelectStreet = useCallback((street) => {
  if (!street) return;
  setSelectedStreet(street);
  setSelectedPnd(null);
  setDetailView('street');
  setPndStatusFilters(EMPTY_FILTERS);
  setStreetsOnlyMode(false);
  onStreetChange?.(street); // 👈 NAYA
}, [onStreetChange]);

const handleSelectPnd = useCallback((pnd) => {
  if (!pnd) return;
  setSelectedPnd(pnd);
  setDetailView('pnd');
}, []);

const handleCloseDetail  = useCallback(() => { setDetailView('none'); setSelectedPnd(null); }, []);
const handleBackToStreet = useCallback(() => { setSelectedPnd(null); setDetailView('street'); }, []);

const handleBackToCity = useCallback(() => {
  setSelectedStreet(null);
  setSelectedPnd(null);
  setDetailView('city');
  onStreetChange?.(null); // 👈 NAYA
}, [onStreetChange]);

const handleBackFromCity = useCallback(() => {
  setSelectedCity(null);
  setDetailView('none');
  onCityChange?.(null);
  onStreetChange?.(null); // 👈 NAYA
}, [onCityChange, onStreetChange]);

const handleStreetFromCity = useCallback((street) => {
  setSelectedStreet(street);
  setSelectedPnd(null);
  setDetailView('street');
  onStreetChange?.(street); // 👈 NAYA
}, [onStreetChange]);

const handlePndFromStreet = useCallback((pnd) => { setSelectedPnd(pnd); setDetailView('pnd'); }, []);


  const handleFiltersChange = useCallback((filters) => {
  setActiveFilters(filters);
  onFiltersChangeExternal?.(filters);
  if (pndStatusFilters.length > 0) setFilteredMapLoading(true); // ✅ NEW — agar status filter already active hai to loading pehle hi set karo
  const { threshold, ...mapFilters } = filters;
  fetchData({
    ...mapFilters,
    ...(selectedCity   && { city_send_name:   selectedCity.city_send_name   }),
    ...(selectedStreet && { street_send_name: selectedStreet.street_send_name }),
  });
}, [fetchData, selectedCity, selectedStreet, pndStatusFilters, onFiltersChangeExternal]);

  if (loading) return <LoadingComponent />;
  if (error)   return <ErrorState error={error} onRetry={fetchData} />;

  const getPndContext = () => {
    if (!selectedPnd) return { street: null, city: null };
    const selectedPndNumber = selectedPnd.pnd_number;
    for (const city of citiesData) {
      for (const street of city.streets || []) {
        const matchingPnd = street.pnds?.find(p => String(p.pnd_number) === String(selectedPndNumber));
        if (matchingPnd) return { street, city };
      }
    }
    return { street: null, city: null };
  };

  const getStreetCity = () => {
    if (!selectedStreet) return null;
    return citiesData.find(c => c.streets?.some(s => s.street_cd === selectedStreet.street_cd)) || null;
  };

  const { street: pndStreet, city: pndCity } = getPndContext();
  const streetCity = getStreetCity();

  return (
    <div className="flex h-full w-full overflow-hidden bg-background">

      {/* ── Collapsible Sidebar ── */}
      <AnimatePresence initial={false}>
        {sidebarOpen && (
          <motion.div
            key="sidebar"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: sidebarWidth, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={isSidebarDragging.current ? { duration: 0 } : { duration: 0.25, ease: 'easeInOut' }}
            className="flex-shrink-0 overflow-hidden relative"
            style={{ minWidth: 0 }}
          >
            {/* Drag-to-resize handle — right edge of sidebar */}
            <div
              onMouseDown={onSidebarResizeMouseDown}
              className="absolute top-0 right-0 bottom-0 hover:bg-primary/20 transition-colors"
              style={{ width: '6px', cursor: 'col-resize', zIndex: 50 }}
            />
            <CityStreetSidebar
              citiesData={citiesData}
              countryInfo={visibleCountryInfo}
              allCitiesData={allCitiesData}
              fullCountryInfo={countryInfo}
              selectedCity={selectedCity}
              selectedStreet={selectedStreet}
              selectedPnd={selectedPnd}
              onSelectCity={handleSelectCity}
              onSelectStreet={handleSelectStreet}
              onSelectPnd={handleSelectPnd}
              onSelectCountry={handleSelectCountry}
              activeFilters={activeFilters}
              onFiltersChange={handleFiltersChange}
              onToggle={() => setSidebarOpen(false)}
              baysPerPnd={baysPerPnd}
              onBaysPerPndChange={setBaysPerPnd}
              monthGroups={monthGroups}
              yearOptions={yearOptions}
              optLoading={optLoading}
              optError={optError}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Map Area ── */}
      <div className="flex-1 relative h-full min-h-0">

        {/* Sidebar band ho tab sirf yeh button dikhega */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="absolute top-4 left-3 z-50 p-2 rounded-lg bg-card border border-border shadow-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
            title="Open sidebar"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {refreshing && (
  <div>
    {/* <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" /> */}
    {/* <span className="text-xs text-muted-foreground">Updating...</span> */}
  </div>
)}

        <GoogleMapComponent
  citiesData={citiesData}
  countryInfo={visibleCountryInfo || countryInfo}
  selectedCity={selectedCity}
  selectedStreet={selectedStreet}
  selectedPnd={selectedPnd}
  onPndClick={handleSelectPnd}
  onCityClick={handleSelectCity}
  onStreetClick={handleSelectStreet}
  isDarkMode={isDarkMode}
  belowThresholdPnds={belowThresholdPnds}
  noTransactionPnds={noTransactionPnds}
  pndStatusFilters={pndStatusFilters}
  streetsOnlyMode={streetsOnlyMode}
  pndRevenueMap={pndRevenueMap}
  sidebarOpen={sidebarOpen}
  detailPanelWidth={detailView !== 'none' ? 520 : 0}
  filteredMapData={filteredMapData}
  filteredMapLoading={filteredMapLoading}
/>

        <AnimatePresence>
          {detailView === 'country' && (
            <CountryDetailPanel
              key="country-panel"
              countryInfo={visibleCountryInfo}
              citiesData={citiesData}
              activeFilters={activeFilters}
              onClose={handleCloseDetail}
              onSelectCity={handleSelectCity}
            />
          )}
          {detailView === 'city' && selectedCity && (
            <CityDetailPanel
              key="city-panel"
              city={selectedCity}
              onClose={handleCloseDetail}
              onBack={handleBackFromCity}
              activeFilters={activeFilters}
              onSelectStreet={handleStreetFromCity}
              onBreakdownLoad={handleBreakdownLoad}
              pndStatusFilters={pndStatusFilters}
              onTogglePndStatusFilter={handleTogglePndStatusFilter}
              onClearPndStatusFilters={handleClearPndStatusFilters}
              streetsOnlyMode={streetsOnlyMode}
              onToggleStreetsOnly={handleToggleStreetsOnly}
              filteredMapData={filteredMapData}
              filteredMapLoading={filteredMapLoading}
              baysPerPnd={baysPerPnd}
              staleThresholdDays={staleThresholdDays}
              onStaleThresholdChange={handleStaleThresholdChange}
              noTransactionThresholdDays={noTransactionThresholdDays}
              onNoTransactionThresholdChange={handleNoTransactionThresholdChange}
            />
          )}
          {detailView === 'street' && selectedStreet && (
            <StreetDetailPanel
              key="street-panel"
              street={selectedStreet}
              city={streetCity}
              onClose={handleCloseDetail}
              onBack={selectedCity ? handleBackToCity : undefined}
              activeFilters={activeFilters}
              onSelectPnd={handlePndFromStreet}
              onBreakdownLoad={handleBreakdownLoad}
              pndStatusFilters={pndStatusFilters}
              onTogglePndStatusFilter={handleTogglePndStatusFilter}
              onClearPndStatusFilters={handleClearPndStatusFilters}
              filteredMapData={filteredMapData}
              filteredMapLoading={filteredMapLoading}
              baysPerPnd={baysPerPnd}
              staleThresholdDays={staleThresholdDays}
              onStaleThresholdChange={handleStaleThresholdChange}
              noTransactionThresholdDays={noTransactionThresholdDays}
              onNoTransactionThresholdChange={handleNoTransactionThresholdChange}
            />
          )}
          {detailView === 'pnd' && selectedPnd && (
            <PndDetailPanel
              key="pnd-panel"
              pnd={selectedPnd}
              street={pndStreet}
              city={pndCity}
              onClose={handleCloseDetail}
              onBack={selectedStreet ? handleBackToStreet : undefined}
              activeFilters={activeFilters}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CollectionDetails;